/**
 * Generic functions used as an addon to orders to provide additional functionality
 * Since this module is isomorphic (Vue + Node) all functions should be pure
 */
const { default: axios } = require('axios');
const _ = require('lodash');
const moment = require('moment-timezone');
const Big = require('big.js');
const Debug = require('debug');
const $debug = Debug('ABI:orderUtils');
const fspath = require('path');
const states = require('../widgets/address/state/states.js');
const $benchmark = require('../services/benchmark').singleton;
// Debug.enable(process.env.DEBUG); // Pass DEBUG so that we can enable it in the browser

// NOTE: Using spread (...) may have unpredictable results where server-side spreads keys from a "knex" document while client-side gets the internal document and spreads it's keys.

// @see https://github.com/MikeMcl/big.js/issues/86#issuecomment-331573255
Big.max = function() {
	var i,
		y,
		x = new this(arguments[0]);
	for (i = 1; i < arguments.length; i++) {
		y = new this(arguments[i]);
		if (x.lt(y)) x = y;
	}
	return x;
};

Big.min = function() {
	var i,
		y,
		x = new this(arguments[0]);
	for (i = 1; i < arguments.length; i++) {
		y = new this(arguments[i]);
		if (x.gt(y)) x = y;
	}
	return x;
};

const features = {
	// ABI-3117: Rounding discounts means discrependies in bundle level expectations relative to their children can be accounted for.
	// Woo only rounds discounts at the display level, rounding during the calculations means that unlike Woo the displayed line-level values always add up to the final totals.
	roundDiscounts : false,
	// ABI-3117: When children round away parts of the discount it may need adding back in to have bundle match tally of children.
	addRemainingCentsToBundleChildren: false,
};

const addressRequiredShippingMethods = ['standard', 'express', 'international', 'free', 'other', 'expressFree','dpdSaturday'];

const orderUtils = {
	pendingInit: undefined,
	pendingFetchProduct: {}, // NOTE: Final responses for products are cached in "products"
	pendingFetchProductStockLevels: {},
	pendingFetchWarehouses: undefined,

	/**
	 * Lookup collection of accepted payments
	 * @type {Array<Object>}
	 * @property {String} id Unique ID for the method
	 * @property {String} title Human readable title of the method
	 * @property {String} icon CSS icon to represent the payment
	 * @property {Boolean|Function} show Either a boolean or a Function which gets evaluated to a boolean, uses Vue's "this" namespace
	 * @property {String} [paymentUI] If the method is selected as a payment within the order payment screen, try to use the <payments/> system with the specified method
	 * @see paymentMethodsById The same collection but indexed by ID
	 */
	paymentMethods: [
		{ id: 'eftpos', title: 'Eftpos', icon: 'far fa-credit-card-front', show: function() { return this.order.instance === 'NZ' } },
		{ id: 'cash', title: 'Cash', icon: 'far fa-money-bill-wave', show: function() { return this.order.instance !== 'UK' && this.order.instance !== 'NZ' } },
		{ id: 'square', title: 'Square', icon: 'square-logo', show: function() { return this.order.instance !== 'NZ' }, ref: true },
		// {id: 'xero', title: 'Xero', icon: 'far fa-times-circle', show: true},  // June 23 ABI no longer needing Xero? Will double check before deleting GW
		{ id: 'directDeposit', title: 'Direct Deposit', icon: 'far fa-receipt', show: true },
		{ id: 'afterpay', title: 'Afterpay', icon: 'far fa-comments-dollar', show: true },
		{ id: 'stripe', title: 'Stripe', icon: 'fab fa-stripe-s', show: true, paymentUI: 'stripe' },
		{ id: 'stripeTerminal', title: 'Stripe Terminal', icon: 'fab fa-stripe-s', show: true, paymentUI: 'stripeTerminal' },
		{ id: 'paypal', title: 'PayPal', icon: 'fab fa-paypal', show: true },
		{ id: 'manual', title: 'Manual', icon: 'fas fa-user', show: false },
		{
			id: 'account',
			title: 'On Account',
			icon: 'fa fa-check',
			show: function() { return this.account && this.account !== 'None' && this.account !== true && this.account != 'true'; },
		},
		{
			id: 'credit',
			title: 'ABI Store Credit',
			icon: 'far fa-ticket-alt',
			show: function() { return this.$session.hasPermission('customersCreditEdit'); },
		},
		{
			id: 'collabContra',
			title: 'ABI Collab Contra',
			icon: 'far fa-comment-dots',
			show: function() { return this.$session.hasPermission('ABICollaborationContra'); },
		},
		{
			id: 'productContra',
			title: 'ABI Product Contra',
			icon: 'far fa-comment-dots',
			show: function() { return this.$session.hasPermission('ABIProductContra'); },
		},
		// {id: 'cxSpend', title: 'ABI CX Spend', icon: 'far fa-badge-dollar', show: "this.$session.hasPermission('ABI_CXSpend')"},
		// {id: 'tradeSpend', title: 'ABI Trade Spend', icon: 'fas fa-badge-dollar', show: "this.$session.hasPermission('ABITradeSpend')"},
		// {id: 'abiSpend', title: 'ABI Spend', icon: 'fas fa-badge-dollar', show: "this.$session.hasPermission('ABISpend')"},
		{
			id: 'ABISpendSales',
			title: 'ABI Spend > Sales',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendSales'); },
		},
		{
			id: 'ABISpendCustomerExperience',
			title: 'ABI Spend > Customer Experience',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendCustomerExperience'); },
		},
		{
			id: 'ABISpendDisplays',
			title: 'ABI Spend > Displays',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendDisplays'); },
		},
		{
			id: 'ABISpendMarketing',
			title: 'ABI Spend > Marketing',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendMarketing'); },
		},
		{
			id: 'ABISpendProcurementLogistic',
			title: 'ABI Spend > Procurement & Logistics',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendProcurementLogistics'); },
		},
		{
			id: 'ABISpendPeopleCulture',
			title: 'ABI Spend > People & Culture',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendPeopleCulture'); },
		},
		{
			id: 'ABISpendRnd',
			title: 'ABI Spend > Research and Development',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendResearchDevelopment'); },
		},
		{
			id: 'ABISpendWarehouse',
			title: 'ABI Spend > Warehouse',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendWarehouse'); },
		},
		{
			id: 'ABISpendOther',
			title: 'ABI Spend > Other',
			icon: 'fas fa-badge-dollar',
			show: function() { return this.$session.hasPermission('ABISpendOther'); },
		},
	],

	/**
	 * Lookup array of order location options
	 * @type {Array<String>}
	 */
	orderLocationOptions: [],

	/**
	 * Lookup array of order 'tags'
	 * @type {Array<String>}
	 */
	orderTagOptions: [],

	/**
	 * array for event order was made
	 * @type {Array<String>}
	 */
	orderEventOptions: [],

	orderLogFilters: [
		{ label: 'Order/Email', value: ['orderHistory', 'info', 'orderShipping'] },
		{ label: 'Payment', value: 'orderPayment' },
		{ label: 'Order Changes', value: 'orderChange' },
		{ label: 'Item Changes', value: 'orderItemChange' },
		{ label: 'Status', value: ['statusChange', 'versionChange'] },
		{ label: 'Comments', value: 'orderComment' },
	],

	/**
	 * Lookup array of order default payment system
	 * @type {Array<String>}
	 */
	orderPaymentSystems: [
		{ label: 'Woo Pay', value: 'woo-pay' },
		{ label: 'AOS Pay', value: 'aos-pay' },
	],

	/**
	 * Computes a good visual payment refrence string for an internal payment
	 *
	 * @param {Object} payment Internal payment as stored in the `orders.payments[n]` schema
	 * @param {String} [payment.method] Payment method
	 * @param {String} [payment.paymentReference] Manually user provided payment reference, can be an empty string which will be ignored
	 * @param {String} [payment.externalId] External ID e.g. stripe or stripe terminal ch_123 charge id
	 * @returns {String} Empty string if not found
	 */
	getReferenceDisplay(payment) {
		let $$debug = $debug.extend("getReferenceDisplay");

		if (!payment?.method) return '';

		if (this.isUserControlledPaymentReference(payment)) {
			if (typeof payment.paymentReference === 'string' && payment.paymentReference !== "") {
				return payment.paymentReference;
			} else {
				// let payment = _.cloneDeep(payment);
				// const msg = `getReferenceDisplay(payment) called on a payment that isUserControlledPaymentReference(payment) == true but payment.paymentReference is not a string or is an empty string`;
				// console.error(msg, payment);
				// throw new Error(msg);
				return '';
			}
		} else {
			if (typeof payment.externalId === 'string' && payment.externalId !== "") {
				if (payment.method.toLowerCase() === payment.externalId.toLowerCase()) {
					return '';
				}
				return payment.externalId;
			} else {
				return '';
			}
		}
	},

	/**
	 * Returns whether the `payment.paymentReference` field should be intentionally set by the user in UI
	 *
	 * @param {Object} payment Payment with method field
	 * @param {String} payment.method Payment method
	 * @returns {Boolean}
	 */
	isUserControlledPaymentReference(payment) {
		if (typeof payment?.method !== "string") throw TypeError();
		switch (payment.method) {
			case "eftpos":
			case "square":
			// case "stripe":
			// case "stripeTerminal":
			// case "paypal":
				return true;
			default:
				return false;
		}
	},


	/**
	 * Calculate tile area
	 *
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Object}
	 */
	calcTiles(item, products, logContext) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('Invalid products lookup object');

		const sku = products[item.product]?.sku;
		const isTile = !!(products[item.product] && products[item.product].tileDimension && products[item.product].tileUnitOfMeasure);

		if (
			products[item.product]
			&& products[item.product].tileDimension
			&& products[item.product].tileUnitOfMeasure
		) {
			const sqmPerBox = products[item.product].tileDimension;
			// FIXME: No need to "parseFloat"
			let totalSqm = parseFloat(new Big(parseFloat(item.quantity)).times(parseFloat(sqmPerBox)));
			let tileRrp = null;
			let tileQuantity = null;
			let originalPricePerSqm = null;
			let discountedPricePerSqm = null;

			// price per tile unit
			tileRrp =
				products[item.product].tileUnitOfMeasure === 'box'
					? '/box'
					: products[item.product].tileUnitOfMeasure === 'sheet'
					? '/sheet'
					: '/unit';

			// tile quantity unit
			if (parseFloat(item.quantity) === 1) {
				tileQuantity =
					products[item.product].tileUnitOfMeasure === 'box'
						? ' box'
						: products[item.product].tileUnitOfMeasure === 'sheet'
						? ' sheet'
						: ' unit';
			} else {
				tileQuantity =
					products[item.product].tileUnitOfMeasure === 'box'
						? ' boxes'
						: products[item.product].tileUnitOfMeasure === 'sheet'
						? ' sheets'
						: ' units';
			}

			// original and discount tile price
			if (
				item.totals.line.subTotal.after ===
				item.totals.line.subTotal.before
			) {
				discountedPricePerSqm = parseFloat(
					new Big(parseFloat(item.totals.line.subTotal.after))
						.div(item.quantity)
						.div(parseFloat(sqmPerBox))
						.toFixed(2)
				);
			} else {
				originalPricePerSqm = parseFloat(
					new Big(parseFloat(item.totals.line.subTotal.before))
						.div(item.quantity)
						.div(parseFloat(sqmPerBox))
						.toFixed(2)
				);
				discountedPricePerSqm = parseFloat(
					new Big(parseFloat(item.totals.line.subTotal.after))
						.div(item.quantity)
						.div(parseFloat(sqmPerBox))
						.toFixed(2)
				);
			}

			return {
				totalSqm,
				tileRrp,
				tileQuantity,
				originalPricePerSqm,
				discountedPricePerSqm,
			};
		}
	},


	/**
	 * Format to rounded percentage
	 * @param {Number} value Value to format
	 * @returns {String}
	 */
	formatPercent(value) {
		return _.round(Number(value || 0), 1) + '%';
	},


	/**
	 * Format all order information
	 * @param {Object} order Order
	 * @param {Object} item Item
	 * @param {Object} itemTotals ItemTotals Output from "itemTotals" method
	 * @param {Object} products Products API lookup
	 * @param {Object} [settings] Options
	 * @param {Boolean} [settings.calcTiles] Ability to disable out-of-place tiles formatting
	 * @returns {Object}
	 */
	formatOrderItem(order, item, itemTotals, products, settings) {
		if (!_.isPlainObject(order)) throw new Error('formatOrderItem(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('formatOrderItem(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('formatOrderItem(): Invalid products lookup object');

		// TODO: move to orderUtils function
		const formatCurrencies = (order, source, paths) => {
			const out = {};
			paths.map((path) => {
				_.set(out, path, this.formatCurrency(_.get(source, path, 0), order.instance));
			});
			return out;
		};

		const formatPercentages = (source, paths) => {
			const out = {};
			paths.map((path) => {
				const value = _.get(source, path);
				_.set(out, path, value ? this.formatPercent(value) : '');
			});
			return out;
		};

		const formatted = _.merge(
			formatCurrencies(order, itemTotals, [
				'item.cost.before',
				'item.cost.after',
				//'item.costExTax.before',
				//'item.costExTax.after',
				'item.tax.before',
				'item.tax.after',
				'item.discount',
				// FIXME: doesnt exist always need to handle this?
			]),
			formatCurrencies(order, itemTotals, [
				'line.subTotal.before',
				'line.subTotal.after',
				'line.subTotalExTax.before',
				'line.subTotalExTax.after',
				'line.tax.before',
				'line.tax.after',
				'line.returned',
				'line.discount',
			]),
			formatPercentages(itemTotals, ['line.discountPercent']),
		);

		formatted.item.quantity = item.quantity;

		if (settings.calcTiles) {
			// TODO: Format tile values during formatting of other fields
			// let pricePerSqm = this.formatCurrency(item.pricePerSqm, order.instance);

			if (_.has(products, [item.product, 'tileDimension']) && _.isString(products, [item.product, 'tileUnitOfMeasure'])) {
				if (parseFloat(item.quantity) === 1) {
					formatted.item.quantity =
					item.quantity +
						(products[item.product]?.tileUnitOfMeasure === 'box'
							? ' box'
							: products[item.product]?.tileUnitOfMeasure === 'sheet'
								? ' sheet'
								: ' unit');
				} else {
					formatted.item.quantity =
						item.quantity +
						(products[item.product]?.tileUnitOfMeasure === 'box'
							? ' boxes'
							: products[item.product]?.tileUnitOfMeasure === 'sheet'
								? ' sheets'
								: ' units');
				}
			}
		}

		return formatted;
	},


	/**
	 * Retrieve bundle children with greater discount applied
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} bundle Item
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.considerDiscountTypes=['customer', 'coupons', 'line', 'override', 'sale']] Included discount types
	 * @param {Boolean} [options.useOriginalCost] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.useItemCost] Passes via full "itemCost" rather than only "itemCostField"
	 * @returns {Array}
	 */
	itemChildDiscountGreater(order, children, bundle, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemChildDiscountGreater(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('itemChildDiscountGreater(): Invalid products lookup object');
		if (!_.isPlainObject(bundle)) throw new Error('itemChildDiscountGreater(): Invalid bundle lookup object');

		if (!this.isItemBundle(bundle, products)) return [];

		// TODO: Move children to an optional setting
		// if (!options.children) options.children = this.getOrderItemChildren(order, bundle);

		if (!children) children = this.getOrderItemChildren(order, bundle);

		return children
			.filter((child) => this.hasItemBundleChildPrice(child, products))
			.map((item) => this.itemDiscountGreater(order, children, item, products, options))
	},


	// TODO: Signature option to include formatting, then use this method in "generateOrderPdf" and "sendCommitEmail"
	/**
	 * Retrieve order items and attach calculations, flags
	 *
	 * @param {Object} order Order
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.calcFlags] Calculate flags indicating item status
	 * @param {Boolean} [options.calcFormatted=false] Include formatted versions
	 * @param {Boolean} [options.calcProduct=false] Include product information for display
	 * @param {Boolean} [options.calcTiles=false] Ability to disable flawed tile calculations section
	 * @param {Boolean} [options.calcTotals=false] Calculate item totals
	 * @param {Boolean} [options.showEta=true] Include ETA message
	 * @param {Boolean} [options.showPrice=true] Show price when greater than zero
	 * @param {String} [options.path_root] Root path for item SVGs
	 * @param {String} [options.publicUrl] URL to include in product links
	 * @returns {Array}
	 */
	getOrderItems(order, products, options = {}) {
		if (!_.isPlainObject(order)) throw new Error('getOrderItems(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('getOrderItems(): Invalid products lookup object');

		const settings = {
			calcFlags: false,
			calcFormatted: false,
			calcProduct: false,
			calcTiles: false,
			calcTotals: false,
			showEta: true,
			showPrice: true,
			path_root: undefined,
			publicUrl: undefined,
			...options,
		};

		const orderId = order?._id;
		const orderRef = order?.ref;

		const items = this.addAdditionalChargeItem(order, products);

		const $$debug = $debug.extend('getOrderItems');

		if ($$debug.enabled)
			$$debug('getOrderItems', _.cloneDeep(order), _.cloneDeep(products), _.cloneDeep(items), this.$session.hasPermission('ordersDiscountsSale'));

		let indexWithoutCategoriesOrChildren = 0;

		const mapped = items.map((item, i) => {
			const sku = products[item.product]?.sku;
			// FIXME: REFACTOR: Remove any mutation and instead provide new fields for formatting
			if (settings.calcTiles) {
				const tileCalc = $benchmark.time('orderUtils.getOrderItems.calcTiles', () => this.calcTiles(item, products, { orderId, orderRef }));
				item.totalSqm = tileCalc?.totalSqm;
				item.tileRrp = tileCalc?.tileRrp;
				item.tileQuantity = tileCalc?.tileQuantity;
				item.originalPricePerSqm = tileCalc?.originalPricePerSqm;
				item.discountedPricePerSqm = tileCalc?.discountedPricePerSqm;
			}

			const children = (settings.calcTotals || settings.calcFlags)
				? order.items.filter(c => c.parentRef === item.ref)
				: [];

			let totals;
			let formatted;
			if (settings.calcTotals) {
				totals = $benchmark.time('orderUtils.getOrderItems.itemTotals', () => this.itemTotals(order, children, item, products));
				if (settings.calcFormatted) formatted = $benchmark.time('orderUtils.getOrderItems.formatOrderItem', () => this.formatOrderItem(order, item, totals, products, settings));
			}

			let product;
			if (settings.calcProduct) {
				product = {
					sku: products[item.product]?.sku,
					name: products[item.product]?.name,
					thumbnail: products[item.product]?.thumbnail
						? products[item.product].thumbnail
						: fspath.join(settings.path_root, '/assets/images/product.svg'),
					link: `${settings.publicUrl}/go/product/${order.instance}/${item.product}`,
				};
			}

			let flags = {};
			if (settings.calcFlags) {
				const greaterDiscount = $benchmark.time('orderUtils.getOrderItems.itemDiscountGreater', () => this.itemDiscountGreater(order, children, item, products));

				flags.isItemSimple = this.isItemSimple(item, products);
				flags.isItemBundle = this.isItemBundle(item, products);
				flags.isItemBundleChild = this.isItemBundleChild(item, products);
				flags.isItemBundleAdditionalCharge = this.isItemBundleAdditionalCharge(item);

				flags.showHorizontalRule = (!item.parentRef);
				flags.showEta = (settings.showEta && item.eta);
				flags.showPrice = (settings.calcTotals && settings.showPrice && totals.line.subTotalExTax.after > 0);

				flags.hasDiscountCustomer = (
					!_.isNil(order?.customerDiscounts)
					&& (
						this.isDiscount(order?.customerDiscounts?.standard)
						|| this.isDiscount(order?.customerDiscounts?.sales)
						|| this.isDiscount(order?.customerDiscounts?.excluded)
					)
				);

				// ENUM['coupons', 'line', 'override', 'sale']
				flags.hasDiscountCoupons = this.hasItemDiscountType(item, 'coupons');
				flags.hasDiscountLine = this.hasItemDiscountType(item, 'line');
				flags.hasDiscountOverride = this.hasItemDiscountType(item, 'override');
				flags.hasDiscountSale = this.hasItemDiscountType(item, 'sale');

				flags.hasDiscountCustomerGreater = (greaterDiscount.name === 'customer');
				flags.hasDiscountCouponsGreater = (greaterDiscount.name === 'coupons');
				flags.hasDiscountLineGreater = (greaterDiscount.name === 'line');
				flags.hasDiscountOverrideGreater = (greaterDiscount.name === 'override');
				flags.hasDiscountSaleGreater = (greaterDiscount.name === 'sale');

				flags.showBadgeSale = (this.hasItemDiscountType(item, 'sale') || this.hasBundleChildDiscountType(order, children, item, 'sale'));
				flags.showBadgeOverride = (this.hasItemDiscountType(item, 'override') || this.hasBundleChildDiscountType(order, children, item, 'override'));

				flags.activeBadgeSale = (
					greaterDiscount.name === 'sale'
					|| $benchmark.time('orderUtils.getOrderItems.itemChildDiscountGreater', () => this.itemChildDiscountGreater(order, children, item, products)).map(d => d.name).includes('sale')
				);

				flags.activeBadgeOverride = (greaterDiscount.name === 'override');

				flags.showDiscount = (this.isItemBundle || !this.isBundleChild || (this.isBundleChild && this.item.pricedIndividually));
				flags.hideStockLevel = (this.isItemBundleAdditionalCharge || products[item.product]?.placeholder);
			}

			if(item.itemType !== 'title' && !item.parentRef){
				indexWithoutCategoriesOrChildren++;
			}

			return {
				originalIndex: i,
				indexWithoutCategoriesOrChildren: indexWithoutCategoriesOrChildren,
				data: item,
				product,
				totals,
				formatted,
				flags,
			};
		});
		return mapped;
	},

	/**
	 * Assigns order items given a calculated, flagged format from "getOrderItems"
	 *
	 * @param {Object} order Order
	 * @param {Array<Object>} value Incoming items data
	 */
	setOrderItems(order, value) {
		if (!_.isPlainObject(order)) throw new Error('setOrderItems(): Invalid order object');

		const regularProduct = value
			.map((d) => d.data) // Extract just the item data, we no longer require "originalIndex"
			.filter((p) => !p.parentRef);
		const newList = _.flatten(
			regularProduct.map((p) => {
				const children = order.items.filter((i) => i.parentRef === p.ref);
				if (!children.length) return p;
				return [p, ...children];
			}),
		);

		this.$set(order, 'items', newList);
	},


	/**
	 * Find list of children for a given item
	 *
	 * @param {Object} items Items
	 * @param {Object} item Item
	 * @returns {Object}
	 */
	getOrderItemParent(items, item) {
		return items.find(p => p.ref === item.parentRef);
	},


	// TODO: Utilise this to calcuate children when none are passed; Moving children to options.children
	/**
	 * Find list of children for a given item
	 *
	 * @param {Array} items Items
	 * @param {Object} item Item
	 * @returns {Array}
	 */
	getOrderItemChildren(items, item) {
		return items
			.filter(c => c.itemType === 'product')
			.filter(c => c.parentRef === item.ref);
	},


	/**
	 * Determine if an order is a "Warranty"
	 *
	 * @param {Object} order Order
	 * @returns {Boolean}
	 */
	isOrderWarranty(order) {
		if (!_.isPlainObject(order)) throw new Error('isOrderWarranty(): Invalid order object');

		if (!order) return false;
		return _.isString(order.ref) && order.ref.includes('-W');
	},

	/**
	 * Determine if an order is a "Dispatch"
	 *
	 * @param {Object} order Order
	 * @returns {Boolean}
	 */
	isOrderDispatch(order) {
		if (!_.isPlainObject(order)) throw new Error('isOrderDispatch(): Invalid order object');

		if (!order) return false;
		return _.isString(order.ref) && order.ref.includes('-D');
	},

	isShippingAddressRequired(order) {
		return _.includes(addressRequiredShippingMethods, _.get(order, 'shippingMethod.ref'));
	},

	countryHasStates(country) {
		return !_.isEmpty(states[country]);
	},

	validateShippingMethod(order) {
		const missing = [];
		if (!_.get(order, 'shippingMethod.ref')) missing.push('shippingMethod.ref');
		if (!_.get(order, 'shippingMethod.fulfilment')) missing.push('shippingMethod.fulfilment');
		return missing;
	},

	validateOrderFields(order, options) {
		const settings = {
			addresses: ['billing', 'shipping'],
			requireCustomer: true,
			requireContact: false,
			requireItems: false,
			...options,
		};
		const requiredAddressFields = ['name', 'address1', 'city', 'postcode', 'country'];
		const missing = [];

		if (settings.requireCustomer && !_.trim(order && order.customer)) missing.push('customer');

		settings.addresses.forEach(key => {
			if (key === 'shipping' && !this.isShippingAddressRequired(order)) return;
			const address = (order && order[key]) || {};
			const fields = this.countryHasStates(address.country)
				? [...requiredAddressFields, 'state']
				: requiredAddressFields;
			fields.forEach(field => {
				if (!_.trim(address[field])) missing.push(`${key}.${field}`);
			});
			const country = _.trim(address.country);
			if (country && !/^[A-Z]{2}$/.test(country)) missing.push(`${key}.country (must be a 2-letter ISO code)`);
		});

		if (settings.requireContact) {
			const billing = (order && order.billing) || {};
			if (!_.trim(billing.email) && !_.trim(billing.phone)) missing.push('billing.email-or-phone');
		}

		if (settings.requireItems) {
			const productItems = (((order && order.items) || []).filter(item => item && item.itemType === 'product'));
			if (productItems.length === 0) {
				missing.push('items');
			} else if (productItems.some(item => !(item.quantity > 0))) {
				missing.push('items.quantity');
			}
		}

		return missing;
	},

	/**
	* Document templates that represent a finalised financial document rather than a working preview
	*/
	strictPdfTemplates: ['invoice', 'receipt', 'dispatch'],

	/**
	* Required-field check for DOCUMENT (PDF) generation specifically
	*
	* Deliberately narrower than the commit check: it does NOT require line items. An order with no
	* items still renders a valid, if empty, document, and staff legitimately print orders that only
	* carry a shipping charge. Missing items remains a hard blocker for COMMIT (orders.rest.doop) and
	* is still listed in the order warnings panel - it just never stops a document being produced.
	*
	* Exists as a named function rather than an inline options object so the server-side gate
	* (orders.methods.documents.doop) and the UI that predicts it (order.warnings.vue) cannot drift
	* apart - see RULES.md #14.
	*
	* @param {Object} order Order
	* @returns {Array<String>} Missing field paths
	*/
	validateOrderDocumentFields(order) {
		return this.validateOrderFields(order, {requireItems: false});
	},

	/**
	* Determine if an order is still an unstarted draft that staff are building up
	*
	* NOTE: `status: 'invoice'` is the DEFAULT assigned at creation (orders.schema.doop) - it means
	* "not committed yet", NOT "this is an invoice". So this checks three independent signals rather
	* than trusting the status name alone.
	*
	* @param {Object} order Order
	* @returns {Boolean}
	*/
	isOrderDraft(order) {
		return (
			!_.get(order, 'isLocked')
			&& !(_.get(order, 'totalPaid', 0) > 0)
			&& _.get(order, 'status', 'invoice') === 'invoice'
		);
	},

	/**
	* Determine if missing required fields should HARD-BLOCK PDF generation (rather than just warn)
	*
	* A draft nobody has committed or paid for is expected to be incomplete - staff add the customer,
	* then the items, then the address, printing as they go to check the document. Hard-rejecting
	* there breaks the preview and (because app.panic() has no dedupe or rate limit) emails the team
	* on every click. Only once the order has actually been locked, paid, or moved past 'invoice' is
	* a missing field a genuine data-integrity failure worth blocking and alerting on.
	*
	* @param {Object} order Order
	* @param {String} docTemplate Template being generated
	* @returns {Boolean}
	*/
	isOrderPdfStrict(order, docTemplate) {
		return (
			_.get(order, 'source') !== 'woo'
			&& !this.isOrderDraft(order)
			&& this.strictPdfTemplates.includes(docTemplate)
		);
	},

	getOrderWarnings(order) {
		const warnings = [];

		const grandTotal = _.get(order, 'totals.grandTotal');
		if (_.isFinite(grandTotal) && grandTotal < 0) warnings.push(`negative grand total (${grandTotal})`);

		const email = _.trim(_.get(order, 'billing.email'));
		if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) warnings.push(`malformed billing email (${email})`);

		return warnings;
	},

	getOrderChangeLogs(oldOrder, newOrder, options) {
		const norm = value => String(value === undefined || value === null ? '' : value).trim();
		const fmt = value => norm(value) === '' ? '(blank)' : norm(value);
		const logs = [];
		const changeMessages = [];
		const change = (path, label) => {
			if (norm(_.get(oldOrder, path)) === norm(_.get(newOrder, path))) return;
			changeMessages.push(`${label} changed — "${fmt(_.get(oldOrder, path))}" → "${fmt(_.get(newOrder, path))}"`);
		};

		// NOTE: Emit one message per address field (rather than bundling all sub-fields into a single "; "-joined
		// message) so each change renders on its own line — consistent with every other change below.
		const addressFields = ['name', 'company', 'address1', 'address2', 'city', 'state', 'country', 'postcode', 'email', 'phone', 'addressType'];
		['billing', 'shipping'].forEach(key => {
			addressFields.forEach(field => change(`${key}.${field}`, `${_.startCase(key)} address (${field})`));
		});

		const installationFields = ['address1', 'address2', 'city', 'state', 'country', 'postcode'];
		installationFields.forEach(field => change(`installation.${field}`, `Installation address (${field})`));

		const methodBefore = _.get(oldOrder, 'shippingMethod.title') || _.get(oldOrder, 'shippingMethod.ref');
		const methodAfter = _.get(newOrder, 'shippingMethod.title') || _.get(newOrder, 'shippingMethod.ref');
		if (norm(methodBefore) !== norm(methodAfter)) changeMessages.push(`Shipping method changed — "${fmt(methodBefore)}" → "${fmt(methodAfter)}"`);
		change('shippingMethod.fulfilment', 'Shipping warehouse');
		change('shippingMethod.cost', 'Shipping cost');

		change('taxExempt', 'Tax exempt');
		change('deposit.method', 'Deposit method');
		change('deposit.amount', 'Deposit amount');
		change('deposit.percentage', 'Deposit percentage');
		change('notes', 'Notes');
		change('customerNotes', 'Customer notes');
		change('owner', 'Owner');
		change('bcUrgentOverwrite', 'BC urgency');
		change('bcPriorityManual', 'BC priority');
		change('bcShipOnDate', 'BC ship date');
		change('customerConfirmShippingAddress', 'Shipping address confirmation');
		change('externalRef', 'External reference');
		change('forceHold', 'Force hold');
		change('orderLocation', 'Order location');
		change('customerShipping', 'Shipping customer');
		change('customerDiscounts.standard', 'Customer discount (standard)');
		change('customerDiscounts.sales', 'Customer discount (sales)');
		change('customerDiscounts.excluded', 'Customer discount (excluded)');


		// Address check overrides. Logged explicitly rather than via change() so the message says what actually
		// happened (granted / re-granted against a different address / cleared) instead of printing raw keys.
		['billing', 'shipping'].forEach(key => {
			const keyBefore = norm(_.get(oldOrder, `addressCheckOverrides.${key}.key`));
			const keyAfter = norm(_.get(newOrder, `addressCheckOverrides.${key}.key`));
			if (keyBefore === keyAfter) return;
			const label = `${_.startCase(key)} address check`;
			const reason = norm(_.get(newOrder, `addressCheckOverrides.${key}.reason`));
			if (!keyAfter) {
				changeMessages.push(`${label} override cleared`);
			} else {
				// Read the address off the new doc rather than un-mangling the key, which is lowercased
				const suburb = fmt(_.get(newOrder, `${key}.city`));
				const postcode = fmt(_.get(newOrder, `${key}.postcode`));
				changeMessages.push(
					`${label} overridden for "${suburb} ${postcode}"`
					+ (reason ? ` - ${reason}` : '')
				);
			}
		});

		const tagsBefore = (_.get(oldOrder, 'tags') || []).toSorted();
		const tagsAfter = (_.get(newOrder, 'tags') || []).toSorted();
		if (!_.isEqual(tagsBefore, tagsAfter)) {
			const added = _.difference(tagsAfter, tagsBefore);
			const removed = _.difference(tagsBefore, tagsAfter);
			const parts = [];
			if (added.length) parts.push(`added ${added.map(tag => `"${tag}"`).join(', ')}`);
			if (removed.length) parts.push(`removed ${removed.map(tag => `"${tag}"`).join(', ')}`);
			changeMessages.push(`Tags changed — ${parts.join('; ')}`);
		}

		const oldCoupons = _.get(oldOrder, 'coupons') || [];
		const newCoupons = _.get(newOrder, 'coupons') || [];
		newCoupons.forEach(after => {
			const before = oldCoupons.find(coupon => coupon.id === after.id);
			if (!before) {
				if (after.code) changeMessages.push(`Added coupon "${after.code}" to order`);
			} else if (norm(before.value) !== norm(after.value)) {
				changeMessages.push(`Coupon "${after.code || after.id}" value changed — "${fmt(before.value)}" → "${fmt(after.value)}"`);
			}
		});
		oldCoupons.forEach(before => {
			if (before.code && !newCoupons.some(after => after.id === before.id))
				changeMessages.push(`Removed coupon "${before.code}" from order`);
		});

		const products = (options && options.products) || {};
		const itemLabel = item => {
			const name = item.name || _.get(products, [item.product, 'name']);
			const sku = item.sku || _.get(products, [item.product, 'sku']);
			return name ? `${name}${sku ? ` (${sku})` : ''}` : `Item ${item.ref || item.product}`;
		};
		const oldItems = _.keyBy((_.get(oldOrder, 'items') || []).filter(item => item && item.itemType === 'product'), 'ref');
		const newItems = _.keyBy((_.get(newOrder, 'items') || []).filter(item => item && item.itemType === 'product'), 'ref');

		const itemMessages = [];
		Object.keys(newItems).forEach(ref => {
			if (!oldItems[ref]) itemMessages.push(`${itemLabel(newItems[ref])} (qty ${fmt(newItems[ref].quantity)}): added to order`);
		});
		Object.keys(oldItems).forEach(ref => {
			const before = oldItems[ref];
			const after = newItems[ref];
			if (!after) {
				itemMessages.push(`${itemLabel(before)} (qty ${fmt(before.quantity)}): removed from order`);
				return;
			}
			[
				{key: 'quantity', label: 'quantity'},
				{key: 'cost', label: 'price'},
				{key: 'override.cost', label: 'price override'},
				{key: 'discount', label: 'discount'},
				{key: 'discountType', label: 'discount type'},
				{key: 'fulfilment', label: 'warehouse'},
				{key: 'eta', label: 'ETA'},
			].forEach(({key, label}) => {
				if (norm(_.get(before, key)) !== norm(_.get(after, key)))
					itemMessages.push(`${itemLabel(after)}: ${label} changed — "${fmt(_.get(before, key))}" → "${fmt(_.get(after, key))}"`);
			});
		});
		// NOTE: Join with '<br>' (not ',') — individual messages contain their own commas (tag lists, discounts, "→" diffs)
		// so a comma delimiter can't be split back apart reliably. logs.vue renders these via v-html and splits on '<br>'.
		if (changeMessages.length) logs.push({type: 'orderChange', body: changeMessages.join('<br>')});
		if (itemMessages.length) logs.push({type: 'orderItemChange', body: itemMessages.join('<br>')});

		return logs;
	},

	/**
	 * Determine if the item is a divider category
	 *
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemCategory(item) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		return item?.itemType === 'title';
	},

	/**
	 * Determine if the item is a bundles "Additional Charge"
	 *
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemBundleAdditionalCharge(item) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		return (
			_.has(item, 'product')
			&& _.isString(item.product)
			&& item.product.endsWith('_add')
			&& _.has(item, 'parentRef')
		);
	},

	/**
	 * Determine if item is a simple product
	 *
	 * @param {Object} item Item
	 * @param {Object} [products] Products API lookup
	 * @returns {Boolean}
	 */
	isItemSimple(item, products) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.has(item, 'product')) return false;
		if (_.isUndefined(products) || !_.has(products, [item.product, 'productType'])) return item?.productType === 'simple';
		// FIXME: Why do we go back to the API's opinion? Shouldn't "productRules" handle any change from "simple" to "bundle"?
		return products[item.product]?.productType === 'simple';
	},

	/**
	 * Determine if item is a bundle product
	 *
	 * @param {Object} item Item
	 * @param {Object} [products] Products API lookup
	 * @returns {Boolean}
	 */
	isItemBundle(item, products) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.has(item, 'product')) return false;
		if (_.isUndefined(products) || !_.has(products, [item.product, 'productType'])) return item?.productType === 'bundle';
		// FIXME: Why do we go back to the API's opinion? Shouldn't "productRules" handle any change from "simple" to "bundle"?
		return products[item.product]?.productType === 'bundle';
	},

	/**
	 * Determine if item is a bundle child
	 *
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemBundleChild(item) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		return _.has(item, 'parentRef') && !_.isNil(item.parentRef);
	},

	/**
	 * Determine if item is itself belonging to an excluded category
	 *
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemExcludedDiscount(item) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		return (item?.excludeDiscount == true);
	},


	/**
	 * Determine if item's parent belongs to an excluded category
	 *
	 * @param {Object} order Order
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemBundleParentExcludedDiscount(order, item) {
		if (!order) return false;
		if (!_.isPlainObject(order)) throw new Error('isItemBundleParentExcludedDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('isItemBundleParentExcludedDiscount(): Invalid item object');

		const parent = (item?.parentRef) ? order.items.find((d) => d.ref === item.parentRef) : null;
		return (parent?.excludeDiscount == true);
	},


	/**
	 * Determine if any of a bundle's children belongs to an excluded category
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	isItemBundleChildExcludedDiscount(order, children, item) {
		if (!order) return false;
		if (!_.isPlainObject(order)) throw new Error('isItemBundleChildExcludedDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('isItemBundleChildExcludedDiscount(): Invalid item object');

		if (!children) children = this.getOrderItemChildren(order.items, item);

		return children.some(d => this.isItemExcludedDiscount(d)); // Any are excluded
	},


	/**
	 * Determine if the item is excluded by coupon rules
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} coupon Coupon
	 * @param {Object} products Products API lookup
	 * @returns {Boolean}
	 */
	isItemExcludedByCoupon(order, children, item, coupon, products) {
		if (!_.isPlainObject(order)) throw new Error('isItemExcludedByCoupon(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('isItemExcludedByCoupon(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('isItemExcludedByCoupon(): Invalid products lookup object');

		if (!order) return false;

		const parent = (item?.parentRef) ? order.items.find((d) => d.ref === item.parentRef) : null;
		if (!children) children = this.getOrderItemChildren(order.items, item);

		const $$debug = $debug.extend('isItemExcludedByCoupon');

		// Check coupon is valid for this item {{{
		if (coupon.excludeIds?.includes('' + item.product) || coupon.excludeIds?.includes('' + parent?.product)) {
			if($$debug.enabled)
				$$debug('excludeIds.excluded', coupon, item);
			return true;
		}
		// }}}

		// Check if coupon is valid for this category of item {{{
		if (
			products[item.product]?.productCategories?.length > 0
			&& coupon.excludeProductCategories?.length > 0
			&& !!products[item.product].productCategories.find((d) => coupon.excludeProductCategories.includes(d))
		) {

		if($$debug.enabled)
			$$debug(
				'excludeProductCategories.item.excluded',
				coupon,
				item,
				products[item.product],
			);
			return true;
		}
		// }}}

		// Check if item is child and coupon is valid for category of parent
		if (
			parent
			&& products[parent.product]?.productCategories?.length > 0
			&& coupon.excludeProductCategories?.length > 0
			&& !!products[parent.product].productCategories.find((d) => coupon.excludeProductCategories.includes(d))
		) {
			if($$debug.enabled)
				$$debug(
					'excludeProductCategories.parent.excluded',
					coupon,
					item,
					products[parent.product],
				);
			return true;
		}

		// Check if coupon is valid for on sale items and item is on sale
		// TODO: Are we concerned with bundle children at all?
		if (
			coupon.excludeSale === 'yes'
			&& this.hasItemDiscountType(item, 'sale')
			&& this.itemDiscountGreater(order, children, item, products).name === 'sale'
		) {
			// FIXME: Respect "hasPermission('ordersDiscountsSale')"?
			if($$debug.enabled)
				$$debug('excludeSale.excluded', coupon, item);
			return true;
		}

		// TODO: "o.discountType === 'excluded" // Legacy field which was set when item was excluded? Never actually defined this way in the data?

		return false;
	},

	/**
	 * Check if an item is included by a coupon's productCategories allowlist
	 * Returns true if the coupon has no productCategories restriction or the item matches
	 *
	 * @param {Object} order The order object
	 * @param {Object} item The item to check
	 * @param {Object} coupon The coupon to check against
	 * @param {Object} products Products lookup object
	 * @returns {Boolean} Whether the item is included by the coupon
	 */
	isItemIncludedByCoupon(order, item, coupon, products) {
		if (!coupon.productCategories?.length) return true;

		const $$debug = $debug.extend('isItemIncludedByCoupon');

		const parent = (item?.parentRef) ? order.items.find((d) => d.ref === item.parentRef) : null;

		const itemCategories = products[item.product]?.productCategories;
		const parentCategories = parent ? products[parent.product]?.productCategories : null;

		const itemMatch = itemCategories?.length > 0
			&& itemCategories.some((d) => coupon.productCategories.includes(d));
		const parentMatch = parentCategories?.length > 0
			&& parentCategories.some((d) => coupon.productCategories.includes(d));

		if (!itemMatch && !parentMatch) {
			if($$debug.enabled)
				$$debug(
					'productCategories.notIncluded',
					coupon,
					item,
					products[item.product],
				);
			return false;
		}

		return true;
	},

	/**
	 * Check if the item list contains an appropriate "Additional charge" line
	 *
	 * @param {Array} children List of children to check for "Additional charge"
	 * @param {Object} item Item
	 * @returns {Boolean}
	 */
	hasItemBundleAdditionalCharge(children, item) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');

		return (children.some(this.isItemBundleAdditionalCharge));
	},

	/**
	 * Check if bundle is priced
	 *
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Boolean}
	 */
	hasItemBundlePrice(item, products) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('Invalid products lookup object');

		return (
			this.isItemBundle(item, products)
			//&& item.pricedIndividually // NOTE: Client specified that "cost" field would be sufficient
			&& item.cost > 0
		);
	},

	/**
	 * Check if bundle child is priced
	 *
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Boolean}
	 */
	hasItemBundleChildPrice(item, products) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('Invalid products lookup object');

		return (
			this.isItemBundleChild(item, products)
			&& item.pricedIndividually
			&& item.cost > 0
		);
	},

	/**
	 * Check if item is priced, regardless of being simple, bundle or child
	 *
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Boolean}
	 */
	hasItemPrice(item, products) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('Invalid products lookup object');

		return (
			(this.isItemSimple(item, products) && !this.isItemBundleChild(item) && item.cost > 0)
			|| this.hasItemBundlePrice(item, products)
			|| this.hasItemBundleChildPrice(item, products)
		);
	},

	// TODO: ABI-2665_1of13: Refactor closer to "set" ("setItemDiscountWithTax") leaving "apply" for "applyTaxExemptionOrder"? Maybe "applyItemDiscountTax" is better...
	// TODO: ABI-2665_1of13: Move to below "setItemDiscount" closer to "applyTaxExemptionOrder"

	/**
	 * Assign discount fields while respecting tax exemption
	 * @param {Object} order Order
	 * @param {Object} discountItem The discount item within discounts array
	 * @param {String} discountString Discount string to apply
	 * @param {Boolean} [taxExempt] Defaults to order "taxExempt" status
	 */
	applyDiscountWithTax(order, discountItem, discountString, taxExempt) {
		if (!_.isPlainObject(order)) throw new Error('applyDiscountWithTax(): Invalid order object');

		const discountValueIncoming = _.isString(discountString) ? new Big(discountString.replace(/[^0-9\.\-]/g, '')) : new Big(0);
		if (_.isUndefined(taxExempt)) taxExempt = order.taxExempt;

		const $$debug = $debug.extend('applyDiscountWithTax');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(discountItem), _.cloneDeep(discountString), taxExempt);

		// Only fixed dollar discounts require "taxExempt" adjustment
		if (_.startsWith(discountString, '$')) {
			// * Order is taxExempt same as input
			//		Input goes into "discount" and "originalDiscount" must be calculated
			if (order.taxExempt && taxExempt) {
				discountItem.discount = discountString;
				discountItem.originalDiscount =
					'$' +
					discountValueIncoming
						.plus(this.calcTaxFromTaxExclusivePrice(order.instance, discountValueIncoming))
						.toFixed(2);

				// * Order is not taxExempt same as input
				//		Input goes into "originalDiscount" and "discount" is the same
			} else if (!order.taxExempt && !taxExempt) {
				discountItem.discount = discountItem.originalDiscount = discountString;

				// * Order is taxExempt but input is not
				//		Input goes in "originalDiscount" and "discount" must be calculated
			} else if (order.taxExempt && !taxExempt) {
				discountItem.originalDiscount = discountString;
				discountItem.discount =
					'$' +
					new Big(discountValueIncoming)
						.minus(this.calcTax(order.instance, discountValueIncoming))
						.toFixed(2);

				// * Order is not taxExempt but input is
				//		Convert everything?
			} else if (!order.taxExempt && taxExempt) {
				console.log('TODO: ABI-2665_1of13: This case should not be used?');
			}
		} else if (_.endsWith(discountString, '%')) {
			if (discountItem.discount !== discountString || discountItem.originalDiscount !== discountString)
				discountItem.originalDiscount = discountItem.discount = discountString;
		} else {
			if (discountItem.discount !== '0' || discountItem.originalDiscount !== '0') discountItem.originalDiscount = discountItem.discount = '0';
		}

		if($$debug.enabled)
			$$debug('discountItem', _.cloneDeep(discountItem));
	},

	migrateLegacyItemDiscounts(order, customer) {
		if (!_.isPlainObject(order)) throw new Error('migrateLegacyItemDiscounts(): Invalid order object');

		const eligibleItems = (order.items || []).filter(item => (
			item.itemType === 'product'
			&& _.has(item, 'discountType')
			&& item.discountType === 'standard'
			&& _.has(item, 'discount')
			&& this.isDiscount(item.discount)
		));



		if (eligibleItems.length === 0) return;
		// NOTE: ABI-3226: we are wipping all discounts on upgrade no matter what they are
		// const customerStandard = _.get(customer, 'discounts.standard');
		// if (_.isNil(customerStandard)) {
		// 	$$debug('deferred', _.cloneDeep(eligibleItems.map(i => i.discount)));
		// 	return;
		// }
		// const customerPercent = Number.parseFloat(String(customerStandard).replace('%', ''));
		const setOrderPath = (path, value) => {
			if (typeof this.$setPath === 'function') this.$setPath(order, path, value);
			else _.set(order, path, value);
		};

		['standard', 'sales', 'excluded'].forEach(slot => {
			const customerValue = _.get(customer, ['discounts', slot]);
			if (this.isDiscount(customerValue) && !this.isDiscount(_.get(order, ['customerDiscounts', slot]))) {
				setOrderPath(['customerDiscounts', slot], customerValue);
			}
		});

		eligibleItems.forEach(item => {
			if (!_.isPlainObject(item.discounts)) item.discounts = {};
		// NOTE: ABI-3226: we are wipping all discounts on upgrade no matter what they are
			// const legacyValue = item.discount;
			// const legacyPercent = _.endsWith(legacyValue, '%')
			// 	? Number.parseFloat(String(legacyValue).replace('%', ''))
			// 	: Number.NaN;
			// // const matchesCustomer = (
			// 	_.isFinite(legacyPercent)
			// 	&& _.isFinite(customerPercent)
			// 	&& legacyPercent === customerPercent
			// );

			// if (matchesCustomer) {
			// 	$$debug('customer', legacyValue, customerStandard);
			// 	console.warn('MIGRATED LEGACY DISCOUNT (matches customer)', legacyValue, customerStandard);
			// } else {
			// 	const newDiscount = {};
			// 	this.applyDiscountWithTax(order, newDiscount, legacyValue);
			// 	$$debug('line', legacyValue, newDiscount);
			// 	if (typeof this.$setPath === 'function') {
			// 		this.$setPath(item, 'discounts.line', newDiscount);
			// 	} else {
			// 		_.set(item, 'discounts.line', newDiscount);
			// 	}
			//
			// 	if (
			// 		_.isFinite(legacyPercent)
			// 		&& _.isFinite(customerPercent)
			// 		&& legacyPercent < customerPercent
			// 		&& item.excludeDiscount !== true
			// 	) {
			// 		if (typeof this.$setPath === 'function') {
			// 			this.$setPath(item, 'excludeDiscount', true);
			// 		} else {
			// 			_.set(item, 'excludeDiscount', true);
			// 		}
			// 		$$debug('excludeDiscount.set', item._id || item.ref, legacyPercent, '<', customerPercent);
			// 	}
			//
			// 	console.warn('MIGRATED LEGACY DISCOUNT', item.discountType, legacyValue, _.cloneDeep(newDiscount));
			// }

			item.discountType = 'standard';
			item.discount = '0';
			item.originalDiscount = '0';
			item.couponDiscount = '0';
			item.maxDiscount = '0';
		});
	},

	/**
	 * Apply a discount to a single item
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} products Products API lookup
	 * @param {Object} item Item
	 * @param {String} discountString Discount string to apply
	 * @param {String} [creator] Use creating the discount
	 */
	setItemDiscount(order, children, products, item, discountString, creator = '') {
		if (!_.isPlainObject(order)) throw new Error('setItemDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('setItemDiscount():Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('setItemDiscount(): Invalid products lookup object');

		const $$debug = $debug.extend('setItemDiscount');
		const autoSetMaxDiscount = true

		// FIXME: Loops with this.recalcCoupons() discounts when coupons are this.removeCoupon()
		// FIXME: Remove all "recalcCoupons" and execute using a watcher which observes deep enough
		if($$debug.enabled)
			$$debug(_.cloneDeep(item), discountString);

		console.log('discountString', discountString)
		if (!discountString) return false;
		const canSetDiscount = this.canSetDiscount(order, children, item, products, discountString);
		if (!canSetDiscount.canSet) {
			console.log('canSetDiscount', canSetDiscount)
			if(autoSetMaxDiscount && canSetDiscount.discountString) discountString = canSetDiscount.discountString
			else
				return false;
		}

		if (!children) children = this.getOrderItemChildren(order.items, item);

		$$debug('discountString', discountString);

		// TODO: ABI-2665_1of13: REFACTOR: The final destination is this simply does "addItemDiscount()" with another function removing them again on demand

		// No existing "line" discount; Create a placeholder so we can adjust in a single code-path
		if (!this.hasItemDiscountType(item, 'line', { validateDiscounts: false })) {
			const newDiscount = {
				creator: creator,
			};
			this.applyDiscountWithTax(order, newDiscount, discountString, false); // FIXME: Must be done here to ensure "item.discounts" updates correctly?
			this.$setPath(item, 'discounts.line', newDiscount);
		} else {
			this.applyDiscountWithTax(order, item.discounts.line, discountString, false);
		}

		this.setItemChildDiscount(order, children, products, item, discountString, creator);

		if($$debug.enabled)
			$$debug('item', _.cloneDeep(item));
	},

	/**
	 * Apply share of discount to children
	 * This also takes into account rounding on each child and removes and remainder
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} products Products API lookup
	 * @param {Object} item Item
	 * @param {String} discountString Discount string to apply
	 * @param {String} [creator] Use creating the discount
	 */
	setItemChildDiscount(order, children, products, item, discountString, creator = '') {
		if (!_.isPlainObject(order)) throw new Error('setItemChildDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('setItemChildDiscount(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('setItemChildDiscount(): Invalid products lookup object');

		// FIXME: Check "products" instead? Use an "isItemBundle" encapsulation
		if (item.productType !== 'bundle') return; // Following logic applies to bundles only

		const $$debug = $debug.extend('setItemChildDiscount');


		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(products), _.cloneDeep(item), discountString);

		if (!children) children = this.getOrderItemChildren(order.items, item);

		if (!children.length > 0) return;

		if($$debug.enabled)
			$$debug('children', _.cloneDeep(children));

		const discountValueIncoming = _.isString(discountString) ? parseFloat(discountString.replace(/[^0-9\.\-]/g, '')) : 0;
		$$debug('discountValueIncoming', discountString, discountValueIncoming);

		children.forEach((child) => {
			if($$debug.enabled)
				$$debug('child', _.cloneDeep(child));

			// Reason why this needs to be initialised?
			if (!this.hasItemDiscountType(child, 'line', { validateDiscounts: false })) {
				const newDiscount = {
					creator: creator,
				};
				this.applyDiscountWithTax(order, newDiscount, '$0', false);
				$$debug('newDiscount', newDiscount);
				// if (!_.isPlainObject(child.discounts)) this.$set(child, 'discounts', {});
				this.$setPath(child, 'discounts.line', newDiscount);
			}

			// TODO: ABI-2665_1of13: Excluded.... If this was just to make it skip applying, then we've already done that in "applyCoupons"
			//if (item.discountType === 'excluded') {
			//	children.forEach(child => child[discountType] = '0');
			//} else

			let childDiscount = discountString;
			// FIXME: Also share out percentage discounts by converting to dollars and fudging any rounding issues
			if (discountString.includes('$')) {
				// FIXME: Is this true? Do they need to be shared out to ensure they match at all times?
				// only dollar line discounts should be distributed/calculated to children
				// percentage line discounts are the same for all children, no need to get each child's share
				childDiscount = this.getBundleChildDiscountShare(order, children, item, child, products, discountString);
			}
			$$debug('childDiscount', childDiscount);

			this.applyDiscountWithTax(order, child.discounts.line, childDiscount, order.taxExempt);
			$$debug('originalDiscount', child.discounts.line.originalDiscount);
			$$debug('discount', child.discounts.line.discount);
		});

		if($$debug.enabled)
			$$debug('children.out', _.cloneDeep(children));
	},


	/**
	 * Remove all item discounts from an order
	 *
	 * @param {Object} order Order
	 * @param {Object} options
	 */
	/*removeOrderDiscounts(order, options) {
		if (!_.isPlainObject(order)) throw new Error('removeOrderDiscounts(): Invalid order object');

		const settings = {
			removeDiscountTypes: [
				'sale',
				'override',
				'line',
				// 'coupons' // TODO: Do we wish to remove coupons?
			],
			...options,
		};

		order.items.filter(d => d.itemType === 'product').forEach(d => this.removeItemDiscounts(d, options));
		if (settings.removeDiscountTypes.includes('coupons')) order.coupons = [];
	},*/


	/**
	 * Remove all discounts from an item
	 *
	 * @param {Object} item Item
	 * @param {Object} [options] Options
	 */
	removeItemDiscounts(item, options) {
		if (!_.isPlainObject(item)) throw new Error('removeItemDiscounts(): Invalid item object');
		if (item.itemType !== 'product' || !_.isPlainObject(item.discounts)) return;

		const settings = {
			removeDiscountTypes: [
				'sale',
				'override',
				'line',
				// NOTE: See https://freedcamp.com/view/2713393/issuetracker/280948#comment-26513105
				//'coupons'
			],
			...options,
		};

		settings.removeDiscountTypes.forEach(d => this.$delete(item.discounts, d));
	},


	/**
	 * Apply tax exempt status to order
	 *
	 * @param {Object} order Order for which to configure tax exemption
	 * @param {Boolean} [taxExempt=false] Order tax exemption status
	 */
	applyTaxExemptionOrder(order, taxExempt = false) {
		if (!_.isPlainObject(order)) throw new Error('applyTaxExemptionOrder(): Invalid order object');

		const $$debug = $debug.extend('applyTaxExemptionOrder');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), taxExempt);

		// Tranform legacy orders or data from products API into data format which maintains "originalCost" {{{
		order.items.forEach((i) => (i.originalCost = i.originalCost ? i.originalCost : i.cost));
		order.items
			.filter((i) => _.isObject(i.override))
			.forEach(
				(i) =>
					(i.override.originalCost = i.override?.originalCost ? i.override.originalCost : i.override?.cost),
			);
		order.shippingMethod.cost = order.shippingMethod.cost || order.totalShipping || 0;
		// }}}

		order.items.forEach((item) => this.applyTaxExemptionItem(item, order.instance, taxExempt));

		order.totalShipping = taxExempt
			? parseFloat(
				new Big(+_.get(order, 'shippingMethod.cost', 0))
					.minus(this.calcTax(order.instance, +_.get(order, 'shippingMethod.cost', 0)))
					.toFixed(2),
			)
			: parseFloat(new Big(+_.get(order, 'shippingMethod.cost', 0)).toFixed(2));

		// Redundant check to make sure shipping discount maxes at total shipping
		// FIXME: ABI-2665_1of13: Correct or should discount also have taxExempt applied to it? Meaning it would need to keep a copy of it's "originalShippingDiscount"
		if (order.shippingDiscount > order.totalShipping) order.shippingDiscount = order.totalShipping;
	},

	/**
	 * Apply tax exempt status to items
	 *
	 * @param {Object} item Item Individual order item
	 * @param {String} country The ISO two letter country code to calculate the tax for
	 * @param {Boolean} [taxExempt=false] Order tax exemption status
	 */
	applyTaxExemptionItem(item, country, taxExempt = false) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');

		const $$debug = $debug.extend('applyTaxExemptionItem');

		const settings = {
			// significantFigures rounds the ex-GST unit cost (set below) BEFORE itemTotals multiplies it by qty and
			// applies discounts. It is intentionally 2:
			//   - 4e760efa4 (feat ABI-3117) originally set it to 5 so tax-exempt line totals matched the client's
			//     full-precision rounding example.
			//   - 013ec1d3c (feat ABI-3113) reverted it to 2 because BusinessCentral consumes the RRP exactly as sent
			//     from AOS and expects 2dp; sending 5dp broke the BC feed.
			// Making it 2dp a tax-exempt line loses sub-cent precision (e.g. 118.0909*0.85*5 renders $501.88, not
			// the example's $501.89).
			significantFigures: 2,
		};

		if($$debug.enabled)
			$$debug(_.cloneDeep(item), country, taxExempt);
		// TODO: Simply pass "(order, item)"?

		// TODO: Encapsulate duplication
		// Item cost {{{
		// FIXME: When running poorly setup tests it was possible to generate a "[big.js] Invalid number"; Doesn't make a lot of sense given the preconditions.
		// Wrapped in try-catch to avoid crash due to improper product entry in DB.
		try {
			if (_.has(item, 'originalCost') && !_.isNil(item.originalCost)) {
				item.cost = taxExempt
					? parseFloat(
						new Big(+_.get(item, 'originalCost', 0))
							.minus(this.calcTax(country, +_.get(item, 'originalCost', 0)))
							.toFixed(settings.significantFigures),
					)
					: parseFloat(new Big(+_.get(item, 'originalCost', 0)).toFixed(settings.significantFigures));
			}
		} catch (error) {
			console.warn(error);
		}

		// }}}

		// Discount fixed dollar values {{{
		// TODO: Validate discounts exists and is an array?
		Object.entries(item.discounts).forEach(([discountType, discount]) => {
			if (!this.isDiscount(discount?.originalDiscount)) return;

			$$debug('discount', discountType, discount);
			// TODO: Is it appropriate to use "applyDiscountWithTax" here? One thing it does not do is check for "Unnecessary assignment"
			// this.applyDiscountWithTax(order, discount, discount.originalDiscount, false);

			if (_.startsWith(discount.originalDiscount, '$')) {
				// TODO: ABI-2665_1of13: Check if "originalDiscount" and "discount" are already equal in the case of "taxExempt === false"
				// if (!taxExempt && discount.discount === discount.originalDiscount) return;

				const originalDiscountValue = _.isString(discount.originalDiscount)
					? discount.originalDiscount.replace(/[^0-9\.\-]/g, '')
					: '0';
				const calculatedDiscountString =
					'$' +
					(taxExempt
						? new Big(originalDiscountValue).minus(this.calcTax(country, originalDiscountValue)).toFixed(2)
						: new Big(originalDiscountValue).toString());
				$$debug('discount.calculatedDiscountString', calculatedDiscountString);

				// NOTE: Unnecessary assignment here would cause an update loop
				if (item.discounts[discountType].discount !== calculatedDiscountString)
					item.discounts[discountType].discount = calculatedDiscountString;
				//} else {
				//	item.discounts[discountType].discount = '0';
			} else if (_.endsWith(discount.originalDiscount, '%')) {
				item.discounts[discountType].discount = discount.originalDiscount;
			}
		});
		// }}}
	},


	/**
	 * Create a single additional charge item
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Order item
	 * @param {Object} products Products API lookup
	 * @returns {Object}
	 */
	createAdditionalChargeItem(order, children, item, products) {
		if (!_.isPlainObject(order)) throw new Error('createAdditionalChargeItem(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('createAdditionalChargeItem(): Invalid products lookup object');

		const $$debug = $debug.extend('createAdditionalChargeItem');

		if($$debug.enabled)
			$$debug('item', _.cloneDeep(item));

		if (!children) children = this.getOrderItemChildren(order.items, item);

		// Skip for bundles without a price; These items will need to be filtered out with ".filter(Boolean)"
		if (!this.hasItemBundlePrice(item, products)) return;

		if (!products[item.product]) return;

		const productKey = item.product + '_add';
		products[productKey] = {
			_id: products[item.product]._id,
			productType: 'simple',
			name: 'Additional charge',
			placeholder: true,
			sku: '',
		};

		// Adjust discount amount down by amount consumed by children for the purposes of displaying "Additional charge"
		// FIXME: When adding line with coupons are they reset to zero?
		const discounts = this.getBundleDiscountChildrenReduced(order, children, item, products);
		if($$debug.enabled)
			$$debug('discounts', _.cloneDeep(item.discounts), _.cloneDeep(discounts));

		// TODO: Also need to pass by "getOrderItemDiscountRemainderShares"? Children will be passed by this in "getBundleChildDiscountShare"; At which point does "Additonal charge" have it's discount adjusted?
		// TODO: Rather than reducing the bundle discount by amount consumed by children, could also calculate it's share using "getBundleChildDiscountShare".
		// NOTE: Perhaps the reduction method is safer for rounding to zero and ensuring the parent always contains the full remainder.

		const additional = {
			ref: null,
			product: productKey,
			itemType: 'product',
			productType: 'simple',
			pricedIndividually: true,
			parentRef: item.ref,
			cost: item.cost,
			originalCost: item.originalCost,
			override: item.override,
			quantity: item.quantity,
			excludeDiscount: item.excludeDiscount,
			discounts: discounts,
			//discounts: item.discounts,
		};
		if($$debug.enabled)
			$$debug('additional', _.cloneDeep(additional));

		return additional;
	},


	/**
	 * Adding "Additional charge" line where bundle "cost" itself partipates in overall cost calculation
	 *
	 * @param {Object} order Order Current order
	 * @param {Object} products Products API lookup
	 * @returns {Array}
	 */
	addAdditionalChargeItem(order, products) {
		if (!_.isPlainObject(order)) throw new Error('addAdditionalChargeItem(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('addAdditionalChargeItem(): Invalid products lookup object');

		const $$debug = $debug.extend('addAdditionalChargeItem');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(products));
		const out = _.flatten(
			order.items.map((item) => {
				if (products[item.product]?.productType !== 'bundle') return item; // Only bundles have "Additional charge"
				if (!item.cost) return item; // "Additional charge" requires bundle has a cost
				//if (!item.pricedIndividually) return item; // "Additional charge" requires bundle be "pricedIndividually" (Client feedback subject to change)

				return [item, this.createAdditionalChargeItem(order, null, item, products)].filter(Boolean);
			}),
		);
			if($$debug.enabled)
				$$debug('out', _.cloneDeep(out));
		return out;
	},

	/**
	 * Calculates the taxable amount on a price using the active instance country
	 *
	 * @param {String} country The ISO two letter country code to calculate the tax for
	 * @param {Number|Big} price The price to calculate the tax from
	 * @returns {Big} High precision floating value representing the tax
	 */
	calcTax(country, price = 0) {
		price = new Big(+price);
		switch (country) {
			case 'AU':
				return price.div(11);

			case 'GB':
			case 'UK':
				return price.div(6);

			case 'NZ':
				return price.times(3).div(23);

			default:
				throw new Error(`Unknown country to calculate tax "${country}"`);
		}
	},

	/**
	 * This function is used to take in a tax-exclusive price to calculate the tax amount that would be
	 * added for each instance/country.
	 *
	 * @param {String} country country code the currency belongs to in AOS this is order.instance
	 * @param {Number|Big} [price=0] the tax-exclusive amount to get the tax that would be added
	 * @returns {Big} High precision floating value representing the tax
	 */
	calcTaxFromTaxExclusivePrice(country, price = 0) {
		price = new Big(+price);
		switch (country) {
			case 'AU':
				return price.times(0.1);

			case 'GB':
			case 'UK':
				return price.times(0.2);

			case 'NZ':
				return price.times(0.15);

			default:
				throw new Error(`Unknown country to calculate tax "${country}"`);
		}
	},

	/**
	 * Determine the name of tax for given on country
	 * @param {String} country Country
	 * @returns {String}
	 */
	taxName(country) {
		switch (country) {
			case 'AU':
				return 'GST';

			case 'GB':
				return 'VAT';
			case 'UK':
				return 'VAT';

			case 'NZ':
				return 'GST';

			default:
				throw new Error(`Unknown country to calculate tax "${country}"`);
		}
	},

	/**
	 * Determine if an item has a particular type of discount
	 *
	 * @param {Object} item Item
	 * @param {String} [discountType] Desired discount type
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.validateDiscounts=true] Also check if the discount contained is non-zero
	 * @returns {Boolean}
	 */
	hasItemDiscountType(item, discountType, options) {
		if (!_.isPlainObject(item)) throw new Error('Invalid item object');

		const $$debug = $debug.extend('hasItemDiscountType');

		if($$debug.enabled)
			$$debug(_.cloneDeep(item), discountType, options);

		if (!item || !_.has(item, 'discounts')) return false;
		if (!discountType) return false;

		const settings = {
			validateDiscounts: true,
			...options,
		};

		// TODO: Requires passing "order" to "hasItemDiscountType"; Doesn't really fit with "ItemDiscount", could be encapsulated elsewhere
		// if (discountType === 'customer') {
		// 	const customerDiscounts = order?.customerDiscounts;
		// 	if (_.isNil(customerDiscounts)) return false;
		// 	return (this.isDiscount(customerDiscounts?.standard) || this.isDiscount(customerDiscounts?.sales) || this.isDiscount(customerDiscounts?.excluded));
		// }

		return (
			_.has(item.discounts, discountType)
			&& (_.isPlainObject(item.discounts[discountType]) || _.isArray(item.discounts[discountType]))
			&& (
				!settings.validateDiscounts
				|| (
					_.isArray(item.discounts[discountType])
					&& item.discounts[discountType].length > 0
					&& item.discounts[discountType].some((discount) => this.isDiscount(discount?.discount))
				)
				|| this.isDiscount(item.discounts[discountType]?.discount)
			)
		);
	},

	/**
	 * Determine if an item's children have a particular type of discount
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {String} discountType Desired discount type
	 * @returns {Boolean}
	 */
	hasBundleChildDiscountType(order, children, item, discountType) {
		if (!_.isPlainObject(order)) throw new Error('hasbundleChildDiscountType(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('hasbundleChildDiscountType(): Invalid item object');

		if (!children) children = this.getOrderItemChildren(order.items, item);

		if (!item || !this.isItemBundle(item)) return false;
		if (!children || !children.length) return false;
		if (!discountType) return false;

		return children.some((child) => this.hasItemDiscountType(child, discountType));
	},

	/**
	 * Determine if a discount can be applied to the item
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @param {String} discountString Desired discount amount
	 * @returns {Boolean}
	 */
	canSetDiscount(order, children, item, products, discountString) {
		if (!_.isPlainObject(order)) throw new Error('canSetDiscount(): Invalid order object', order);
		if (!_.isPlainObject(item)) throw new Error('canSetDiscount(): Invalid item object', item);
		if (!children) children = this.getOrderItemChildren(order.items, item);
		// NOTE: If discountString is not provided assume we are checking if the user can apply a discount to the item
		if (discountString == undefined) {
			// Should we sepearte this out or make some type of setting to consider this maybe?
			const canSet =
				!item.parentRef && // No children
				item?.itemType === 'product' && // Products only
				!this.isItemBundleAdditionalCharge(item) && // Real items only
				(!(
					this.hasItemDiscountType(item, 'sale') ||
					this.hasBundleChildDiscountType(order, children, item, 'sale')
				) || // Not allowed on sale items
					!(
						this.hasItemDiscountType(item, 'override') ||
						this.hasBundleChildDiscountType(order, children, item, 'override')
					) || // Not allowed on override items
					//  FIXME: refactor this ordersDiscountsSale perm is getting deleted for now just set to true
					//  for no until certain just set to true
					true);
			return { canSet };
		}

		let discount = 0;
		let isDollarDiscount = false
		// discountString can be a $ or a % or undefined
		if (_.startsWith(discountString, '$')) {
			isDollarDiscount = true
			const discountDollar = new Big(discountString.replace('$', ''));
			// TODO: Should this "considerDiscount" for existing discounts or determine if it would be max against the original price?
			const cost = this.itemCost(order, children, item, products);
			discount = discountDollar.div(cost).times(100);
		} else if (_.endsWith(discountString, '%')) {
			discount = parseFloat(discountString.replace('%', ''));
		}

		// const itemMaxDiscount = parseFloat((item.maxDiscount ?? '100').replace('%', ''))
		const userMaxDiscount = parseFloat((this.$session.data.totalDiscount ?? '0').replace('%', ''));
		const orderDiscounts = {
			excluded: parseFloat((order.customerDiscounts.excluded ?? '0').replace('%', '')),
			sales: parseFloat((order.customerDiscounts.sales ?? '0').replace('%', '')),
			standard: parseFloat((order.customerDiscounts.standard ?? '0').replace('%', '')),
		};

		// early returns for if item is a child or isnt product or virtual item
		if (
			!item.parentRef && // No children
			item.itemType !== 'product' && // products only
			!this.isItemBundleAdditionalCharge(item) // real items only
		)
			throw `Can't apply discount to a child item or additional charge line`;

		const isSaleItem =
			this.hasItemDiscountType(item, 'sale') || this.hasBundleChildDiscountType(order, children, item, 'sale');
		const isOverride =
			this.hasItemDiscountType(item, 'override') ||
			this.hasBundleChildDiscountType(order, children, item, 'override');

		const isBundle = this.isItemExcludedDiscount(item) || this.isItemBundleParentExcludedDiscount(order, item);
		const ordersDiscountsSale = true;
		if (isSaleItem && !ordersDiscountsSale) return false;
		if (isOverride && !ordersDiscountsSale) return false;

		const totals = this.itemTotals(order, children, item, products)
		let errorString = `Discount of ${discountString} can't be applied`;
		if (isSaleItem) {
			const maxDiscount = Math.max(orderDiscounts.sales, userMaxDiscount)
			if (maxDiscount < discount) {
				let discountMax = `${maxDiscount}%`
				if(isDollarDiscount) {
					discountMax = `$${totals.item.cost.before * (maxDiscount / 100)}`
				}
				return {
					canSet: false,
					error: errorString + ` the max discount that can be applied to this item is ${maxDiscount}%`,
					discountString: discountMax
				};
			}
		} else if (isBundle) {
			const maxDiscount = Math.max(orderDiscounts.excluded, userMaxDiscount);
			if (maxDiscount < discount) {
				let discountMax = `${maxDiscount}%`
				if(isDollarDiscount) {
					discountMax = `$${totals.item.cost.before * (maxDiscount / 100)}`
				}
				return {
					canSet: false,
					error: errorString + ` the max discount that can be applied to this item is ${maxDiscount}%`,
					discountString: discountMax
				};
			}
		} else {
			const maxDiscount = Math.max(orderDiscounts.standard, userMaxDiscount);
			if (maxDiscount < discount) {
				let discountMax = `${maxDiscount}%`
				if(isDollarDiscount) {
					discountMax = `$${totals.item.cost.before * (maxDiscount / 100)}`
				}
				return {
					canSet: false,
					error: errorString + ` the max discount that can be applied to this item is ${maxDiscount}%`,
					discountString: discountMax
				};
			}
		}
		return { canSet: true };
	},

	/**
	 * Determine if the given value is a non-zero discount
	 * @param {String} value Discount string
	 * @returns {Boolean}
	 */
	isDiscount(value) {
		return (
			_.isString(value)
			&& (_.startsWith(value, '$') || _.endsWith(value, '%'))
			&& value !== '$0'
			&& value !== '0%'
			&& value !== '%'
			&& value !== '$'
		);
	},


	// TODO: REFACTOR: Change "products" to "items" for consistency
	/**
	 * Utility function to splice items into an item list array
	 * @param {Object} order The order
	 * @param {Array<Object>} products The item(s) to add
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.replace=false]  indicates whether the item is a replacement
	 * @param {Number} [options.insertAt=0] The index to splice the item into
	 * @returns {Array<Object>} The new list of items
	 */
	insertItem(order, products, options) {
		if(!order)
			throw 'inserjtItem: order must be present'
		if(!products)
			throw 'insertItem: products must be present'


		const settings = {
			newOrder: false,
			insertAt: 0,
			replace: false,
			...options,
		};

		const $$debug = $debug.extend('insertItem');

		if($$debug.enabled)
			$$debug(_.cloneDeep(products), _.cloneDeep(order.items), settings);
		return Promise.all(
			products.map((product) =>
				product.itemType === 'title'
					? product // Is a title, return as is
					: Promise.resolve()
						.then(() => this.fulfilmentLocation(order, product, products))
						.then((fulfilment) => ({
							product: product._id,
							ref: product.ref || this.$uuid.generate(),
							parentRef: product.parentRef,

							itemType: 'product',
							productType: product.productType,
							sku: product.sku,
							name: product.name,

							quantity: product.defaultQuantity
								? product.quantity > 0
									? +product.defaultQuantity * product.quantity
									: +product.defaultQuantity
								: product.quantity > 0
									? product.quantity
									: 1,

							costOfGoods: /[^0-9\.\-]/.test(product.costOfGoods)
								? (product.costOfGoods?.replace(/[^0-9\.\-]/g, '') ?? 0.0)
								: _.get(product, 'costOfGoods', 0), // As of ABI-2480 sometimes costOfGoods may contain invalid characters
							originalCost: product.cost, // Keep track of price at time item was added (tax inclusive)

							activeBundle: product.activeBundle,
							backorders: product.backorders,
							fulfilment: settings.replace ? product.fulfilment : fulfilment,
							weight: _.get(product, 'weight', 0),
							oversized: _.get(product, 'oversized', false), // Already a boolean off the product transform - do not cast, Woo's 'no' is truthy
							note: _.get(product, 'note', ''),

							pricedIndividually: !!product.pricedIndividually ?? true, // Bundle children and parents have priced individually set, everything else defaults true
							excludeDiscount: !!product.excludeDiscount ?? false, // Bundle children and parents have exclude discount set, everything else defaults false
							maxDiscount: null,

							discounts:
								(
									this.hasItemDiscountType(product, 'sale')
									|| ['copy', 'split'].includes(settings.name)
									|| settings.replace
								)
								? _.mapValues(product.discounts, discount => {
									if (_.isPlainObject(discount)) {
										return {
											...discount,
											originalDiscount: discount?.discount,
										};
									} else { // Coupons are in an array
										return discount;
									}
								})
								: {},
						}))
						.then((item) => {
							this.applyTaxExemptionItem(item, order.instance, order.taxExempt);
							if($$debug.enabled)
								$$debug('item', _.cloneDeep(item));
							return item;
						}),
			),
			// TODO: Fetch products here?
		).then((addItems) => {
			addItems = addItems.filter((i) => i); //Remove empty items
			if (settings.newOrder) {
				return addItems;
			} else if (Number.isInteger(settings.insertAt)) {
				order.items.splice(options.insertAt, 0, ...addItems);
				return addItems;
			} else if (Number.isInteger(settings.replaceAt)) {
				order.items.splice(options.replaceAt, 1, ...addItems);
				return addItems;
			} else {
				order.items.push(...addItems);
				return addItems;
			}
		});
	},


	// TODO: REFACTOR: Change "product" to "item" for consistency
	/**
	 * Fetch ID of warehouse to use for a product
	 * @param {Object} order The order
	 * @param {Object} product Product to query
	 * @param {Array<Object>} products Product list to use as a lookup when searching bundles
	 * @returns {Promise<number>} Async return of the warehouse to use for a product
	 */
	fulfilmentLocation(order, product, products) {
		if (!_.isArray(products)) throw new Error('Invalid products array');
		var parent;
		if (
			product.parentRef // Is item a child of a bundle?
			&& (parent = products.find((parent) => parent.ref == product.parentRef)) // Parent exists
		) {
			// use bundle's warehouse
			if (parent.fulfilment) {
				return Promise.resolve(parent.fulfilment);
			} else {
				if (order.instance !== this.$session.data.settings.instance) {
					return this.$digest.get(`/api/instances/${order.instance}`, 'warehouse'); // Fetch primary warehouse from instance
				} else
					return order.instance == this.$session.data.settings.instance && this.$session.data.settings.fulfilment
						? this.$session.data.settings.fulfilment
						: undefined;
			}
		// ABI-3092
		} else if (order.instance !== this.$session.data.settings.instance) {
			// User belongs to a different instance - use order instance primary
			return Promise.resolve()
				.then(() => this.$digest.get(`/api/instances/${order.instance}`, 'warehouse'))
				.then((data) => (data.toString()));

		} else if (order.instance == this.$session.data.settings.instance && this.$session.data.settings.fulfilment) {
			// Use user preference if order instance matches
			return this.$session.data.settings.fulfilment;
		} else {
			// Give up
			return undefined;
		}
	},

	/**
	 * @typedef {Object} SplitTotals
	 * @property {Number} price Item price
	 * @property {Number} discount Discount in dollars
	 * @property {Number} discountPercent Discount as percentage
	 */

	/**
	 * Utility function to caclulate the price and discount on an item based on a unit price + discount value
	 * NOTE: This should always calculate discounts on a single item. Multiply the result if line item quantity total is needed
	 *
	 * @param {Number|String} [price=0] The unit price to work with
	 * @param {String|Array} discount The discount to apply, all formats accepted by the <orderDiscount/> component are supported e.g. `'$10'`, `'10%'`, `0`
	 * @param {Number} [quantity=1] The quantity of items, used in weird situations where you need to pass the line item value and receive a single item discount
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.discountPerItem=false] Switch between discounting items per-line or per-item
	 * @returns {SplitTotals} An object containing the calculated values. Object has the form `{price: Number, discount: Number, discountPercent: Number}`
	 */
	splitPrice(price, discount, quantity = 1, options) {
		const settings = {
			discountPerItem: false,
			...options,
		};

		const $$debug = $debug.extend('splitPrice');

		$$debug(price, discount, quantity);

		if (_.isNil(price)) price = 0;
		price = new Big(price);

		let discounts = _.isArray(discount) ? discount : [discount];

		// TODO: Encapsulate duplication
		discounts = discounts.sort((a, b) =>
			// Send fixed dollar to end
			(a.startsWith('$') && b.endsWith('%')) ||
				// Percentages by largest % first
				(a.endsWith('%') && b.endsWith('%') && parseFloat(a.slice(1)) < parseFloat(b.slice(1)))
				? 1
				: -1,
		);
		if($$debug.enabled)
			$$debug('discounts.sorted', _.cloneDeep(discounts));

		// How to add percents (where discounts are a decimal)
		// (disc1 + disc2) - (disc1 * disc2) or
		// 1 - ( (1 - disc1) * (1 - disc2) )
		return _.chain(discounts)
			.reduce(
				(totals, disc) => {
					if($$debug.enabled)
						$$debug('totals', _.mapValues(totals, d => d.toFixed(4)), disc);
					const out = {};
					const totalPrice = totals.price;
					const totalDiscount = totals.discount;
					const totalDiscountPercent = totals.discountPercent;

					if (_.isString(disc) && disc.startsWith('$')) {
						let discountString = disc.substring(1);
						if (_.isEmpty(discountString) || !_.isFinite(parseFloat(discountString))) discountString = 0;

						// NOTE: Divided by quantity because fixed dollar discounts apply to the line sub-total rather than per item cost
						const discountValRaw = settings.discountPerItem
							? new Big(discountString)
							: new Big(discountString).div(quantity);

						// Cap the dollar discount at the remaining price so lines never go
						// below $0 (100% discount max).  With multiple stacking coupons the
						// earlier ones consume available headroom; later ones get less.
						const discountVal = totalPrice.gt(0)
							? (discountValRaw.gt(totalPrice) ? totalPrice : discountValRaw)
							: new Big(0);

						out.price = totalPrice.minus(discountVal);
						out.discount = totalDiscount.plus(discountVal);
						// FIXME: "price" may be zero if "bundle" has since become "simple" @see ABI-2675
						out.discountPercent = price.gt(0)
							? totalDiscountPercent.plus(discountVal.div(price))
							: totalDiscountPercent;

						//console.assert(out.discountPercent.gt(0) && out.discountPercent.times(price).eq(discountVal), 'Discount percentage times price does not equal discount dollars');
						if($$debug.enabled) {
							$$debug('discountPercent.dollar', price.toString(), discountString, out.discountPercent.toString());
							$$debug('discountVal.dollar', price.toString(), discountString, discountVal.toString());
							$$debug('out.dollar', discountString, _.mapValues(out, d => d.toString()));
						}

						return out;
					} else if (_.isString(disc) && disc.endsWith('%')) {
						let discountString = disc.slice(0, -1);
						if (_.isEmpty(discountString) || !_.isFinite(parseFloat(discountString))) discountString = 0;

						const discountPercent = new Big(discountString).div(100);
						//const discountVal = new Big(price).times(discountPercent); // Simply stack percentage discounts
						//const discountVal = new Big(price).minus(totalDiscount).times(discountPercent); // Reduce by existing discount before applying this one
						//const discountVal = new Big(totalPrice).times(discountPercent); // Applying discount to price which remains after other discounts are applied
						const discountVal = (features.roundDiscounts)
							? new Big(totalPrice)
								.times(quantity)
								.times(discountPercent)
								.round(2) // NOTE: Round at the line level before determining the per-item value
								.div(quantity)
							: new Big(totalPrice)
								.times(quantity)
								.times(discountPercent)
								.div(quantity);
						$$debug('totalPrice', totalPrice.toString());
						$$debug('discountPercent', discountPercent.toString());
						$$debug('discountVal', discountVal.toString());

						out.price = totalPrice.minus(discountVal);
						out.discount = totalDiscount.plus(discountVal);
						out.discountPercent = totalDiscountPercent.plus(discountPercent); // FIXME: Should be a calculation as above?

						$$debug('discountPercent.percent', price.toString(), discountString, out.discountPercent.toString());
						$$debug('discountVal.percent', price.toString(), discountString, discountVal.toString());

						$$debug('out.percent', discountString, _.mapValues(out, d => d.toString()));
						return out;
					} else {
						return totals;
					}
				},
				{
					price: new Big(price), // FIXME: Dislike that we flip back and forth between "price" and "cost", should be "price" throughout the system. "Cost of Goods" being the only "cost".
					discount: new Big(0),
					discountPercent: new Big(0),
				},
			)
			.thru((v) => ({
				price: v.price,
				discount: v.discount,
				discountPercent: v.discountPercent.times(100), // NOTE: Multipled to match format of "10%" strings coming in from data // FIXME: Needed? Could keep percentages as factors?
			}))
			.thru((v) => {
				if($$debug.enabled)
					$$debug('return', _.mapValues(v, d => d.toString()));
				if (v.discount.gt(0)) {
					const checkDollars = new Big(v.price).plus(v.discount);
					const checkPercent = new Big(price).times(v.discountPercent);
					console.assert(
						checkDollars.eq(price),
						checkDollars.toString(),
						price.toString(),
						'Discounted price plus discount dollars does not equal original price',
					);
					console.assert(
						!checkPercent.eq(v.discount),
						checkPercent.toString(),
						v.discount.toString(),
						'Original price times discount percentage does not equal discount dollars',
					);
				}
				return v;
			})
			.value();
	},

	/**
	 * Utility function to calculate region specific currency string
	 * @param {Number} value Price
	 * @param {String} [instance='AU'] Instance name
	 * @returns {String} Formatted currency string
	 */
	formatCurrency(value, instance = 'AU') {
		// FIXME: Handle BigJS explicitly; Will fail if BigJS is put into strict mode.
		if (typeof value != 'number') value = parseFloat(value);
		if (_.isNaN(value)) value = 0;

		// TODO: Get codes from config; "app.config.instances[order.instance].currency"
		const countries = {
			AU: {locale: 'en-AU', currency: 'AUD'},
			CN: {locale: 'zh-CN', currency: 'CNY'},
			NZ: {locale: 'en-NZ', currency: 'NZD'},
			UK: {locale: 'en-GB', currency: 'GBP'},
		};

		const country = countries[instance];
		if (!country) throw new Error(`Unknown instance code "${instance}"`);

		return Number(value).toLocaleString(
			country.locale,
			{
				style: 'currency',
				currency: country.currency,
			}
		);
	},

	/**
	 *
	 * A utility that returns the currency symbol of a particular instance.
	 * @param {String} instance the instance which to get the currency symbol of
	 * @returns {String}
	 */
	getCurrencySymbol(instance) {
		// TODO: REFACTOR: "get" prefix is not used in this library
		if (instance == 'UK') instance = 'GB';
		var currencyCodes = {
			AU: 'AUD',
			NZ: 'NZD',
			GB: 'GBP',
		};

		if (!currencyCodes[instance]) instance = 'AU';
		const formatter = new Intl.NumberFormat('en-' + instance, {
			style: 'currency',
			currency: currencyCodes[instance],
		});
		const parts = formatter.formatToParts(0);
		const symbol = parts.find((part) => part.type === 'currency');
		return symbol.value;
	},


	/**
	 * Determine each items share of any discount remainder.
	 * @see https://github.com/woocommerce/woocommerce/blob/3b2f568a9750238a847b7673424b2d99a1a5654d/plugins/woocommerce/includes/class-wc-discounts.php#L554-L592
	 *
	 * @param {Array} items List of items to share among
	 * @param {Big} remainderDollars Discount remainder to apply
	 * @param {Object} [options] Options
	 * @param {Function} [options.filter] Optionally filter items while respecting existing indexes
	 * @returns {Array}
	 */
	getOrderItemDiscountRemainderShares(items, remainderDollars, options) {
		const $$debug = $debug.extend('getOrderItemDiscountRemainderShares');

		const settings = {
			filter: () => true,
			...options
		};

		if($$debug.enabled)
			$$debug(
				_.cloneDeep(items),
				remainderDollars.toString(),
				settings,
			);

		const remainingDiscountCents = new Big(remainderDollars).times(100);
		$$debug('remainingDiscountCents', remainingDiscountCents.toString());

		let totalCents = 0;
		const out = items.map(item => {
			if($$debug.enabled) $$debug('loop', totalCents, _.cloneDeep(item), this.isItemBundleAdditionalCharge(item), (!settings.filter.call(item, item)));
			// FIXME: Is there a case under which additional charge line must not be included? When used in "applyCoupons"?
			// NOTE: We skip "Additional charge" line as any adjustment to it would stomp percentages saved against it; While Woo simply shares between other items
			// NOTE: We have to include "Additional charge" line as it may be the only priced item... Any additional clauses required? Could only include it when no children are priced?
			// if (this.isItemBundleAdditionalCharge(item)) return 0;
			if (!settings.filter.call(item, item)) return 0;

			let itemCents = 0;
			if (remainingDiscountCents.abs().lt(1)) return itemCents;
			if (new Big(totalCents).gte(remainingDiscountCents.abs())) return itemCents;

			for (let i = 0; i < _.get(item, 'quantity', 1); i++) {
				// TODO: If the price is less than a cent then apply only that price and not a full cent
				// @see https://github.com/woocommerce/woocommerce/blob/3b2f568a9750238a847b7673424b2d99a1a5654d/plugins/woocommerce/includes/class-wc-discounts.php#L575
				const itemDiscount = 1;
				// NOTE: Woo doesn't seem to push in either direction but instead always rounds down and then adds back any missing cents
				if (remainingDiscountCents.s === 1) {
					totalCents += itemDiscount;
					itemCents += itemDiscount;
					$$debug('add', itemCents);
				} else {
					totalCents += itemDiscount;
					itemCents -= itemDiscount;
					$$debug('sub', itemCents);
				}
				if (new Big(totalCents).gte(remainingDiscountCents.abs())) break;
			}
			return itemCents;
		});
		$$debug('out', out);
		return out;
	},


	/**
	 * Determine child's share of a discount applied to bundle
	 * This is the pure unrounded share without taking into consideration rounding of other children
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} bundle Item (bundle)
	 * @param {Object} child Item (bundle child)
	 * @param {Object} products Products API lookup
	 * @param {String} discount Discount string
	 * @param {Object} [options] Options
	 * @param {Function} [options.roundingMethod=Big.roundHalfUp] Specify rounding method; Rounding down mirrors Woo coupons but results in greater fudge cents added to children
	 * @returns {String}
	 */
	getBundleChildDiscountShare(order, children, bundle, child, products, discount, options) {
		const $$debug = $debug.extend('getBundleChildDiscountShare');

		if (!_.isPlainObject(order)) {
			console.error('order', order)
			throw new Error('getBundleChildDiscountShare(): Invalid order object');
		}
		if (!_.isPlainObject(bundle)) {
			if($$debug.enabled) $$debug(_.cloneDeep(order));
			console.error('bundle', bundle)
			throw new Error('getBundleChildDiscountShare(): Invalid bundle object');
		}
		if (!_.isPlainObject(child)) {
			console.error('child', child)
			throw new Error('getBundleChildDiscountShare(): Invalid child object');
		}
		if (!_.isPlainObject(products)) {
			console.error('products', products)
			throw new Error('getBundleChildDiscountShare(): Invalid products lookup object');
		}

		const settings = {
			...options
		};

		if($$debug.enabled)
			$$debug(
				'in',
				_.cloneDeep(order),
				_.cloneDeep(children),
				_.cloneDeep(bundle),
				_.cloneDeep(child),
				_.cloneDeep(products),
				discount,
				settings,
			);

		if (!children) children = this.getOrderItemChildren(order.items, bundle);
		if (!_.isString(discount)) discount = '';

		if (bundle.itemType !== 'product' || child.itemType !== 'product') return '$0';
		// FIXME: Return customer excluded discount when it exists? Cover with tests.
		if (!child.pricedIndividually) return '$0';
		//if (!child.pricedIndividually || this.isItemBundleAdditionalCharge(child)) return '$0';

		const bundleCost = this.bundleCost(order, children, bundle, products, {
			//taxExempt: order.taxExempt, // Deprecated
			instance: order.instance,
			considerDiscount: false,
		});
		$$debug('bundleCost', bundleCost.toString());

		// Percentages must be converted to dollars so that rounding can be properly handled on children
		let discountValueIncoming = new Big(0);
		if (_.startsWith(discount, '$')) {
			discountValueIncoming = new Big(discount.replace(/[^0-9\.\-]/g, ''));
			discountValueIncoming = (order.taxExempt)
				? (features.roundDiscounts)
					? discountValueIncoming.minus(this.calcTax(order.instance, discountValueIncoming)).round(2)
					: discountValueIncoming.minus(this.calcTax(order.instance, discountValueIncoming))
				: discountValueIncoming;
		} else if (_.endsWith(discount, '%')) {
			// FIXME: The discount might not be applying to whole bundle; As is the case with customer excluded discount only applying to excluded child items
			// TODO: When adding up "bundleCost" pass via "isItemBundleChildExcludedDiscount"?
			// NOTE: "taxExempt" is already applied to "bundleCost"
			discountValueIncoming = (features.roundDiscounts)
				? new Big(discount.replace(/[^0-9\.\-]/g, ''))
					.div(100)
					.times(bundleCost)
					.round(2)
				: new Big(discount.replace(/[^0-9\.\-]/g, ''))
					.div(100)
					.times(bundleCost)
		}
		$$debug('discountValueIncoming', discount, discountValueIncoming.toString());

		const itemCost = this.itemCost(order, children, child, products, {
			considerDiscount: false,
		}).times(_.get(child, 'quantity', 1));
		$$debug('itemCost', itemCost.toString());

		// Child's share of bundle
		const shareDiscount = (features.roundDiscounts)
			? discountValueIncoming
				.div(bundleCost)
				.times(itemCost)
				.round(2)
			: discountValueIncoming
				.div(bundleCost)
				.times(itemCost);
		$$debug('shareDiscount', shareDiscount.toString());

		// Include "Additional charge" line to ensure remainders can apply to it
		const allChildren = [this.createAdditionalChargeItem(order, children, bundle, products)].concat(children).filter(Boolean);
		$$debug('allChildren', _.cloneDeep(allChildren));

		// Tally up all children while applying rounding
		// NOTE: Must included the "Additional charge" line
		// FIXME: The discount might not be applying to whole bundle; As is the case with customer excluded discount only applying to excluded child items
		const totalDiscount = allChildren
			.map(d => {
				const itemCost = this.itemCost(order, children, d, products, {
					considerDiscount: false,
				});
				$$debug('child.itemCost', d.product, itemCost.toString(), d.quantity);
				const childValue = (features.roundDiscounts)
					? discountValueIncoming
						.div(bundleCost)
						.times(itemCost)
						.round(2)
					: discountValueIncoming
						.div(bundleCost)
						.times(itemCost);
				$$debug('child.discount', d.product, childValue.toString());
				return childValue;
			})
			.reduce((acc, cur) => acc.plus(cur), new Big(0));

		// Compare the total of rounded children to the original desired discount.
		$$debug('discountValueIncoming', discountValueIncoming.toString());
		$$debug('totalDiscount', totalDiscount.toString());
		const remainingDiscount = totalDiscount.minus(discountValueIncoming);
		$$debug('remainingDiscount', remainingDiscount.toString());
		if (remainingDiscount.gt(0)) console.warn('Child discounts do not add up to bundle parent', remainingDiscount.toString());

		let out;
		if (features.addRemainingCentsToBundleChildren) {
			// NOTE: Woo handles this with the remainder being adjusted one item at a time cent by cent.
			// @see https://github.com/woocommerce/woocommerce/blob/3b2f568a9750238a847b7673424b2d99a1a5654d/plugins/woocommerce/includes/class-wc-discounts.php#L554-L592
			const orderItemsRemainingCents = this.getOrderItemDiscountRemainderShares(allChildren, remainingDiscount);
			$$debug('orderItemsRemainingCents', _.cloneDeep(orderItemsRemainingCents));

			const itemCurrentIndex = allChildren.findIndex(c => c.ref === child.ref);
			$$debug('itemCurrentIndex', itemCurrentIndex);
			const orderItemsRemainingDollars = new Big(orderItemsRemainingCents[itemCurrentIndex]).div(100);
			$$debug('orderItemsRemainingDollars', orderItemsRemainingDollars.toString());

			out = (features.roundDiscounts)
				? '$' + shareDiscount.minus(orderItemsRemainingDollars).toFixed(2)
				: '$' + shareDiscount.minus(orderItemsRemainingDollars).toString();
		} else {
			out = (features.roundDiscounts)
				? '$' + shareDiscount.toFixed(2)
				: '$' + shareDiscount.toString();
		}

		$$debug('out', out);
		return out;
	},

	/**
	 * Reduces bundle line fixed dollar discounts by value which has already been consumed by children.
	 * This is required as the bundle retains it's discounts for UI purposes, these are shared out among children.
	 * Then when actually calculating discounts these child values must be removed from whatever was set against the parent.
	 *
	 * TODO: Alternative there may be a pattern where only the children are added up and bundle parent acts only as a UI item.
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} bundle Find child items belonging to this bundle
	 * @param {Object} products Products API lookup
	 * @returns {Array}
	 */
	getBundleDiscountChildrenReduced(order, children, bundle, products) {
		if (!_.isPlainObject(order)) throw new Error('getBundleDiscountChildrenReduced(): Invalid order object');
		if (!_.isPlainObject(bundle)) throw new Error('getBundleDiscountChildrenReduced(): Invalid bundle object');
		if (!_.isPlainObject(products)) throw new Error('getBundleDiscountChildrenReduced(): Invalid products lookup object');

		const $$debug = $debug.extend('getBundleDiscountChildrenReduced');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(bundle), _.cloneDeep(products));

		if (bundle.itemType !== 'product') return {};
		if (!_.isPlainObject(bundle.discounts)) return {};

		if (!children) children = this.getOrderItemChildren(order.items, bundle);

		const reduceDiscountByType = (bundleDiscount, discountType, bundleDiscountIndex) => {
		if($$debug.enabled)
			$$debug(
				'reduceDiscountByType',
				_.cloneDeep(bundleDiscount),
				discountType,
				bundleDiscountIndex,
			);

			const bundleDiscountValue =
				_.isArray(bundleDiscount[discountType]) && _.isNumber(bundleDiscountIndex)
					? _.isString(bundleDiscount[discountType][bundleDiscountIndex].originalDiscount)
						? new Big(
							bundleDiscount[discountType][bundleDiscountIndex].originalDiscount.replace(
								/[^0-9\.\-]/g,
								'',
							),
						)
						: new Big(0)
					: _.isString(bundleDiscount[discountType].originalDiscount)
						? new Big(bundleDiscount[discountType].originalDiscount.replace(/[^0-9\.\-]/g, ''))
						: new Big(0);
			$$debug(
				'reduceDiscountByType.bundleDiscountValue',
				bundleDiscountValue.toString(),
			);

			const childrenDiscountValue = children
				.filter((child) => child.pricedIndividually)
				.filter((child) => !this.isItemBundleAdditionalCharge(child))
				.map((child) => {
					if($$debug.enabled)
						$$debug(
							'reduceDiscountByType.child',
							child.product,
							_.cloneDeep(child),
							child.discounts[discountType],
						);
					// Only concerned with adjusting fixed dollar discounts
					let childDiscount;
					if (_.isArray(child.discounts[discountType]) && _.isNumber(bundleDiscountIndex)) {
						childDiscount = child.discounts[discountType][bundleDiscountIndex];
					} else if (_.isPlainObject(child.discounts[discountType])) {
						childDiscount = child.discounts[discountType];
					}
					if($$debug.enabled)
						$$debug(
							'reduceDiscountByType.child.childDiscount',
							child.product,
							childDiscount,
							_.isPlainObject(childDiscount),
							_.isString(childDiscount?.originalDiscount),
							_.startsWith(childDiscount?.originalDiscount, '$'),
						);
					if (!_.isPlainObject(childDiscount)) return new Big(0);
					if (!_.isString(childDiscount.originalDiscount)) return new Big(0);
					if (!_.startsWith(childDiscount.originalDiscount, '$')) return new Big(0);

					// NOTE: Wish to keep original shares of bundle discount regardless of subsequent overrides; The follow code recalculates shares when child winners change
					//if (
					//	['sale', 'override', 'line'].includes(discountType) // Greatest only applies to these discount types. Coupons (or any other future discount types) always apply.
					//	&& this.itemDiscountGreater(order, child, products).name !== discountType
					//) return new Big(0); // Only reducing bundle those child discounts of the same type which are greatest

					const childDiscountValue = new Big(childDiscount.originalDiscount.replace(/[^0-9\.\-]/g, ''));
					if($$debug.enabled)
						$$debug(
							'reduceDiscountByType.child.childDiscountValue',
							child.product,
							childDiscountValue.toString(),
						);
					return childDiscountValue;
				})
				.flat()
				.reduce((acc, cur) => acc.plus(cur), new Big(0));

			if($$debug.enabled) {
				$$debug(
					'reduceDiscountByType.bundleDiscountValue',
					bundleDiscountValue.toString(),
				);
				$$debug(
					'reduceDiscountByType.childrenDiscountValue',
					childrenDiscountValue.toString(),
				);
			}

			// TODO: Possible that instead of adjusting the parent, the parent is instead simply the sum of the children. Mixed bundles may preclude this for now. Doing so would mean applyCoupons could ignore parents and only deal with "pricedIndividually" items.
			const discountAdjusted = bundleDiscountValue.minus(childrenDiscountValue);
			if($$debug.enabled) {
				$$debug(
					'reduceDiscountByType.discountAdjusted',
					discountAdjusted.toString(),
				);
			}

			// NOTE: "taxExempt: false" as discount is "original" version
			// NOTE: Rounded off to zero when vanishingly small. Otherwise tax conversion will blow up the error.
			if (_.isArray(bundleDiscount[discountType]) && _.isNumber(bundleDiscountIndex)) {
				this.applyDiscountWithTax(
					order,
					bundleDiscount[discountType][bundleDiscountIndex],
					discountAdjusted.lt(0.01) ? '$0.00' : '$' + discountAdjusted.toString(),
					false,
				);
				if($$debug.enabled)
					$$debug(
						'reduceDiscountByType.out',
						_.cloneDeep(bundleDiscount[discountType][bundleDiscountIndex]),
					);
				return bundleDiscount[discountType][bundleDiscountIndex];
			} else {
				this.applyDiscountWithTax(
					order,
					bundleDiscount[discountType],
					discountAdjusted.lt(0.01) ? '$0.00' : '$' + discountAdjusted.toString(),
					false,
				);
				if($$debug.enabled)
					$$debug(
						'reduceDiscountByType.out',
						_.cloneDeep(bundleDiscount[discountType]),
					);
				return bundleDiscount[discountType];
			}
		};

		const discounts = _.cloneDeep(bundle.discounts);
		// TODO: ABI-2665_3of13: Do nothing? Parent only has the discount?
		//if (this.hasItemDiscountType(bundle, 'sale')) {
		//	discounts.sale = _.cloneDeep(bundle.discounts.sale);
		//}

		// TODO: ABI-2665_3of13: Do the same for overrides?

		// Only concerned with adjusting fixed dollar discounts
		if (this.hasItemDiscountType(bundle, 'line')) {
			if (_.startsWith(discounts.line.originalDiscount, '$')) {
				discounts.line = reduceDiscountByType(discounts, 'line');
			} else if (_.endsWith(discounts.line.originalDiscount, '%')) {
				discounts.line = discounts.line;
			}
		}

		if (this.hasItemDiscountType(bundle, 'coupons')) {
			_.cloneDeep(discounts.coupons).map((bundleCouponDiscount, bundleCouponDiscountIndex) => {
				if (_.startsWith(bundleCouponDiscount.originalDiscount, '$')) {
					discounts.coupons[bundleCouponDiscountIndex] = reduceDiscountByType(
						discounts,
						'coupons',
						bundleCouponDiscountIndex,
					);
				} else if (_.endsWith(bundleCouponDiscount.originalDiscount, '%')) {
					discounts.coupons[bundleCouponDiscountIndex] = discounts.coupons[bundleCouponDiscountIndex];
				}
				discounts.coupons[bundleCouponDiscountIndex].discountType = bundleCouponDiscount.discountType;
			});
		}

		if($$debug.enabled)
			$$debug('out', _.cloneDeep(discounts));
		return discounts;
	},

	/**
	 * Limit customer discounts by item max
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @param {Number|Big} cost Cost to use when determining max
	 * @returns {Object} Customer discounts with maxDiscount applied
	 */
	getMaxDiscountCustomerDiscounts(order, children, item, products, cost) {
		if (!_.isPlainObject(order)) throw new Error('getMaxDiscountDifference(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('getMaxDiscountDifference(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('getMaxDiscountDifference(): Invalid products lookup object');
		const customerDiscounts = _.cloneDeep(order?.customerDiscounts);

		const $$debug = $debug.extend('getMaxDiscountCustomerDiscounts');

		if (_.isNil(customerDiscounts)) return customerDiscounts;

		// Apply this item's "maxDiscount" to effect customer discounts
		Object.entries(customerDiscounts).forEach(([key, val]) => {
			const discountValue = val.replace(/[^0-9\.\-]/g, '');
			const discount = this.splitPrice(cost, val, item.quantity).discount;
			const discountDollars = '$' + discount.toString();
			// NOTE: We wish to limit customer discount simply rather than search for maximum value of limit
			const appliedMax = Math.max(this.getMaxDiscountDifference(order, children, item, products, discountDollars), 0);
			$$debug('customerDiscounts', key, val, discountDollars, appliedMax);
			// NOTE: Customer discounts are always percentages
			// TODO: Implement for both percentage and fixed dollar customer discounts for completeness
			customerDiscounts[key] = new Big(discountValue).minus(new Big(appliedMax).times(100)) + '%';
		});
		if (!_.isNil(customerDiscounts))
			$$debug('customerDiscounts', _.cloneDeep(customerDiscounts));
		return customerDiscounts;
	},

	/**
	 * Determine how much of a discount may be applied
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @param {String} discount Discount
	 * @returns {Number} Difference between input and max, allowing Binary Search in direction of difference
	 */
	getMaxDiscountDifference(order, children, item, products, discount) {
		if (!_.isPlainObject(order)) throw new Error('getMaxDiscountDifference(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('getMaxDiscountDifference(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('getMaxDiscountDifference(): Invalid products lookup object');

		if (item.itemType !== 'product') return 0;

		const $$debug = $debug.extend('getMaxDiscountDifference');

		const discountValue = _.isString(discount) ? new Big(discount.replace(/[^0-9\.\-]/g, '')) : new Big(0);
		if($$debug.enabled) {
			$$debug('discount', item.product, discount);
			$$debug('discountValue', item.product, discountValue.toString());
		}
		if (!discountValue.gt(0)) return 0;

	// NOTE: item.maxDiscount has been removed as of ABI-3235
		item.maxDiscount = '0'
		// If max is dollars, compare directly
		const maxValue = _.isString(item.maxDiscount)
			? new Big(item.maxDiscount.replace(/[^0-9\.\-]/g, '')).div(100)
			: new Big(0);
		if($$debug.enabled)
			$$debug('maxValue', item.product, maxValue.toString());
		if (_.startsWith(item.maxDiscount, '$')) {
			if (!discountValue.eq(maxValue)) {
				if($$debug.enabled)
					$$debug(
						'MISS.fixed',
						item.product,
						discountValue.toString(),
						discountValue.minus(maxValue).toNumber(),
					);
				return discountValue.minus(maxValue).toNumber();
			}

			// Otherwise find percentage of item total and then compare
		} else if (_.endsWith(item.maxDiscount, '%')) {
			const cost = this.itemCost(order, children, item, products, {
				considerDiscount: false,
			}).times(item.quantity);
			// NOTE: Expecting discount input to be a dollar figure
			const discountPercent = discountValue.div(cost);
			if($$debug.enabled)
				$$debug(
					'discountPercent',
					item.product,
					item.maxDiscount,
					discountPercent.toString(),
					maxValue.toString(),
				);
			if (!discountPercent.eq(maxValue)) {
				if($$debug.enabled)
					$$debug(
						'MISS.percent',
						item.product,
						discountValue.toString(),
						discountPercent.toString(),
						maxValue.toString(),
						discountPercent.minus(maxValue).toNumber(),
					);
				return discountPercent.minus(maxValue).toNumber();
			}
		}

		return 0;
	},

	/**
	 * Perform "maxDiscount" validation of an individual item
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Number} Difference between input and max, allowing Binary Search in direction of difference
	 */
	validateMaxDiscount(order, children, item, products) {
		if (!_.isPlainObject(order)) throw new Error('validateMaxDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('validateMaxDiscount(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('validateMaxDiscount(): Invalid products lookup object');

		if (item.itemType !== 'product') return 0;

		const $$debug = $debug.extend('validateMaxDiscount');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(item), _.cloneDeep(products));

		if (!children) children = this.getOrderItemChildren(order.items, item);

		const discount = this.itemDiscount(order, children, item, products);
		return this.getMaxDiscountDifference(order, children, item, products, discount);
	},

	/**
	 * Ensure discount is not greater than maxDiscount
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @returns {Promise}
	 */
	applyMaxDiscount(order, children, item, products) {
		if (!_.isPlainObject(order)) throw new Error('applyMaxDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('applyMaxDiscount(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('applyMaxDiscount(): Invalid products lookup object');

		if (item.itemType !== 'product') return Promise.resolve();

		// Clone so as to not be hammering UI updates
		const cloneItem = _.cloneDeep(item);
		// TODO: ABI-2665_3of13: Apply limits throughout the process, thus new discounts aren't split between items which are already full

		//if (_.isNil(item.maxDiscount))
		//	item.maxDiscount = this.hasItemDiscountType(item, 'sale')
		//		? (this.$session.hasPermission('retailDiscount') ? '45%' : '40%')  // If the item is on sale allow 40% (from max price)
		//		: (this.$session.hasPermission('retailDiscount') ? '40%' : '35%'); // Default to 40% // TODO: Default somewhere other than deeply embeded in logic; Configurable

		// FIXME: What about "0"?
		if (_.isNil(cloneItem.maxDiscount) || cloneItem.maxDiscount === '0') cloneItem.maxDiscount = '100%';

		const $$debug = $debug.extend('applyMaxDiscount');

		if($$debug.enabled)
			$$debug(
				_.cloneDeep(order),
				_.cloneDeep(cloneItem),
				_.cloneDeep(cloneItem.discounts),
				cloneItem.maxDiscount,
			);

		if (!children) children = this.getOrderItemChildren(order.items, item);

		return (
			Promise.resolve()
				// Customer Discounts {{{
				.then(() => {
					if (this.itemDiscountGreater(order, children, cloneItem, products).name !== 'customer') return;

					// Valid discount; Already under maximum, no need to search further
					if (this.validateMaxDiscount(order, children, cloneItem, products) <= 0) {
						if($$debug.enabled)
							$$debug('VALID', cloneItem.product, _.cloneDeep(cloneItem));
						return;
					}

					$$debug('INVALID CUSTOMER DISCOUNT');
				})
				// }}}
				// Item Discounts {{{
				.then(() => {
					if (!this.isDiscount(cloneItem?.discounts?.line?.originalDiscount)) return;
					// ABI-3225: Avoid attempting to reduce line discount when they're not being applied
					if (this.itemDiscountGreater(order, children, cloneItem, products).name !== 'line') return;

					// Valid discount; Already under maximum, no need to search further
					if (this.validateMaxDiscount(order, children, cloneItem, products) <= 0) {
						if($$debug.enabled)
							$$debug('VALID', cloneItem.product, cloneItem.discounts.line, _.cloneDeep(cloneItem));
						return;
					}

					const incomingDiscountValue = parseFloat(
						cloneItem.discounts.line.originalDiscount.replace(/[^0-9\.\-]/g, ''),
					);
					if (!(incomingDiscountValue > 0)) return;

					// FIXME: Will stop at the first valid value without searching back up to the highest valid value

					// Invalid discount; Binary Search for value matching maximum
					let discountRemainder;
					let cnt = 0;
					let step = incomingDiscountValue / 2; // NOTE: Start halfway through the space. When step size equals incoming discount value the algo stops at $0.00
					let lastDiscountRemainder;
					while ((discountRemainder = this.validateMaxDiscount(order, children, cloneItem, products))) {
						if (cnt++ > 10000) {
							debugger;
							$$debug('FAIL.TIMEOUT', cloneItem.product, _.cloneDeep(cloneItem));
							this.$toast.warning(
								'Failed to determine nearest discount which satisfies maximum within reasonable time',
							);
							break; // Emergency escape. Should find value much faster than this.
						}
						$$debug(
							'search',
							cloneItem.product,
							step,
							discountRemainder,
							cloneItem.discounts?.line.originalDiscount,
						);

						let adjustedDiscountValue = _.isString(cloneItem.discounts?.line.originalDiscount)
							? parseFloat(cloneItem.discounts.line.originalDiscount.replace(/[^0-9\.\-]/g, ''))
							: 0;
						if (adjustedDiscountValue <= 0) break; // FIXME: Right? Not part of the search?

						if (discountRemainder > 0) {
							if (step > adjustedDiscountValue) step = adjustedDiscountValue / 2;
							$$debug('reduce', cloneItem.product, step, discountRemainder);
							adjustedDiscountValue -= step;
						} else {
							$$debug('increase', cloneItem.product, discountRemainder);
							adjustedDiscountValue += step;
						}
						$$debug('attempting', adjustedDiscountValue);
						if (_.startsWith(cloneItem.discounts?.line?.originalDiscount, '$')) {
							this.applyDiscountWithTax(
								order,
								cloneItem.discounts.line,
								'$' + adjustedDiscountValue.toFixed(2),
								false,
							); // Passing tax inclusive value
						} else if (_.endsWith(cloneItem.discounts?.line?.originalDiscount, '%')) {
							this.applyDiscountWithTax(
								order,
								cloneItem.discounts.line,
								adjustedDiscountValue.toFixed(1) + '%',
								false,
							);
						}

						$$debug(
							'adjustedDiscountValue',
							cloneItem.product,
							cloneItem.discounts?.line?.originalDiscount,
							adjustedDiscountValue,
						);
						if (adjustedDiscountValue <= 0) break;

						// Only reduce steps when changing direction
						if (Math.sign(lastDiscountRemainder) !== Math.sign(discountRemainder)) step /= 2;
						lastDiscountRemainder = discountRemainder;
					}
					$$debug('TARGET', cloneItem.product, discountRemainder);

					// Finally apply the discount to the underlying item
					this.applyDiscountWithTax(order, item.discounts.line, cloneItem.discounts.line.originalDiscount);
				})
				// }}}

				// TODO: Coupon Discounts {{{
				.then(() => {
					if (!this.isDiscount(cloneItem?.discounts?.line?.originalDiscount)) return;

					if (this.validateMaxDiscount(order, children, item, products) !== 0) {
						$$debug('FAIL.TODO', cloneItem.product);
						// TODO: ABI-2665_1of13: What if that is not good enough? The change should not be permitted, but the change may be many things other than adding a discount i.e. Adjusting quantity or adding items
						// TODO: ABI-2665_3of13: Should re-apply maximums during "applyCoupons"?
						//this.applyDiscountWithTax(order, item.discounts.line, '0');
					}
				})
		);
		// }}}
	},

	/**
	 * Calculates bundle children cost and takes into consideration indvidually priced children.
	 * If a child is indvidually priced their price is considered otherwise it is take to be zero.
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} bundle Order item
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.singleCost=false] Cost for a single item
	 * @param {Boolean} [options.considerDiscount=false] Include discounts
	 * @param {Boolean} [options.considerDiscountTypes=['customer', 'coupons', 'line', 'override', 'sale']] Included discount types
	 * @param {Boolean} [options.useOriginalCost=false] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.perLineRounding=true] Apply rounding per line
	 * @returns {Number|Big} High precision floating point representing the cost of the bundle children
	 */
	bundleChildrenCost(order, children, bundle, products, options) {
		if (!_.isPlainObject(order)) throw new Error('bundleChildrenCost(): Invalid order object');
		if (!_.isPlainObject(bundle)) throw new Error('bundleChildrenCost(): Invalid bundle object');
		if (!_.isPlainObject(products)) throw new Error('IbundleChildrenCost(): nvalid products lookup object');

		const settings = {
			singleCost: false,
			considerDiscount: false, // Apply discounts to children
			considerDiscountTypes: ['customer', 'coupons', 'line', 'override', 'sale'],
			useOriginalCost: false, // to avoid "taxExempt" affected cost, use in preTaxSubTotal()
			perLineRounding: true,
			...options,
		};

		const $$debug = $debug.extend('bundleChildrenCost');

		if($$debug.enabled)
			$$debug('in', _.cloneDeep(order), _.cloneDeep(children), _.cloneDeep(bundle), _.cloneDeep(products), _.cloneDeep(settings));

		if (!children) children = this.getOrderItemChildren(order.items, bundle);

		const out = children
			.filter((item) => this.hasItemBundleChildPrice(item, products))
			.map((item) => {
				const childDiscount = (settings.considerDiscount) ? this.itemDiscount(order, children, item, products, settings) : '0';

				let cost = this.splitPrice(
					this.itemCostField(item, settings),
					childDiscount,
					item.quantity,
				).price.times(_.get(item, 'quantity', 1));

				// NOTE: ABI-3117 per line rounding https://freedcamp.com/view/2713393/issuetracker/341094#comment-27538254
				if(settings.perLineRounding) cost = cost.round(2);

				if (settings.singleCost) {
					cost = cost
						.div(_.get(item, 'quantity', 1)) // Go back to single child item
						.times(new Big(+_.get(item, 'quantity', 1)).div(+_.get(bundle, 'quantity', 1))) // Ensure child has appropriate quantity for 1 bundle
				}

				$$debug(
					'cost',
					item.product,
					_.get(products, [item.product, 'sku']),
					settings.considerDiscount,
					settings.perLineRounding,
					cost.toNumber(),
					childDiscount,
				);
				return cost;
			})
			.reduce((t, v) => t.plus(v), new Big(0));

		if ($$debug.enabled) {
			$$debug('bundle', _.cloneDeep(bundle));
			$$debug('product', bundle.product);
			$$debug('cost', bundle?.cost);
			$$debug('quantity', bundle?.quantity);
			$$debug('considerDiscount', settings.considerDiscount);
			$$debug('singleCost', settings.singleCost);
			$$debug('out', out.toNumber());
		}

		return out;
	},

	/**
	 * Utility function to calculate the total cost of a bundle from its child items
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} bundle Parent item to focus on
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.taxExempt=false] DEPRECATED see ABI-2352
	 * @param {String} [options.instance] Instance name
	 * @param {Boolean} [options.singleCost=false] Cost for a single item
	 * @param {Boolean} [options.considerDiscount=false] Include discounts
	 * @param {Boolean} [options.considerDiscountTypes=['customer', 'coupons', 'line', 'override', 'sale']] Included discount types
	 * @param {Boolean} [options.useOriginalCost=false] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.lookupOverride=false] Ignore looking up products, already loaded (saves us from passing in products)
	 * @param {Boolean} [options.perLineRounding=true] Apply rounding per line
	 * @returns {Number|Big} High precision floating point representing the cost of the bundle
	 */
	bundleCost(order, children, bundle, products, options) {
		if (!_.isPlainObject(order)) throw new Error('bundleCost(): Invalid order object');
		if (!_.isPlainObject(bundle)) throw new Error('bundleCost(): Invalid bundle object');
		if (!_.isPlainObject(products)) throw new Error('bundleCost(): Invalid products lookup object');

		const settings = {
			taxExempt: false, // usage removed, as of ABI-2352: use to cause double tax
			instance: undefined,
			singleCost: false,
			considerDiscount: false, // Apply discounts to children
			considerDiscountTypes: ['customer', 'coupons', 'line', 'override', 'sale'],
			useOriginalCost: false, // to avoid "taxExempt" affected cost, use in preTaxSubTotal()
			lookupOverride: false, // to ignore looking up products, already loaded (saves us from passing in products)
			perLineRounding: true,
			...options,
		};

		const $$debug = $debug.extend('bundleCost');

		if (!children) children = this.getOrderItemChildren(order.items, bundle);

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(children), _.cloneDeep(bundle), _.cloneDeep(products), settings);

		// NOTE: Adding bundle additional charge line so that bundle cost is simply tally of all children
		const out = this.bundleChildrenCost(
			order,
			[this.createAdditionalChargeItem(order, children, bundle, products)].concat(children).filter(Boolean),
			bundle,
			products,
			settings
		);

		if ($$debug.enabled) {
			$$debug('bundle', _.cloneDeep(bundle));
			$$debug('product', bundle.product);
			$$debug('cost', bundle?.cost);
			$$debug('quantity', bundle?.quantity);
			$$debug('considerDiscount', settings.considerDiscount);
			$$debug('singleCost', settings.singleCost);
			$$debug('out', out.toNumber());
		}

		return out;
	},

	/**
	 * This callback type is called `requestCallback` and is displayed as a global symbol.
	 *
	 * @callback filterDiscountsWith
	 * @param {Object} item Item
	 * @param {Object} discount
	 * @returns {Boolean}
	 */

	/**
	 * Retrieve the per-item item discount given a discountType and quantity over which it would be applied
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Order item
	 * @param {Object} products Products API lookup
	 * @param {String} discountType Discount type
	 * @param {Number} [quantity=item.quantity] Quantity
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.useOriginalCost] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.useItemCost] Passes via full "itemCost" rather than only "itemCostField"
	 * @returns {Object}
	 */
	itemDiscountByType(order, children, item, products, discountType, quantity, options) {
		if (!_.isPlainObject(order)) throw new Error('itemDiscountbyType(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('itemDiscountByType(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('itemDiscountByType(): Invalid products lookup object');

		const settings = {
			useItemCost: false,
			...options,
			// Cost before any sales/overrides/discounts
			considerDiscount: false,
			useOverrideCost: true,
		};

		if (_.isNil(quantity)) quantity = item.quantity;

		const $$debug = $debug.extend('itemDiscountByType');

		if (!children) children = this.getOrderItemChildren(order.items, item);

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(children), _.cloneDeep(item), _.cloneDeep(products), settings);

		$$debug('quantity', quantity);

		// Cost before any sales/overrides/discounts
		const cost = settings.useItemCost
			? this.itemCost(order, children, item, products, settings)
			: this.itemCostField(item, settings);
		if($$debug.enabled)
			$$debug('cost', cost.toString());
		if (!this.hasItemDiscountType(item, discountType)) return this.splitPrice(cost, '0', quantity);

		const discount = settings.useOriginalCost
			? _.get(item.discounts, [discountType, 'originalDiscount'], '0')
			: _.get(item.discounts, [discountType, 'discount'], '0');
		$$debug('discount', discountType, discount);

		return this.splitPrice(cost, discount, quantity);
	},

	/**
	 * @typedef {Object} DiscountGreater
	 * @property {String} name - Name of winning discount
	 * @property {Object} discount - Resulting price/discount object
	 */

	/**
	 * Retrieve the greater of sales, override and line discounts
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Order item
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.considerDiscountTypes=['customer', 'coupons', 'line', 'override', 'sale']] Included discount types
	 * @param {Boolean} [options.useOriginalCost] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.useItemCost] Passes via full "itemCost" rather than only "itemCostField"
	 * @returns {DiscountGreater}
	 */
	itemDiscountGreater(order, children, item, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemDiscountGreater(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('itemDiscountGreater(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('itemDiscountGreater(): Invalid products lookup object');

		const settings = {
			considerDiscountTypes: ['customer', 'coupons', 'line', 'override', 'sale'],
			useOriginalCost: false,
			useItemCost: true,
			...options,
		};

		const $$debug = $debug.extend('itemDiscountGreater');

		if (!children) children = this.getOrderItemChildren(order.items, item);

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(children), _.cloneDeep(item), _.cloneDeep(products), settings);

		// Return non-priced items without any defaulting; Avoiding flagging them as "sale" by default
		// NOTE: Won't return discount numbers for a bundle. It can only show the discount for the "Additional charge" line
		if (!this.hasItemPrice(item, products)) {
			$$debug('hasItemPrice false');
			return {
				name: '',
				discount: {
					price: new Big(0),
					discount: new Big(0),
					discountPercent: new Big(0),
				},
			};
		}

		const cost = settings.useItemCost
			? this.itemCost(order, children, item, products, {
				...settings,
				considerDiscount: false,
			})
			: this.itemCostField(item, settings);


		const customerDiscounts = this.getMaxDiscountCustomerDiscounts(order, children, item, products, cost);
		if (!_.isNil(customerDiscounts))
			$$debug('customerDiscounts', _.cloneDeep(customerDiscounts));

		const defaultDiscount = _.cloneDeep(this.splitPrice(cost, '0', item.quantity));
		if($$debug.enabled)
			$$debug(
				'defaultDiscount',
				_.cloneDeep(item),
				_.mapValues(defaultDiscount, (d) => d.toString()),
			);

		const customerStandard = this.isDiscount(customerDiscounts?.standard)
			? (this.isItemBundleChild(item, products))
				? this.splitPrice(cost, this.getBundleChildDiscountShare(
					order,
					null,
					order.items.find(d => d.ref === item.parentRef),
					item,
					products,
					customerDiscounts.standard
				), item.quantity)
				: this.splitPrice(cost, customerDiscounts.standard, item.quantity)
			: defaultDiscount;
		customerStandard.discount = _.get(customerStandard, 'discount', new Big(0));

		if($$debug.enabled)
			$$debug(
				'customerStandard',
				_.cloneDeep(item),
				_.mapValues(customerStandard, (d) => d.toString()),
			);

		const customerSale = this.isDiscount(customerDiscounts?.sales)
			? (this.isItemBundleChild(item, products))
				? this.splitPrice(cost, this.getBundleChildDiscountShare(
					order,
					null,
					order.items.find(d => d.ref === item.parentRef),
					item,
					products,
					customerDiscounts.sales
				), item.quantity)
				: this.splitPrice(cost, customerDiscounts.sales, item.quantity)
			: defaultDiscount;
		customerSale.discount = _.get(customerSale, 'discount', new Big(0));

		if($$debug.enabled)
			$$debug(
				'customerSale',
				_.cloneDeep(item),
				_.mapValues(customerSale, (d) => d.toString()),
			);

		// TODO: Bundle parents with addition charge which are "excludeDiscount" should apply the customer excluded discount only to the additional charge?
		const customerExcluded = this.isDiscount(customerDiscounts?.excluded)
			? (this.isItemBundleChild(item, products))
				? this.splitPrice(cost, this.getBundleChildDiscountShare(
					order,
					null,
					order.items.find(d => d.ref === item.parentRef),
					item,
					products,
					customerDiscounts.excluded
				), item.quantity)
				: this.splitPrice(cost, customerDiscounts.excluded, item.quantity)
			: defaultDiscount;
		customerExcluded.discount = _.get(customerExcluded, 'discount', new Big(0));

		if($$debug.enabled)
			$$debug(
				'customerExcluded',
				_.cloneDeep(item),
				_.mapValues(customerExcluded, (d) => d.toString()),
			);
		// }}}

		// 1) Tally sale discounts {{{
		if($$debug.enabled)
			$$debug('considerDiscountTypes.includes.sale', settings.considerDiscountTypes.includes('sale'));
		const tallySale = settings.considerDiscountTypes.includes('sale')
			? this.itemDiscountByType(order, children, item, products, 'sale', 1, settings)
			: defaultDiscount;
		tallySale.discount = _.get(tallySale, 'discount', new Big(0));
		if($$debug.enabled)
			$$debug(
				'tallySale',
				_.cloneDeep(item),
				_.mapValues(tallySale, (d) => d.toString()),
			);
		// }}}

		// Determine which is greater between sale discount and customer's sale discount {{{
		// NOTE: This customer discount is always checked when "considerDiscountTypes: ['sale']" discounts are requested regardless of "considerDiscountTypes: ['customer']" being requested or not
		// Customer Sales discount dont apply for excluded items
		// TODO: test for the customer sales excluded portion
		const saleDiscountGreater = settings.considerDiscountTypes.includes('sale')
			? (
				tallySale.discount.gt(0)
				&& _.isPlainObject(customerSale)
				&& customerSale.discount.gt(tallySale.discount)
				&& !this.isItemExcludedDiscount(item)
			)
				? customerSale
				: tallySale
			: defaultDiscount;
		if($$debug.enabled)
			$$debug(
				'saleDiscountGreater',
				_.cloneDeep(item),
				_.mapValues(saleDiscountGreater, (d) => d.toString()),
			);
		// }}}

		// 2) Tally override discounts {{{
		const tallyOverride = settings.considerDiscountTypes.includes('override')
			? this.itemDiscountByType(order, children, item, products, 'override', 1, settings)
			: defaultDiscount;
		tallyOverride.discount = _.get(tallyOverride, 'discount', new Big(0));
		if($$debug.enabled)
			$$debug(
				'tallyOverride',
				_.cloneDeep(item),
				_.mapValues(tallyOverride, (d) => d.toString()),
			);
		// }}}

		// 3) Tally item discounts
		const tallyLine = settings.considerDiscountTypes.includes('line')
			? this.itemDiscountByType(order, children, item, products, 'line', item.quantity, settings)
			: defaultDiscount;
		tallyLine.discount = _.get(tallyLine, 'discount', new Big(0));
		if($$debug.enabled)
			$$debug(
				'tallyLine',
				_.cloneDeep(item),
				_.mapValues(tallyLine, (d) => d.toString()),
			);

		// 4) Tally percentage coupon discounts (include all percentage coupons)
		// NOTE: "itemDiscountByType" specialises in the simplier discount types. Coupons is an array.
		const tallyPercentCoupon = _.cloneDeep(defaultDiscount);
		if (settings.considerDiscountTypes.includes('coupons')) {
			const tallyPercentCouponDiscount = ((_.isArray(item.discounts.coupons))
				? item.discounts.coupons
				: [])
					.filter(d => d.discountType === 'percent') // NOTE: Fixed dollar coupons stack in "itemDiscount()"
					.map(d => new Big(d.discount.replace(/[^0-9\.\-]/g, '')))
					.reduce((t, v) => t.plus(v), new Big(0));

			$$debug('tallyPercentCouponDiscount', tallyPercentCouponDiscount.toString());

			// NOTE: Coupons store their discounts as line totals but we need to compare to single item RRPs here
			tallyPercentCoupon.discount = tallyPercentCouponDiscount.div(item.quantity);
			tallyPercentCoupon.discountPercent = new Big(tallyPercentCoupon.discount).div(cost).times(100);
			tallyPercentCoupon.price = new Big(cost).minus(tallyPercentCoupon.discount);
		}
		if($$debug.enabled)
			$$debug(
				'tallyPercentageCoupon',
				_.cloneDeep(item),
				_.mapValues(tallyPercentCoupon, (d) => d.toString()),
			);

		// 5) Determine which discount should apply {{{
		// NOTE: Given we're returning the per-item price, the per-item or per-line manner in which discounts apply is handled by the above calls to "itemDiscountByType" and subsequently "splitPrice"

		// Excluded discount applies when it is greater than any other and item is excluded
		if($$debug.enabled) {
			$$debug('considerDiscountTypes.includes.customer', settings.considerDiscountTypes.includes('customer'));
			$$debug('isItemExcludedDiscount', this.isItemExcludedDiscount(item));
		}
		if (_.isPlainObject(customerExcluded)) {
			if($$debug.enabled) {
				$$debug('greaterThan.tallyLine', customerExcluded.discount.toString(), tallyLine.discount.toString(), customerExcluded.discount.gt(tallyLine.discount));
				$$debug('greaterThan.tallyOverride', customerExcluded.discount.toString(), tallyOverride.discount.toString(), customerExcluded.discount.gt(tallyOverride.discount));
			}
			if (_.isPlainObject(saleDiscountGreater)) {
				if($$debug.enabled)
					$$debug('greaterThan.saleDiscountGreater', customerExcluded.discount.toString(), saleDiscountGreater.discount.toString(), customerExcluded.discount.gt(saleDiscountGreater.discount));
			}
		}

		if (
			settings.considerDiscountTypes.includes('customer')
			// Either the item, it's parent, or any of it's children are excluded
			&& (
				this.isItemExcludedDiscount(item)
				// NOTE: https://freedcamp.com/view/2713393/issuetracker/280948#comment-26513105
				// || this.isItemBundleParentExcludedDiscount(order, item)
				//|| this.isItemBundleChildExcludedDiscount(order, children, item)
			)
			&& _.isPlainObject(customerExcluded)
			&& customerExcluded.discount.gt(tallyLine.discount)
			&& customerExcluded.discount.gt(tallyOverride.discount)
			&& customerExcluded.discount.gt(tallyPercentCoupon.discount)
			// When item is on sale excluded discount must also be greater than it
			// NOTE: 1. Items marked as on sale and excluded are affected by both sales discount and excluded discount. Only excluded discount should be applicable to these items. See [vii]
			&& (
				_.isPlainObject(saleDiscountGreater)
				//(this.hasItemDiscountType(item, 'sale') || this.hasBundleChildDiscountType(order, children, item, 'sale'))
				&& customerExcluded.discount.gt(saleDiscountGreater.discount)
			)
		) {
			if($$debug.enabled)
				$$debug(
					'customerExcluded.wins',
					_.cloneDeep(item),
					_.mapValues(customerExcluded, (d) => d.toString()),
				);
			return {
				name: 'customer',
				discount: customerExcluded,
			};

			// Standard discount applies when it is greater than any other
		} else if (
			settings.considerDiscountTypes.includes('customer')
			&& _.isPlainObject(customerStandard)
			&& customerStandard.discount.gt(tallyLine.discount)
			&& customerStandard.discount.gt(tallyOverride.discount)
			&& customerStandard.discount.gt(tallyPercentCoupon.discount)
			// A 'standard' standalone product or bundle child is a product that is neither an excluded or sale product (as defined above in: [iii] and [iv]) {{{
			&& !this.isItemExcludedDiscount(item)
			// NOTE: https://freedcamp.com/view/2713393/issuetracker/280948#comment-26513105
			// && !this.isItemBundleParentExcludedDiscount(order, item)
			&& !this.hasItemDiscountType(item, 'sale')
			&& !this.hasBundleChildDiscountType(order, children, item, 'sale')
			// }}}
		) {
			if($$debug.enabled)
				$$debug(
					'customerStandard.wins',
					_.cloneDeep(item),
					_.mapValues(customerStandard, (d) => d.toString()),
				);
			return {
				name: 'customer',
				discount: customerStandard,
			};

			// Line discount applies when greater than sale and override
		} else if (
			_.isPlainObject(tallyLine)
			&& tallyLine.discount.gt(tallyOverride.discount)
			&& tallyLine.discount.gt(saleDiscountGreater.discount)
			&& tallyLine.discount.gt(tallyPercentCoupon.discount)
		) {
			if($$debug.enabled)
				$$debug(
					'tallyLine.wins',
					_.cloneDeep(item),
					_.mapValues(tallyLine, (d) => d.toString()),
				);
			return {
				name: 'line',
				discount: tallyLine,
			};

			// Override discount applies when greater than sale, line and percentage coupon
		} else if (
			_.isPlainObject(tallyOverride)
			&& tallyOverride.discount.gt(saleDiscountGreater.discount)
			&& tallyOverride.discount.gt(tallyLine.discount)
			&& tallyOverride.discount.gt(tallyPercentCoupon.discount)
		) {
			if($$debug.enabled)
				$$debug(
					'tallyOverride.wins',
					_.cloneDeep(item),
					_.mapValues(tallyOverride, (d) => d.toString()),
				);
			// TODO: ABI-2665_3of13: Should override also count as "sale" for the purposes of applying customer "sales" discount
			return {
				name: 'override',
				discount: tallyOverride,
			};

			// Percentage coupon discount applies when greater than sale, override and line
		} else if (_.isPlainObject(tallyPercentCoupon)
			&& tallyPercentCoupon.discount.gt(tallyOverride.discount)
			&& tallyPercentCoupon.discount.gt(tallyLine.discount)
			&& tallyPercentCoupon.discount.gt(saleDiscountGreater.discount)
		) {
			if($$debug.enabled)
				$$debug(
					'percentageCouponDiscountGreater.wins',
					_.cloneDeep(item),
					_.mapValues(tallyPercentCoupon, (d) => d.toString()),
				);
			return {
				name: 'coupons',
				discount: tallyPercentCoupon,
			};

			// Sale discount applies when greater than override and line
		} else if (_.isPlainObject(saleDiscountGreater)
			// && saleDiscountGreater.discount.gt(tallyOverride.discount)
			// && saleDiscountGreater.discount.gt(tallyLine.discount)
		) {
			if($$debug.enabled)
				$$debug(
					'saleDiscountGreater.wins',
					_.cloneDeep(item),
					_.mapValues(saleDiscountGreater, (d) => d.toString()),
				);
			return {
				name: 'sale',
				discount: saleDiscountGreater,
			};

			// Otherwise no discount applies
		} else {
			if($$debug.enabled)
				$$debug('ELSE.wins', _.cloneDeep(item));
			return {
				name: 'sale', // Default to sale when no other applies as it will always be the fallback state
				discount: defaultDiscount,
			};
		}
		// }}}
	},

	/**
	 * Retrieve the combined total discount
	 *
	 * @param {Object} order Order Current order
	 * @param {Array} [children] Precalculated child list
	 * @param {Object} item Order item
	 * @param {Object} products Products API lookup
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.considerDiscount=false] Include discounts
	 * @param {Boolean} [options.considerDiscountTypes=['customer', 'coupons', 'line', 'override', 'sale']] Included discount types
	 * @param {Boolean} [options.useOriginalCost] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.useItemCost] Passes via full "itemCost" rather than only "itemCostField"
	 * @param {Boolean} [options.perLineRounding=true] Apply rounding per line
	 * @returns {String}
	 */
	itemDiscount(order, children, item, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemDiscount(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('itemDiscount():Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('itemDiscount():Invalid products lookup object');

		const settings = {
			considerDiscount: false,
			considerDiscountTypes: ['customer', 'coupons', 'line', 'override', 'sale'],
			useOriginalCost: false,
			useItemCost: false,
			perLineRounding: true,
			...options,
		};

		// TODO: Validate "products" has already loaded requested "item" here?

		const $$debug = $debug.extend('itemDiscount');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(item), _.cloneDeep(products), settings);

		if (!children) children = this.getOrderItemChildren(order.items, item);

		// When child has no discounts of it's own, apply discounts from parent instead
		// TODO: ABI-2665: Why? Causes tests to fail... Perhaps it's percentage coupons only? ... Something to do with "pricedIndivudally" bundle?
		// TODO: ABI-2665: Handle assignment of discounts to children in "setItemDiscount"; Logic has been commented out.
		// NOTE: Required for bundle with percentage discount where the discount is not split between children already
		//if (item.parentRef && item.pricedIndividually && item.discounts.length === 0) { // Children only
		//	const parent = order.items.find(i => (i.ref === item.parentRef));
		//	if (parent.pricedIndividually) discounts = parent.discounts;
		//}

		//$debug('itemDiscount.itemDiscounts', discounts);

		const couponDiscounts =
			settings.considerDiscountTypes.includes('coupons')
				&& this.hasItemDiscountType(item, 'coupons')
				&& _.isArray(item.discounts.coupons)
				? item.discounts.coupons
					.filter(d => d.discountType === 'fixed_cart') // NOTE: Percentage coupons win or lose in "itemDiscountGreater()"
					.map(d => (settings.useOriginalCost ? d.originalDiscount : d.discount))
				: [];
		$$debug('couponDiscounts', couponDiscounts);

		// Tally sale/override/item and percentage coupon discounts
		const totalDiscounted = this.itemDiscountGreater(order, children, item, products, settings).discount;
		if($$debug.enabled)
			$$debug(
				'totalDiscounted',
				_.mapValues(totalDiscounted, (d) => d.toString()),
			);

		// Apply fixed coupon discounts
		const tallyCoupon = this.splitPrice(totalDiscounted.price, couponDiscounts, item.quantity);

		const tally = tallyCoupon;
		if($$debug.enabled)
			$$debug('tally', tally.price.toString(), tally.discount.toString());

		// NOTE: We wish to display total discounts next to bundle header items, while calculating based on purely "cost" where such header items may have none.
		const cost = settings.useItemCost
			? this.itemCost(order, children, item, products, settings)
			: this.itemCostField(item, settings);
		if($$debug.enabled)
			$$debug('cost', cost.toString());

		const discount = new Big(cost).minus(tally.price); // Discount is difference between original and new price
		if($$debug.enabled)
			$$debug('discount', discount.toString());

		// FIXME: Round discount after quantity is applied? This is per-line after-all.
		return '$' + discount.times(item.quantity).toString();
	},

	/**
	 * Gets appropriate cost field
	 *
	 * @param {Object} item Item
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.useOriginalCost=false] Use "originalCost" before "taxExempt" is applied
	 * @param {Boolean} [options.useCostOfGoods=false] Use "costOfGoods" field rather than "cost" field
	 * @returns {Number|Big}
	 */
	itemCostField(item, options) {
		const settings = {
			useOriginalCost: false,
			useCostOfGoods: false,
			...options,
		};
		//$debug('itemCostField', item, settings);

		// When a bundle child lacks "pricedIndividually" it's "cost" field should be ignored.
		if (this.isItemBundleChild(item) && !item.pricedIndividually) {
			return new Big(0);
		}

		if (settings.useOriginalCost) {
			return new Big(+_.get(item, 'originalCost', 0));
		} else if (settings.useCostOfGoods) {
			return new Big(+_.get(item, 'costOfGoods', 0));
		} else {
			return new Big(+_.get(item, 'cost', 0));
		}
	},

	/**
	 * Extract cost from "simple" or "bundle" item and apply overrides
	 *
	 * @param {Object} order Order
	 * @param {Array} [children] Precalculated child list
	 * @param {Object} item Item
	 * @param {Object<Product>} products Product lookup object
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.considerDiscount=false] Include discounts
	 * @returns {Big}
	 */
	itemCost(order, children, item, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemCost(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('itemCost(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('itemCost(): Invalid products lookup object');

		const settings = {
			considerDiscount: false,
			...options,
		};

		const $$debug = $debug.extend('itemCost');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(item), _.cloneDeep(products), settings);

		if (!children) children = this.getOrderItemChildren(order.items, item);

		if (
			!_.isNil(item?.product)
			&& _.has(products, item.product)
			&& !_.isNil(item?.productType)
			&& !_.isNil(products[item.product]?.productType)
			&& item?.productType !== products[item.product]?.productType
		)
			$$debug(
				'productType mismatch',
				item?.product,
				item?.productType,
				products[item.product]?.productType,
			);

		if (products[item.product]?.productType === 'bundle') {
			const bundleCost = this.bundleCost(order, children, item, products, {
				//taxExempt: order.taxExempt, // Deprecated
				instance: order.instance,
				//singleCost: true,
				...settings,
			}).div(item.quantity);

			return bundleCost
		} else {
			if (settings.considerDiscount)
				if($$debug.enabled)
					$$debug('discount', this.itemDiscount(order, children, item, products, settings));

			const price = this.splitPrice(
				this.itemCostField(item, settings),
				(settings.considerDiscount) ? this.itemDiscount(order, children, item, products, settings) : '0',
				item.quantity,
			).price;

			$$debug('out', price.toString());
			return price;
		}
	},

	/**
	 * Calculate the sum of order items
	 *
	 * @param {Object} order Order
	 * @param {Object<Product>} products Product lookup object
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.considerDiscount=false] Include discounts
	 * @param {Boolean} [options.perLineRounding=true] Apply rounding per line
	 * @param {Function} [options.filter] Function to filter items
	 * @returns {Number|Big}
	 */
	itemTotal(order, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemTotal(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('itemTotal(): Invalid products lookup object');

		const settings = {
			considerDiscount: false,
			// TODO: Rename this to "applyRounding" like the other one?
			perLineRounding: true,
			// TODO: What if we avoid the bundles instead and only process children after adding "Additional charge" line?
			filter: item => (!item.parentRef), // No bundle children
			...options,
		};

		const $$debug = $debug.extend('itemTotal');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(products), settings);

		return _.chain(order.items)
			.filter((item) => item.itemType == 'product')
			.filter(settings.filter)
			.map((item) => {
				let price = this.itemCost(order, null, item, products, settings).times(item.quantity);
				if($$debug.enabled) $$debug('line', _.cloneDeep(item), item.quantity, price.toString());

				// NOTE: ABI-3117 per line rounding https://freedcamp.com/view/2713393/issuetracker/341094#comment-27538254
				if(settings.perLineRounding) {
					price = price.round(2);
					if($$debug.enabled) $$debug('rounded', price.toString());
				}

				return price;
			})
			.reduce((t, v) => t.plus(v), new Big(0))
			.value();
	},

	// WARN: ONLY use this for DEBUGGING
	convertToRealNumbers(obj) {
		for (let key in obj) {
			if (obj[key] instanceof Big) {
				// Convert from Big.js number to native JavaScript number
				obj[key] = obj[key].toNumber();
			}
			if (typeof obj[key] === 'object') {
				obj[key] = this.convertToRealNumbers(obj[key]);
			}
		}
		return obj;
	},

	// TODO: OPTO: Run "itemTotals" only within items loop in an item component, thus only executing this code based on changes tracked by ":key"

	/**
	 * Calculates tax and totals given a line item
	 *
	 * @param {Object} order Order
	 * @param {Array} children Precalculated child list
	 * @param {Object} item Item
	 * @param {Object<Product>} products Product lookup object
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.applyRounding=true] Apply rounding or retain all significant figures
	 * @returns {Object}
	 */
	itemTotals(order, children, item, products, options) {
		if (!_.isPlainObject(order)) throw new Error('itemTotals(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('itemTotals(): Invalid item object');
		if (!_.isPlainObject(products)) throw new Error('itemTotals(): Invalid products lookup object');

		const settings = {
			applyRounding: true,
			...options,
		};

		const $$debug = $debug.extend('itemTotals');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(item), products[item.product]?.productType);

		try {
			return this._itemTotalsImpl(order, children, item, products, settings, $$debug);
		} catch (err) {
			throw err;
		}
	},

	_itemTotalsImpl(order, children, item, products, settings, $$debug) {

		if (!children) children = this.getOrderItemChildren(order.items, item);

		const totals = {
			discount: '',
			item: {
				discount: 0,
				cost: {
					before: 0,
					after: 0,
				},
				//costExTax: {
				//	before: 0,
				//	after: 0,
				//},
				tax: {
					before: 0,
					after: 0,
				},
			},
			line: {
				discount: 0,
				subTotal: {
					before: 0,
					after: 0,
				},
				subTotalExTax: {
					before: 0,
					after: 0,
				},
				tax: {
					before: 0,
					after: 0,
				},
				returned: 0,
			},
		};

		// Only process "product" items; Otherwise return valid input in-case a template needs it.
		if (item.itemType === 'product') {
			const greatestDiscount = this.itemDiscountGreater(order, children, item, products).name;
			if($$debug.enabled)
				$$debug('greatestDiscount', greatestDiscount);

			const costValues = {
				itemCostSingle: this.itemCost(order, children, item, products, {
					considerDiscount: false,
				}), // Before

				itemCostSingleRrp: null,
				itemCostSingleDiscounted: null, // After all discounts

				itemCostAll: null, // Before
				itemCostAllDiscounted: null, // After

				itemCostReturned: null, // Returned
			};

			// When order has been committed include the saved totals
			if (!settings.forceCalculation && order.isLocked) {
				_.merge(totals, item.totals);
			}

			costValues.itemCostReturned = costValues.itemCostSingle.times(_.get(item, 'quantityReturned', 0));
			if (costValues.itemCostReturned.gt(0)) {
				totals.line.returned = settings.applyRounding
					? parseFloat(costValues.itemCostReturned.toFixed(2))
					: costValues.itemCostReturned;
			}

			// Locked orders: item.totals from schema has no discountPercent. Derive from discount and before/after for display.
			if (!settings.forceCalculation && order.isLocked) {
				const divisor = (before, after, discount) => {
					const b = Number(before) || 0;
					return b > 0 ? b : (Number(after) || 0) + Number(discount);
				};
				const sub = totals.line.subTotal || {};
				const lineDivisor = divisor(sub.before, sub.after, totals.line.discount);
				if (totals.line.discount > 0 && lineDivisor > 0) {
					totals.line.discountPercent = new Big(totals.line.discount).div(lineDivisor).times(100).toNumber();
				}
				const cost = totals.item.cost || {};
				const itemDivisor = divisor(cost.before, cost.after, totals.item.discount);
				if (totals.item.discount > 0 && itemDivisor > 0) {
					totals.item.discountPercent = new Big(totals.item.discount).div(itemDivisor).times(100).toNumber();
				}
			}


			// When order has not been committed continue to calculate fields other than "returned"
			if (settings.forceCalculation || !order.isLocked) {
				// NOTE: When sale and override are not winning the displayed price should still be retained for sale and override, badges indicate winning status
				// When any child sale items exist the before/after reflects them, when none exist it reverts to same price
				costValues.itemCostSingleRrp = this.itemCost(order, children, item, products, {
					considerDiscount: true,
					considerDiscountTypes: ['sale', 'override'],
				});
				costValues.itemCostSingleDiscounted = this.itemCost(order, children, item, products, {
					considerDiscount: true,
				}); // After all discounts

				costValues.itemCostAll = costValues.itemCostSingle.times(item.quantity);
				costValues.itemCostAllDiscounted = costValues.itemCostSingleDiscounted.times(item.quantity);

				if($$debug.enabled)
					$$debug(
						'costValues',
						_.mapValues(costValues, (d) => d.toString()),
					);

				// Bundle children which are not "pricedIndividually" do not participate
				if (item.parentRef && !item.pricedIndividually) {
					totals.item.cost.before = totals.item.cost.after = 0;
					totals.line.subTotal.before = totals.line.subTotal.after = 0;
				} else {
					totals.line.subTotal.before = settings.applyRounding
						? parseFloat(costValues.itemCostAll.toFixed(2))
						: costValues.itemCostAll;
					totals.line.subTotal.after = settings.applyRounding
						? parseFloat(costValues.itemCostAllDiscounted.toFixed(2))
						: costValues.itemCostAllDiscounted;
					totals.item.cost.before = settings.applyRounding
						? parseFloat(costValues.itemCostSingle.toFixed(2))
						: costValues.itemCostSingle;
					// NOTE: ABI-3224: We may not wish to display a crossed out RRP unless sale or override wins. i.e. When a percentage coupon wins.
					if (greatestDiscount === 'override' || greatestDiscount === 'sale') {
						totals.item.cost.after = settings.applyRounding
							? parseFloat(costValues.itemCostSingleRrp.toFixed(2))
							: costValues.itemCostSingleRrp;
					} else {
						totals.item.cost.after = totals.item.cost.before;
					}
				}

				if (!order.taxExempt && (!item.parentRef || item.pricedIndividually)) {
					totals.line.tax.before = settings.applyRounding
						? parseFloat(this.calcTax(order.instance, totals.line.subTotal.before).toFixed(2))
						: this.calcTax(order.instance, totals.line.subTotal.before);
					totals.line.tax.after = settings.applyRounding
						? parseFloat(this.calcTax(order.instance, totals.line.subTotal.after).toFixed(2))
						: this.calcTax(order.instance, totals.line.subTotal.after);
				}

				//totals.discount = costValues.itemDiscountString; // Discount string, not discount amount

				// NOTE: Discount includes value of any price override

				// Discount dollar value
				// FIXME: Keep as BigJS unless "applyRounding" is specified
				if (totals.line.subTotal.before !== totals.line.subTotal.after)
					totals.line.discount = totals.line.subTotal.before - totals.line.subTotal.after;
				if (totals.item.cost.before !== totals.item.cost.after)
					totals.item.discount = totals.item.cost.before - totals.item.cost.after;

				// Discount percentage value
				// TODO: Use unrounded value for percentage?
				if (totals.line.discount > 0)
					totals.line.discountPercent = new Big(totals.line.discount)
						.div(totals.line.subTotal.before)
						.times(100)
						.toNumber();
				if (totals.item.discount > 0)
					totals.item.discountPercent = new Big(totals.item.discount).div(totals.item.cost.before).times(100).toNumber();

				// FIXME: Keep as BigJS unless "applyRounding" is specified
				if (totals.line.subTotal.before > 0)
					totals.line.subTotalExTax.before = totals.line.subTotal.before - totals.line.tax.before;
				if (totals.line.subTotal.after > 0)
					totals.line.subTotalExTax.after = totals.line.subTotal.after - totals.line.tax.after;

				//if (totals.item.cost.before !== totals.item.cost.before) totals.item.costExTax.before = totals.item.cost.before - totals.item.tax.before;
				//if (totals.item.cost.after !== totals.item.cost.after) totals.item.costExTax.after = totals.item.cost.after - totals.item.tax.after;
			}
		}

		// Always tack on "rrp" and "highlights"
		totals.rrp = totals.item.cost; // Alias

		totals.highlights = {
			rrp: {
				before: false,
				after: totals.rrp.after === 0,
			},
			subTotal: {
				before: false,
				after: totals.line.subTotal.after === 0,
			},
			subTotalExTax: {
				before: false,
				after: totals.line.subTotalExTax.after === 0,
			},
			tax: {
				before: false,
				after: totals.line.tax.after === 0,
			},
			returned: totals.line.returned !== 0,
		};

		$$debug('totals', totals);
		return totals;
	},

	// TODO: ABI-2665_1of13: Deprecated? Value of "order.taxExempt" being set directly...
	/**
	 * Mutate shipping costs depending on application of tax
	 *
	 * @param {Object} order Order The order to be updated
	 * @param {String} action Type of tax implementation ENUM['on','off','toggle']
	 */
	toggleTax(order, action) {
		if (!_.isPlainObject(order)) throw new Error('toggleTax(): Invalid order object');

		const $$debug = $debug.extend('toggleTax');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), action);

		// "Tax is 'off'" => "taxExempt is true"
		let taxExempt = false;
		switch (action) {
			case 'on':
				taxExempt = false;
				break;
			case 'off':
				taxExempt = true;
				break;
			case 'toggle':
			default:
				taxExempt = !order.taxExempt;
		}
		this.applyTaxExemptionOrder(order, taxExempt);
	},

	/**
	 * @typedef {Object} OrderTotals
	 * @property {Number} discount Discount tally
	 * @property {Number} returned Returned tally
	 * @property {Number} grandTotal Grand total including shipping
	 * @property {Number} grandTotalTax Tax applied to grand total including shipping
	 * @property {Number} grandTotalExTax Grand total including shipping with tax removed after calculations
	 * @property {Number} grandTotalOwing Grand total including shipping with total paid removed
	 * @property {Number} grandTotalPaid Grand total of payments
	 * @property {Number} subTotal Total for items before shipping
	 * @property {Number} subTotalTax Tax applied to total for items before shipping
	 * @property {Number} subTotalExTax Total for items before shipping with tax removed after calculations
	 * @property {Number} shipping Total shipping
	 * @property {Number} shippingTax Tax applied to total shipping
	 * @property {Number} shippingDiscounted Shipping with any shipping discounts applied
	 * @property {Number} shippingExTax Total shipping with any shipping discounts applied and tax removed after calculation
	 */

	/**
	 * Saves the final totals for discounts, tax and shipping
	 *
	 * @param {Object} order Order The order to be tallied
	 * @param {Object} products Products API lookup A list of products to lookup bundle status
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.forceCalculation=false] Force re-calculation rather than using existing values when locked
	 * @param {Boolean} [options.updateOrder=true] Set "totals" for the order object
	 * @param {Boolean} [options.updateItems=false] Set "totals" for the item objects within order
	 *
	 * @returns {OrderTotals}
	 */
	applyOrderTotals(order, products, options) {
		// if (!_.isPlainObject(order)) throw new Error('applyOrderTotals(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('applyOrderTotals(): Invalid products lookup object');

		const settings = {
			forceCalculation: false,
			updateOrder: true,
			updateItems: false,
			...options,
		};

		const $$debug = $debug.extend('applyOrderTotals');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(products), settings);

		const plainOrder = (!_.isPlainObject(order)) ? order.toObject() : order;
		const orderTotals = this.orderTotals(plainOrder, products, settings);

		if (settings.updateOrder) {
			if (settings.forceCalculation || !order.isLocked) {
				$$debug('Updating all order calculations');
				order.totals = orderTotals;

				// DEPRECATED: {{{
				// Unsupported: totalShipping
				// Unsupported: grandTotalExTax
				order.totalDiscount = orderTotals.discount;
				order.subTotal = orderTotals.subTotal;
				order.totalAmount = orderTotals.grandTotal;
				order.totalTax = orderTotals.grandTotalTax;
				// }}}
			} else {
				$$debug('Updating some order calculations');
				// Update items which are subject to change
				order.totals.grandTotalPaid = orderTotals.grandTotalPaid;
				order.totals.grandTotalOwing = orderTotals.grandTotalOwing;
				order.totals.returned = orderTotals.returned;
			}
		}

		// NOTE: Updating items on the client side would cause watchers to re-trigger and thus this part is only included when server-side
		if (settings.updateItems) {
			order.items.forEach(item => {
				const plainOrderItem = (!_.isPlainObject(item)) ? item.toObject() : item;
				const itemTotals = $benchmark.time('orderUtils.applyOrderTotals.itemTotals', () => this.itemTotals(plainOrder, null, plainOrderItem, products, settings));
				if (settings.forceCalculation || !order.isLocked) {
					$$debug('Updating all item calculations');
					item.totals = itemTotals;
					// NOTE: ABI-3135: Reusing deprecated "discount" field to include the total discount; We would prefer "totals.line.discount" is used instead of giving this field new meaning and potentially making automatic migration fragile.
					// FIXED: Causes issues with migration and was generally a bad idea; Removed.
					// item.discount = itemTotals.line.discount.toString();
				} else {
					$$debug('Updating some item calculations');
					// Update items which are subject to change
					item.totals.returned = itemTotals.returned;
				}
			});
		}

		return orderTotals;
	},


	/**
	 * Calculate the final totals for discounts, tax and shipping
	 *
	 * @param {Object} order Order The order to be tallied
	 * @param {Object} products Products API lookup A list of products to lookup bundle status
	 * @param {Object} [options] Options
	 * @param {Boolean} [options.forceCalculation=false] Force re-calculation rather than using existing values when locked
	 *
	 * @returns {OrderTotals}
	 */
	orderTotals(order, products, options) {
		if (!_.isPlainObject(order)) throw new Error('orderTotals(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('orderTotals(): Invalid products lookup object');

		const settings = {
			forceCalculation: false,
			...options,
		};

		const $$debug = $debug.extend('orderTotals');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(products));

		// NOTE: Always performing calculations regardless of "forceCalculation" so that "returned" is always from latest data
		const out = order.items
			.filter((item) => item.itemType === 'product')
			.filter((item) => !item.parentRef) // No children
			.map((item) => {
				return $benchmark.time('orderUtils.orderTotals.itemTotals', () => this.itemTotals(order, null, item, products, {
					applyRounding: true, // NOTE: ABI-3117: Woo applies rounding to each product when calculating totals. We believed otherwise for what reason?
				}));
			})
			// TODO: 1 liner to reduce values; or perhaps reduce each one by one... Function to reduce a list of paths given 2 objects?
			.reduce(
				(acc, cur) => ({
					returned: acc.returned.plus(cur.line.returned),

					discount: acc.discount.plus(cur.line.discount),

					// "subTotal" is "before" any discounts are applied
					subTotal: acc.subTotal.plus(cur.line.subTotal.before),
					subTotalTax: acc.subTotalTax.plus(cur.line.tax.before),
					subTotalExTax: acc.subTotalExTax.plus(cur.line.subTotalExTax.before),

					// "grandTotal" is "after" any discounts are applied
					grandTotal: acc.grandTotal.plus(cur.line.subTotal.after),
					// "grandTotalTax" and "grandTotalExTax" are calculated after shipping is applied
				}),
				{
					discount: new Big(0),
					returned: new Big(0),
					grandTotal: new Big(0),
					grandTotalTax: new Big(0),
					grandTotalExTax: new Big(0),
					grandTotalOwing: new Big(0),
					subTotal: new Big(0),
					subTotalTax: new Big(0),
					subTotalExTax: new Big(0),
					shipping: new Big(0),
					shippingTax: new Big(0),
					shippingDiscounted: new Big(0),
					shippingExTax: new Big(0),
				},
			);

		// FIXME: Should tax be calculated after discount is applied? Do you still pay tax on shipping which has been set to "free" by a coupon?
		out.shipping = new Big(_.get(order, 'totalShipping', 0));
		out.shippingDiscounted = out.shipping.minus(_.get(order, 'shippingDiscount', 0));
		if (out.shippingDiscounted.lt(0)) out.shippingDiscounted = new Big(0); // Guard against stale shippingDiscount exceeding current shipping
		out.shippingTax = orderUtils.calcTax(order.instance, out.shippingDiscounted);
		out.shippingExTax = out.shippingDiscounted.minus(out.shippingTax);

		if (!settings.forceCalculation && order.isLocked) {
			// DEPRECATED {{{
			const legacy = {};
			legacy.discount = new Big(_.get(order, 'totalDiscount', 0));
			legacy.grandTotal = new Big(_.get(order, 'totalAmount', 0));
			legacy.grandTotalTax = new Big(_.get(order, 'totalTax', 0));
			legacy.subTotal = new Big(_.get(order, 'subTotal', 0));
			// }}}

			// Use "legacy" where it contains a value but "totals" does not
			Object.entries(legacy).forEach(([key,val]) => {
				if (val > 0 && (!_.has(order.totals, key) || order.totals[key] === 0)) {
					out[key] = val;
				} else if (_.isFinite(order.totals[key])) {
					out[key] = new Big(order.totals[key]);
				} else {
					out[key] = val; // Fall back to legacy when totals value is missing or invalid
				}
			});
		} else {
			// "grandTotal" includes discounts and shipping
			// NOTE: A refundable deposit, thus added in addition to the cost of the order
			out.grandTotal = out.grandTotal.plus(out.shippingDiscounted).plus(_.get(order, 'deposit.amount', 0));

			// NOTE: Could intentionally round up tax however ATO says normal rounding is fine.
			// @see https://www.ato.gov.au/Business/GST/Tax-invoices/
			out.grandTotalTax = (order.taxExempt) ? new Big(0) : this.calcTax(order.instance, out.grandTotal); // Tax includes shipping
		}

		out.subTotalTax = (order.taxExempt) ? new Big(0) : this.calcTax(order.instance, out.subTotal);
		out.subTotalExTax = out.subTotal.minus(out.subTotalTax);

		// NOTE: Having "remoteFetch" and others handle updating "totalPaid" and adding any missing "payments"
		// is order is paid in woo
		// const isPaid = (
		// 	// FIXME: Why would a variable be called "wooPaid" and then look for "aos" orders?
		// 	// order.source === 'aos'
		// 	// FIXME: Is "completed" status actually desired or a bad assuption?
		// 	(order.status === 'completed' || order.status === 'processing') // Assume "processing" order is paid in full
		// 	&& order.totalPaid === 0
		// 	&& order.payments.length === 0
		// );
		// If woo paided we assume the order has been paid in full
		// out.grandTotalPaid = (isPaid) ? _.get(order, 'totalAmount', 0) : _.get(order, 'totalPaid', 0);

		out.grandTotalExTax = out.grandTotal.minus(out.grandTotalTax);
		out.grandTotalPaid = _.get(order, 'totalPaid', 0);
		out.grandTotalOwing = out.grandTotal.minus(out.grandTotalPaid);
		// NOTE: "grandTotalOwing" is deliberately left signed - it is persisted onto the order and a negative
		// value is what stops further coupon allocation. "grandTotalOverpaid" is the positive magnitude of an
		// overpayment, so documents can print "Overpaid $X" rather than "Outstanding -$X"
		out.grandTotalOverpaid = out.grandTotalOwing.lt(0) ? out.grandTotalOwing.times(-1) : new Big(0);

		// NOTE: Rounding is applied after all maths has been performed and before any values are used in invo
		if($$debug.enabled)
			$$debug(
				'out',
				_.mapValues(out, (d) => parseFloat(d.toFixed(2))),
			);
		return _.mapValues(out, (d) => parseFloat(d.toFixed(2)));
	},


	/**
	 * Determine whether an order has been paid in full
	 * Compares `totalPaid` / `totalAmount` to the cent, avoiding floating point drift that
	 * accumulates when summing payment amounts (e.g. store credit made up of several coupon values)
	 *
	 * @param {Object} order Order to check, expects `totalPaid` + `totalAmount`
	 * @returns {Boolean} True if `totalPaid` is greater than or equal to `totalAmount` once rounded to cents
	 */
	isOrderFullyPaid(order) {
		const totalPaid = new Big(_.get(order, 'totalPaid', 0)).round(2);
		const totalAmount = new Big(_.get(order, 'totalAmount', 0)).round(2);
		return totalPaid.gte(totalAmount);
	},

	// FIXME: Legacy orphan? Not observed being used.

	/**
	 * Decode the active search into helper values
	 *
	 * @param {String} [searchQuery=''] Search string
	 * @param {Object} currentHelper Existing helper configuration
	 * @returns {Object}
	 */
	decodeSearch(searchQuery = '', currentHelper) {
		return (
			_.chain(searchQuery)
				// Split into array preserving quotes {{{
				.thru((v) => v.match(/\\?.|^$/g))
				.reduce(
					(p, c) => {
						if (c == '"') {
							p.quote ^= 1;
						} else if (!p.quote && c == ' ') {
							p.a.push('');
						} else {
							p.a[p.a.length - 1] += c.replace(/\\(.)/, '$1');
						}
						return p;
					},
					{ a: [''] },
				)
				.get('a')
				.filter()
				// }}}
				.reduce(
					(helper, i) => {
						if (i.startsWith('after:')) {
							helper.after = moment(i.substring(6)).toDate();
						} else if (i.startsWith('before:')) {
							helper.before = moment(i.substring(7)).toDate();
						} else if (i.startsWith('is:')) {
							var statuses = i.substring(3).split(/\s*,\s*/);
							helper.statusInvoice = statuses.includes('invoice');
							helper.statusProcessing = statuses.includes('processing');
							helper.statusCompleted = statuses.includes('completed');
							helper.statusReturnProcessing = statuses.includes('returnProcessing');
							helper.statusReturnCompleted = statuses.includes('returnCompleted');
							helper.statusRefundCompleted = statuses.includes('refundCompleted');
							helper.statusDeleted = statuses.includes('deleted');
						} else if (i.startsWith('payment:')) {
							helper.payment = i.substr(8);
						} else if (i.startsWith('owner:')) {
							helper.user = i.substr('owner:'.length);
						} else if (i.startsWith('customer:')) {
							helper.customer = i.substr('customer:'.length);
						} else if (i.startsWith('paymentmethod:')) {
							var methods = i.substring('paymentmethod:'.length).split(/\s*,\s*/);
							methods.forEach((method) => {
								helper.paymentMethod[method] = true;
							});
						} else if (i.startsWith('report:')) {
							helper.reportEd = true;
						} else {
							// Don't recognise as a token - throw onto fuzzy searching pile
							helper.search.push(i);
						}
						return helper;
					},
					{
						..._.mapValues(currentHelper, (v, k) => (k == 'paymentMethod' ? {} : undefined)), // Copy of helper values with everything set to undefined
						search: [], // Array version of generic search (joined later)
					},
				)
				.thru((v) => _.set(v, 'search', v.search.join(' '))) // Join generic search terms back up again
				.value()
		);
	},

	/**
	 * Format phone numbers to local format from international
	 * @param {String} instance enum ['AU', 'UK', 'NZ']
	 * @param {String} number Phone number to change, will take local or international format
	 * @returns {String} local format phone number
	 */
	formatPhoneLocal(instance, number) {
		var codes = {
			AU: '61',
			UK: '44',
			NZ: '64',
		};
		return Promise.resolve()
			.then(() => {
				if (!['AU', 'UK', 'NZ'].includes(instance)) throw `${instance} not a supported instance`;
			})
			.then(() => {
				if (_.startsWith(number, '+')) {
					number = number.replace('+', '');
				}
				if (_.startsWith(number, codes[instance])) {
					number = number.replace(codes[instance], '0');
				}
			})
			.then(() => {
				number = number.replace(/\D/g, '');
			})
			.then(() => number)
			.catch(() =>{
				console.warn('formatPhoneLocal: Error formatting phone number', {
					instance,
					number,
				});
				number
			})
	},

	/**
	 * Calculate coupon values per order item and mutate items to save calculation
	 * @param {Object} order Order The order to be mutated
	 * @param {Object} products Products API lookup A list of products to lookup bundle status WARNING: Do not update this and expect it to be applied to the products object that is passed. We CloneDeep this in places to get it to update
	 */
	applyCoupons(order, products) {
		//if (!_.isPlainObject(order)) throw new Error(`applyCoupons(): Invalid order object`);
		if (!_.isPlainObject(products)) throw new Error(`applyCoupons(): Invalid products lookup object`);

		// Test coupons {{{
		// yjon-4ko0-vsrt = $50
		// FB5 = 5%
		// }}}

		const $$debug = $debug.extend('applyCoupons');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order.items), _.cloneDeep(order.coupons), _.cloneDeep(products));

		const plainOrder = (!_.isPlainObject(order) ? order.toObject() : order);

		// TODO: ABI-3224: Move "set" encapsulation outside this function

		// Server-safe setters for isomorphic (Vue + Node) execution.
		// On the client (Vue component), this.$setPath / this.$set provide reactivity.
		// On the server (Mongoose document), use Mongoose's own set() method for proper change tracking.
		// Otherwise, fall back to _.set / direct assignment for plain objects.
		const safeSetPath = (target, path, value) => {
			if (typeof this.$setPath === 'function') {
				this.$setPath(target, path, value);
			} else if (typeof target.set === 'function' && typeof target.markModified === 'function') {
				// Mongoose subdocument: use its own set() for proper change tracking.
				// _.set bypasses Mongoose's internal _doc and change detection, meaning
				// subsequent toObject() / save() calls may not reflect the mutation.
				const pathStr = _.isArray(path) ? path.join('.') : path;
				target.set(pathStr, value);
			} else {
				_.set(target, path, value);
			}
		};
		const safeSet = (target, key, value) => {
			if (typeof this.$set === 'function') {
				this.$set(target, key, value);
			} else if (typeof target.set === 'function' && typeof target.markModified === 'function') {
				// Mongoose subdocument: use its own set() for proper change tracking
				target.set(key, value);
			} else {
				target[key] = value;
			}
		};

		// TODO: ABI-2665_1of13: Move shipping discount into "discounts" also?
		order.shippingDiscount = 0; //Reset shipping discount

		if (!products || _.isEmpty(products)) {
			console.warn('applyCoupons.end: Product not yet loaded', products, order);
			return; // Product data not loaded yet
		}

		// When there are no coupons we can simply exit after removing any "coupon" discounts from all the items
		if (_.isEmpty(order.coupons)) {
			if($$debug.enabled)
				$$debug('empty', _.cloneDeep(order.items));

			// Remove any existing applied coupons
			order.items
				.filter((item) => this.hasItemDiscountType(
					(!_.isPlainObject(item)) ? item.toObject() : item,
					'coupons'
				) && _.isArray(item.discounts.coupons) && item.discounts.coupons.length > 0)
				.forEach(item => safeSet(item.discounts, 'coupons', []));
			return;
		}

		const subTotal = this.itemTotal(plainOrder, products, { useOriginalCost: false });
		if($$debug.enabled)
			$$debug('subTotal', parseFloat(subTotal.toFixed(2)));

		// Apply shipping discount
		if (_.find(order.coupons, ['freeShipping', 'yes'])) {
			order.shippingDiscount = order.totalShipping;
		}

		// NOTE: Validation on adding coupons restricts there from ever being any other coupons on an order where a "individualUse" coupon has already been added.
		// Only warn when multiple coupons exist alongside an individual-use coupon; a single individual-use coupon is valid on its own.
		if (order.coupons.length > 1 && order.coupons.some((c) => c.individualUse && c.individualUse !== 'no'))
			console.warn('Order contains coupons which should have been restricted by "individualUse"');

		// NOTE: Not processing bundles until the end, instead adding the "Additional charge" line and dealing with it directly
		// This allows "itemCost" to return the "Additional charge" cost instead of the combined bundle total
		// NOTE: Using plainOrder to satisfy _.isPlainObject guards in addAdditionalChargeItem and downstream.
		const orderItems = this.addAdditionalChargeItem(plainOrder, products).filter(d => !this.isItemBundle(d));
		if($$debug.enabled)
			$$debug('orderItems.before', _.cloneDeep(orderItems));

		// When order is a Mongoose document, plainOrder is a deep copy so orderItems reference copies.
		// Build a ref-based lookup to the original order items so finalization mutations propagate
		// to the actual document (needed for order.save() to persist changes).
		const getOriginalItem = (order !== plainOrder)
			? (item) => (item.ref && order.items.find(oi => oi.ref === item.ref)) || item
			: (item) => item;

		// TODO: ABI-3224: Use BigJS for parsing values.

		// Parse coupon value safely without mutating the original Mongoose subdocument.
		// CRITICAL: Direct mutation of coupon.value (e.g., coupon.value = parseFloat(coupon.value))
		// causes Mongoose String→Number type-casting issues that corrupt the stored value to "0"
		// when the order is subsequently saved server-side (e.g., payment page).
		const _parseCouponValue = (coupon) => {
			const raw = coupon.value;
			const parsed = _.isString(raw) ? parseFloat(raw) : Number(raw);
			return _.isFinite(parsed) ? parsed : 0;
		};

		const orderCoupons = order.coupons
			.filter((coupon) => _.isString(coupon.discountType) && (_parseCouponValue(coupon) > 0 || coupon.freeShipping === 'yes'))
			.sort((a, b) =>
				// Send fixed dollar to end
				(a.discountType === 'fixed_cart' && b.discountType === 'percent') ||
					// Percentages by largest % first
					(a.discountType === 'percent' && b.discountType === 'percent' && _parseCouponValue(a) < _parseCouponValue(b))
					? 1
					: -1,
			);
		if($$debug.enabled)
			$$debug('orderCoupons.sorted', _.cloneDeep(orderCoupons));

		// TODO: Encapsulate duplication

		const appliedCoupons = [];
		const appliedItems = [];
		const appliedTotals = [];

		// Initialise tracking maps {{{
		orderCoupons.forEach((coupon, couponIndex) => {
			appliedCoupons[couponIndex] = {
				calculatedValue: new Big(0),
			};
		});
		if($$debug.enabled)
			$$debug(
				'appliedCoupons.before',
				_.cloneDeep(appliedCoupons).map((out) => _.mapValues(out, (d) => d.toString())),
			);

		orderItems.forEach((item, itemIndex) => {
			appliedItems[itemIndex] = {
				discounts: _.omit(_.cloneDeep(item.discounts), 'coupons'),
			};
		});
		if($$debug.enabled)
			$$debug('appliedItems.before', _.cloneDeep(appliedItems));
		// }}}

		// Process coupons {{{
		orderCoupons.forEach((coupon, couponIndex) => {
			// Parse coupon value locally — do NOT mutate coupon.value on the original document
			const couponValueNum = _parseCouponValue(coupon);

			if($$debug.enabled)
				$$debug('coupon', _.cloneDeep(coupon), couponIndex, 'couponValueNum', couponValueNum);

			const itemFilter = item => {
				if (!this.hasItemPrice(item, products)) return false;
				const children = this.getOrderItemChildren(orderItems, item);
				if (this.isItemExcludedByCoupon(plainOrder, children, item, coupon, products)) return false;
				if (!this.isItemIncludedByCoupon(plainOrder, item, coupon, products)) return false;
				return true;
			};

			const validation = this.validateCoupon(coupon, plainOrder, products);
			appliedCoupons[couponIndex].enabled = validation.valid;
			if (!validation.valid) {
				// For Woo-imported orders, preserve the imported coupon data so the UI shows what Woo had
				const wooImportedValue = Number(coupon.calculatedValue);
				if (order.source === 'woo' && wooImportedValue > 0) {
					appliedCoupons[couponIndex].enabled = true;
					appliedCoupons[couponIndex].calculatedValue = wooImportedValue;
					appliedCoupons[couponIndex].validationMessage = '';
					return;
				}

				$$debug('INVALID', validation);
				appliedCoupons[couponIndex].validationMessage = validation.message;

				// Add placeholder so that "couponIndex" align {{{
				orderItems.forEach((item, itemIndex) => {
				// TODO: Encapsulate duplication
				// Add value to discounts array {{{
				const newDiscount = {
					creator: coupon.creator,
					code: coupon.code,
					discountType: coupon.discountType,
				};
				this.applyDiscountWithTax(
					plainOrder,
					newDiscount,
					'$0',
					plainOrder.taxExempt,
				);
				if($$debug.enabled)
					$$debug(
						'discounts',
						_.cloneDeep(cloneItem),
						'$0',
						_.cloneDeep(newDiscount),
					);

				// NOTE: appliedItems is a local tracking object — direct assignment is fine (no Vue reactivity needed here)
				if (!_.isArray(appliedItems[itemIndex].discounts.coupons))
					appliedItems[itemIndex].discounts.coupons = [];
				appliedItems[itemIndex].discounts.coupons.push(newDiscount);
				// }}}
			});
			// }}}
			return;
			}

			// Calculate costs before this coupon is applied to any items for the purposes of determining what the discount would have been if applied to the subtotal {{{
			// NOTE: Each item uses this same tally as the goal is to split fixed dollar discounts proportionally among items
			const discountedItems = orderItems.map((discountedItem, discountedItemIndex) => {
				const cloneItem = _.omit(_.cloneDeep(discountedItem), Object.keys(appliedItems[discountedItemIndex]));
				_.merge(cloneItem, appliedItems[discountedItemIndex]);
				return cloneItem;
			});
			if($$debug.enabled)
				$$debug('discountedItems', _.cloneDeep(discountedItems));

			// TODO: ABI-2665_3of13: Exclude items which are already at their maximum; Each item must consume what it can before leaving the rest for others.
			const nonExcludedItems = discountedItems.filter(itemFilter);
			if($$debug.enabled)
				$$debug('nonExcludedItems', _.cloneDeep(nonExcludedItems));

			// NOTE: Include bundle parent items alongside non-excluded items so that downstream
			// functions (e.g., getBundleChildDiscountShare via itemDiscountGreater) can look up
			// parents via order.items.find(d => d.ref === item.parentRef). Bundle parents are
			// excluded from direct coupon processing by the itemTotal/itemCost filters.
			const bundleParents = plainOrder.items.filter(d => this.isItemBundle(d, products));
			const nonExcludedOrder = _.omit(_.cloneDeep(plainOrder), ['items']);
			_.merge(nonExcludedOrder, {
				items: [...nonExcludedItems, ...bundleParents],
			});

			appliedCoupons[couponIndex].calculatedValue = '0.00';

			// TODO: ABI-2665_3of13: Should coupon sharing be based on prices after sale/override? What about line discounts?

			// NOTE: Using totals with "taxExempt" applied as "pricedIndividually" bundles may have children with rounded tax affected costs.
			// If the rounding applied to these items is not considered then value calculations may disagree depending on if they're looking at children or only what the parent knows of it's value.
			const excludeSubTotalDiscounted = this.itemTotal(nonExcludedOrder, products, {
				considerDiscount: true,
				considerDiscountTypes: ['customer', 'line', 'override', 'sale'], // Coupon split based on ratio after sale/override/line are applied
				filter: item => !this.isItemBundle(item, products) && itemFilter(item), // Exclude bundle parents (included only for reference lookups); bundle children pass through itemFilter
			});
			if($$debug.enabled)
				$$debug('excludeSubTotalDiscounted', excludeSubTotalDiscounted.toString());
			// }}}

			const itemCount = orderItems
				.filter(itemFilter)
				.map(d => d.quantity)
				.reduce((acc, cur) => acc + cur, 0);
			$$debug('itemCount', itemCount);

			// Guard: No eligible items for this coupon — skip discount calculation to avoid division by zero
			if (itemCount === 0) {
				appliedCoupons[couponIndex].validationMessage = 'No eligible items';
				appliedCoupons[couponIndex].enabled = false;

			// Add placeholder so that couponIndex aligns
			orderItems.forEach((item, itemIndex) => {
				const newDiscount = {
					creator: coupon.creator,
					code: coupon.code,
					discountType: coupon.discountType,
				};
				this.applyDiscountWithTax(
					plainOrder,
					newDiscount,
					'$0',
					plainOrder.taxExempt,
				);
				// NOTE: appliedItems is a local tracking object — direct assignment is fine (no Vue reactivity needed here)
				if (!_.isArray(appliedItems[itemIndex].discounts.coupons))
					appliedItems[itemIndex].discounts.coupons = [];
				appliedItems[itemIndex].discounts.coupons.push(newDiscount);
				});
				return;
			}

			// Process items {{{
			orderItems.forEach((item, itemIndex) => {
				if (!itemFilter(item)) {
					// Add $0 placeholder for excluded/non-priced items so that finalization
					// clears any stale coupon discounts from previous calculations.
					const newDiscount = {
						creator: coupon.creator,
						code: coupon.code,
						discountType: coupon.discountType,
					};
					this.applyDiscountWithTax(plainOrder, newDiscount, '$0', plainOrder.taxExempt);
					if (!_.isArray(appliedItems[itemIndex].discounts.coupons))
						appliedItems[itemIndex].discounts.coupons = [];
					appliedItems[itemIndex].discounts.coupons.push(newDiscount);
					return;
				}

				if($$debug.enabled)
					$$debug('item', _.cloneDeep(item), itemIndex);

				const cloneItem = _.omit(_.cloneDeep(item), Object.keys(appliedItems[itemIndex]));
				_.merge(cloneItem, appliedItems[itemIndex]);

				const excludeLineCostDiscounted = this.itemCost(nonExcludedOrder, null, cloneItem, products, {
					considerDiscount: true,
					considerDiscountTypes: ['customer', 'line', 'override', 'sale'], // Coupon split based on ratio after sale/override/line are applied
				}).times(item.quantity);
				if($$debug.enabled)
					$$debug(
						'excludeLineCostDiscounted',
						_.cloneDeep(nonExcludedOrder),
						_.cloneDeep(cloneItem),
						_.cloneDeep(appliedItems[itemIndex].discounts),
						excludeLineCostDiscounted.toString(),
					);

				// Determine coupon value {{{
				$$debug('couponValueNum', couponValueNum);
				let couponDiscountString;
				switch (coupon.discountType) {
					case 'fixed_cart': {
						// Apply "taxExempt" to incoming fixed dollar discounts
						const couponValueRaw = (order.taxExempt)
							? new Big(couponValueNum).minus(this.calcTax(order.instance, couponValueNum)).round(2)
							: new Big(couponValueNum); // "original"

						// Cap the coupon value at the eligible subtotal so lines never go
						// below $0 (100% discount max). When the coupon exceeds the order
						// subtotal, only the subtotal amount is applied.
						const couponValue = couponValueRaw.gt(excludeSubTotalDiscounted)
							? excludeSubTotalDiscounted
							: couponValueRaw;
						if ($$debug.enabled)
							$$debug('couponValue', couponValue.toString(), 'raw', couponValueRaw.toString(), 'subtotalCap', excludeSubTotalDiscounted.toString());

						let totalCouponValFromDollar = new Big(0);
						// Distribute proportionally based on each item's contribution to the subtotal
						// e.g., $20 coupon on a $200 cart: $150 item (75%) gets $15, $50 item (25%) gets $5
						// Rounding down per-item; any remaining cents are distributed afterwards via getOrderItemDiscountRemainderShares
						if (excludeSubTotalDiscounted.gt(0)) {
							totalCouponValFromDollar = couponValue
								.times(excludeLineCostDiscounted)
								.div(excludeSubTotalDiscounted)
								.round(2, Big.roundDown);
						}

						if($$debug.enabled)
							$$debug('totalCouponValFromDollar', totalCouponValFromDollar.toString());

						// FIXME: "calculatedValue" may contain value which was not subsequently consumed by the item; For instance a sale may win out.
						appliedCoupons[couponIndex].calculatedValue = new Big(appliedCoupons[couponIndex].calculatedValue)
							.plus(totalCouponValFromDollar).toFixed(2);

						// NOTE: Fixed dollar discounts are stored as their total which is then divided between quantity
						// Where percentage coupons are stored as their per-item value which allows for easier determining "winning" discounts
						// TODO: ABI-3224: Should this also store as per-item? Would it cause rounding issues or the like? It means a single item can be accurately refunded...
						couponDiscountString = '$' + totalCouponValFromDollar.toFixed(2);

						break;
				}

				case 'percent': {
					// Calculate discount from item's base cost (before other discounts) so the percentage
					// coupon competes fairly with other discount types (sale/override/line/customer) via
					// the highest-discount-wins logic in itemDiscountGreater.
					// NOTE: "taxExempt" is applied via itemCost which respects order.taxExempt on the cost field.
					const itemBaseCost = this.itemCost(nonExcludedOrder, null, cloneItem, products, {
						considerDiscount: false,
					}).times(item.quantity);
					if($$debug.enabled)
						$$debug('itemBaseCost', itemBaseCost.toString());

					let totalCouponValFromPercent = itemBaseCost.times(couponValueNum).div(100).round(2, Big.roundDown);
					if($$debug.enabled)
						$$debug('totalCouponValFromPercent', totalCouponValFromPercent.toString());

					// Percentage coupon discount applies when greater than sale, override and line
					const children = this.getOrderItemChildren(orderItems, item);
					const greaterDiscount = this.itemDiscountGreater(plainOrder, children, item, products, {
						considerDiscountTypes: ['customer', 'line', 'override', 'sale'] // Omitting "coupons"
					});
					if ($$debug.enabled) $$debug('greaterDiscount', greaterDiscount.name, _.mapValues(greaterDiscount.discount, d => d.toString()));

					$$debug('totalCouponValFromPercent', totalCouponValFromPercent.toString());
					$$debug('appliedCoupons[couponIndex]', appliedCoupons[couponIndex]);

					// "calculatedValue" is interested in the total value
					if (totalCouponValFromPercent.div(item.quantity).gt(greaterDiscount.discount.discount))
						appliedCoupons[couponIndex].calculatedValue = new Big(appliedCoupons[couponIndex].calculatedValue)
							.plus(totalCouponValFromPercent).toFixed(2);

					// NOTE: Passing down dollar values for simple/child items means value per item is always known and we can adjust remainders in the same manner as Woo
					// "discounts" is interested in the per-item value
					couponDiscountString = '$' + totalCouponValFromPercent.toFixed(2);
					break;
				}}

				if($$debug.enabled)
					$$debug('calculatedValue', appliedCoupons[couponIndex].calculatedValue);
				// }}}

				// TODO: Encapsulate duplication
				// Add value to discounts array {{{
				const newDiscount = {
					creator: coupon.creator,
					code: coupon.code,
					discountType: coupon.discountType,
				};
				// FIXME: Tax was already applied to "couponValue", though this will ensure all fields are defined correctly?
				this.applyDiscountWithTax(
					plainOrder,
					newDiscount,
					couponDiscountString,
					plainOrder.taxExempt,
				);
				if($$debug.enabled)
					$$debug(
						'discounts',
						_.cloneDeep(cloneItem),
						couponDiscountString,
						_.cloneDeep(newDiscount),
					);

				// NOTE: appliedItems is a local tracking object — direct assignment is fine (no Vue reactivity needed here)
				if (!_.isArray(appliedItems[itemIndex].discounts.coupons))
					appliedItems[itemIndex].discounts.coupons = [];
				appliedItems[itemIndex].discounts.coupons.push(newDiscount);
				// }}}
			});
			// }}}
			// }}}

			// Account for any remaining cents due to rounding {{{
			$$debug('remaining.coupon', _.cloneDeep(coupon), appliedCoupons[couponIndex].calculatedValue);
			let remainingDiscount = 0
			switch (coupon.discountType) {
				case 'fixed_cart': {
					// Use the same capped coupon value as the distribution step so that the
					// remainder calculation is consistent and never pushes lines below $0.
					const totalCouponValFromDollar = (() => {
						const raw = (order.taxExempt)
							? new Big(couponValueNum).minus(this.calcTax(order.instance, couponValueNum)).round(2)
							: new Big(couponValueNum);
						return raw.gt(excludeSubTotalDiscounted)
							? excludeSubTotalDiscounted
							: raw;
					})();
					if($$debug.enabled)
						$$debug('remaining.totalCouponValFromDollar', totalCouponValFromDollar.toString());

					remainingDiscount = totalCouponValFromDollar.minus(appliedCoupons[couponIndex].calculatedValue);
					$$debug('remainingDiscount', remainingDiscount.toString());

					break;
			}

			case 'percent': {
				// ABI-3224: Percentage coupons only apply when they are greater than others
				// Use base subtotal (before other discounts) to match per-item base-cost calculation
				// so the remainder accurately reflects rounding differences.
				const baseSubTotal = this.itemTotal({
					...plainOrder,
					items: _.cloneDeep(orderItems) // NOTE: "orderItems" indexes match "appliedItems"
				}, products, {
					considerDiscount: false,
					filter: (item, itemIndex) => {
						if (
							!itemFilter(item) // Excluding items insitu to maintain "itemIndex" match between "orderItems" and "appliedItems"
						) return false;

						const children = this.getOrderItemChildren(orderItems, item);
						$$debug('remaining.item', item.product, _.cloneDeep(item), _.cloneDeep(appliedItems[itemIndex]), itemIndex);

						const percentageWinner = this.itemDiscountGreater(plainOrder, children, {
							...item,
							...appliedItems[itemIndex], // Pulling from "appliedItems" as we need "discounts.coupons" populated with new data
						}, products);

						$$debug('remaining.percentageWinner', item.product, percentageWinner.name, _.mapValues(percentageWinner.discount, d => d.toString()));

						// Include either default "sale" winner, zero discount or percentage coupon winners
						return (percentageWinner.name === 'coupons' || percentageWinner.discount.discount.eq(0));
					},
				});
				if($$debug.enabled) $$debug('remaining.baseSubTotal', baseSubTotal.toString());
				const totalCouponValFromPercent = baseSubTotal.times(couponValueNum).div(100).round(2);
				if($$debug.enabled) $$debug('remaining.totalCouponValFromPercent', totalCouponValFromPercent.toString());

				remainingDiscount = totalCouponValFromPercent.minus(appliedCoupons[couponIndex].calculatedValue);

				break;
			}}

			$$debug('remainingDiscount', remainingDiscount.toString());

			const orderItemsRemainingCents = this.getOrderItemDiscountRemainderShares(orderItems, remainingDiscount, {
				// NOTE: We must filter in this way as desire the item indexes to remain aligned
				// TODO: ABI-3224: Do we need to filter for percent coupons here also?
				filter: itemFilter,
			});
			$$debug('orderItemsRemainingCents', _.cloneDeep(orderItemsRemainingCents));

			// NOTE: Assumes that all discounts will be in dollars by this point
			orderItemsRemainingCents.forEach((remainingCents, itemIndex) => {
				if (!remainingCents) return;

				const remainingDollars = new Big(remainingCents).div(100);
				$$debug('discounts.coupons', itemIndex, couponIndex, appliedItems[itemIndex].discounts.coupons[couponIndex]);
				const discountString = appliedItems[itemIndex].discounts.coupons[couponIndex].discount;
				const discountValueIncoming = _.isString(discountString) ? new Big(discountString.replace(/[^0-9\.\-]/g, '')) : new Big(0);
				$$debug('discountValueIncoming', discountValueIncoming.toString());
				const discountvalueAdjusted = discountValueIncoming.plus(remainingDollars)
				$$debug('discountvalueAdjusted', discountvalueAdjusted.toString());
				appliedCoupons[couponIndex].calculatedValue = new Big(appliedCoupons[couponIndex].calculatedValue)
					.plus(remainingDollars.toFixed(2)).toFixed(2);
				$$debug('calculatedValue', appliedCoupons[couponIndex].calculatedValue);

				this.applyDiscountWithTax(
					plainOrder,
					appliedItems[itemIndex].discounts.coupons[couponIndex],
					'$' + discountvalueAdjusted.toFixed(2),
					plainOrder.taxExempt,
				);
			});
		});
		// }}}

		// Finalise output {{{
		// Ensure "itemTotals" computed in "order.item" updates
		orderItems.forEach((item, itemIndex) => {
			if (appliedItems[itemIndex]) {
			// NOTE: Only apply mutation when value share in fact changed; Otherwise generates an update loop.
			const a = _.get(_.cloneDeep(appliedItems[itemIndex]), 'discounts.coupons', []);
			const b = _.get(_.cloneDeep(item), 'discounts.coupons', []);
			const diffA = _.differenceBy(a, b, 'code');
			const diffB = _.differenceBy(a, b, 'discount');
			// NOTE: Length check catches cases where coupons were added or removed (e.g., excluded items
			// whose stale discounts need clearing). differenceBy alone misses this when a is shorter than b.
			if ((diffA && diffA.length > 0) || (diffB && diffB.length > 0) || a.length !== b.length) {
					// Copy to item itself; Be it a simple, bundle child or "Additional charge" line
					// NOTE: getOriginalItem maps plain copies back to original Mongoose subdocs for server-side mutation
					safeSetPath(getOriginalItem(item), 'discounts.coupons', _.get(appliedItems[itemIndex], 'discounts.coupons'));

					// For "Additional charge" lines sum with others and copy to parent bundle
					// NOTE: We do the calculations against "Additional charge" line and then apply to the parent; This mimics Woo only dealing with children and having bundles with no cost of their own
					if (this.isItemBundleAdditionalCharge(item)) {
						const parent = this.getOrderItemParent(order.items, item);
						if ($$debug.enabled) $$debug('parent', _.cloneDeep(item), _.cloneDeep(parent));

						// Children excluding the "Additional charge" line
						const children = this.getOrderItemChildren(orderItems, parent).filter(d => !this.isItemBundleAdditionalCharge(d));
						if ($$debug.enabled) $$debug('children', _.cloneDeep(children));

						orderCoupons.forEach((coupon, couponIndex) => {
							let bundleDiscount = {};
							// NOTE: Assumes that all discounts will be in dollars by this point
							const childTally = children.map(child => {
								const childIndex = orderItems.findIndex(d => d.ref === child.ref); // FIXME: By "ref" or by entire object?
								const childDiscount = _.get(appliedItems[childIndex], ['discounts', 'coupons', couponIndex, 'discount'], '0').replace(/[^0-9\.\-]/g, '');
								if ($$debug.enabled) $$debug('childDiscount', _.cloneDeep(appliedItems[childIndex]), childDiscount);
								return new Big(childDiscount);
							}).reduce((acc, cur) => acc.plus(cur), new Big(0));
							$$debug('childTally', childTally.toString());

							// NOTE: "Additional charge" line is standing in for the bundle itself
							const parentDiscount = _.get(appliedItems[itemIndex], ['discounts', 'coupons', couponIndex, 'discount'], '0').replace(/[^0-9\.\-]/g, '');
							if ($$debug.enabled) $$debug('parentDiscount', _.cloneDeep(appliedItems[itemIndex]), parentDiscount);
							const parentTally = new Big(parentDiscount);
							$$debug('parentTally', parentTally.toString());

							bundleDiscount = {
								creator: coupon.creator,
								code: coupon.code,
								discountType: coupon.discountType,
							};
							this.applyDiscountWithTax(
								plainOrder,
								bundleDiscount,
								'$' + (parentTally.plus(childTally).toFixed(2)),
								plainOrder.taxExempt,
							);
							$$debug('bundleDiscount', bundleDiscount);

							if (!_.isArray(parent?.discounts?.coupons))
								safeSetPath(parent, 'discounts.coupons', []);

							// NOTE: Only apply mutation when value share in fact changed; Otherwise generates an update loop.
							const a = [_.cloneDeep(bundleDiscount)];
							const b = [_.get(_.cloneDeep(parent), ['discounts', 'coupons', couponIndex], {})];
							const diffA = _.differenceBy(a, b, 'code');
							const diffB = _.differenceBy(a, b, 'discount');
							if ((diffA && diffA.length > 0) || (diffB && diffB.length > 0))
								safeSetPath(parent, ['discounts', 'coupons', couponIndex], bundleDiscount);
						});
					}
				}
			} else {
				// TODO: Must we also hande bundle parents lines?
				if (typeof this.$delete === 'function') {
					this.$delete(item, itemIndex);
				} else {
					delete item[itemIndex];
				}
			}
		});

		// NOTE: Using $set for Vue reactivity — properties like "enabled", "calculatedValue", "validationMessage"
		// may not exist on the coupon object initially (they come from applyCoupons, not the API response).
		// Plain _.merge / direct assignment won't trigger Vue 2 reactivity for new properties.
		orderCoupons.forEach((coupon, couponIndex) => {
			if (!appliedCoupons[couponIndex]) return;
			Object.keys(appliedCoupons[couponIndex]).forEach(key => {
				if (typeof this.$set === 'function') {
					this.$set(coupon, key, appliedCoupons[couponIndex][key]);
				} else {
					// Server-side fallback: no Vue reactivity needed
					coupon[key] = appliedCoupons[couponIndex][key];
				}
			});
		});

		if($$debug.enabled) {
			$$debug(
				'applyCoupons.appliedCoupons.after',
				_.cloneDeep(appliedCoupons).map((out) => _.mapValues(out, (d) => d.toString())),
			);
			$$debug('appliedItems.after', _.cloneDeep(appliedItems));
			$$debug(
				'appliedTotals.after',
				_.cloneDeep(appliedTotals).map((out) => _.mapValues(out, (d) => d.toString())),
			);
			$$debug('orderItems.after', _.cloneDeep(orderItems));
			$$debug('orderCoupons.after', _.cloneDeep(orderCoupons));
			$$debug('order.after', _.cloneDeep(order));
		}
		// }}}
	},

	/**
	 * Validate an orders coupons
	 * @param {Object} coupon The coupon object to validate
	 * @param {Object} order Order The order to validate against
	 * @param {Object} products Products API lookup A list of products to lookup bundle status
	 * @param {Object} [options] Options Additional options
	 * @param {boolean} [options.ignoreExpired=false] Ignore expired coupons
	 * @returns {Object}
	 */
	validateCoupon(coupon, order, products, options) {
		if (!_.isPlainObject(order)) throw new Error('validateCoupon(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('validateCoupon(): Invalid products lookup object');

		// TODO: REFACTOR: Others pass "order" as first argument
		const settings = {
			ignoreExpired: false,
			...options,
		};

		const $$debug = $debug.extend('validateCoupon');

		if($$debug.enabled)
			$$debug(_.cloneDeep(coupon), _.cloneDeep(order), _.cloneDeep(products), options);

		let valid = true;
		//let validOthers = false;
		let message = '';

		if (!coupon) return { valid, message };

		const subTotal = this.itemTotal(order, products, { useOriginalCost: false });

		if (!settings.ignoreExpired && coupon.expiry && coupon.expiry < moment().unix()) {
			valid = false;
			message = 'Expired';
		} else if (_.has(coupon, 'maxAmount') && !_.isNil(coupon.maxAmount) && subTotal > parseFloat(coupon.maxAmount)) {
			valid = false;
			message = 'Maximum spend exceeded';
		} else if (_.has(coupon, 'minAmount') && !_.isNil(coupon.minAmount) && parseFloat(coupon.minAmount) > subTotal) {
			valid = false;
			message = 'Minimum spend not met';
		} else if (_.isArray(coupon.availableEmails) && coupon.availableEmails.length > 0 && !(
			coupon.availableEmails.includes(order.shipping?.email?.toLowerCase())
			|| coupon.availableEmails.includes(order.billing?.email?.toLowerCase())
		)) {
			valid = false;
			message = 'Not valid with customer email address';
		} else if (coupon.individualUse && coupon.individualUse !== 'no' && order.coupons.length > 1) {
			valid = false;
			message = 'Must not be used with any other coupons';
		}

		// NOTE: "limitPerUser" and "usageLimit" are enforced server-side in orders.methods.doop order.update()
		// as they require async MongoDB queries which cannot run in this isomorphic/synchronous function

		return { valid, message };
	},

	// FIXME: Legacy orphan? Not observed being used.

	/**
	 * Encode the helper values into a search string
	 *
	 * @param {Object} helper Helper configuration
	 * @returns {Object}
	 */
	encodeSearch(helper) {
		return [
			helper.search,
			helper.statusDeleted ||
				helper.statusInvoice ||
				helper.statusProcessing ||
				helper.statusCompleted ||
				helper.statusReturnProcessing ||
				helper.statusReturnCompleted
				? 'is:' +
				[
					helper.statusInvoice && 'invoice',
					helper.statusProcessing && 'processing',
					helper.statusCompleted && 'completed',
					helper.statusReturnProcessing && 'returnProcessing',
					helper.statusReturnCompleted && 'returnCompleted',
					helper.statusReturnCompleted && 'refundCompleted',
					helper.statusDeleted && 'deleted',
				]
					.filter((s) => s)
					.join(',')
				: '',
			helper.after ? `after:${moment(helper.after).format('YYYY-MM-DD')}` : '',
			helper.before ? `before:${moment(helper.before).format('YYYY-MM-DD')}` : '',
			helper.payment && helper.payment != 'all' ? `payment:${helper.payment}` : '',
			helper.user ? `owner:${helper.user}` : '',
			helper.customer ? `customer:${helper.customer}` : '',
			helper.reportEd ? `report:ed` : '',
			helper.paymentMethod && !_.isEmpty(helper.paymentMethod) && _.some(helper.paymentMethod, (v) => v)
				? 'paymentmethod:' +
				_(helper.paymentMethod)
					.pickBy((v) => v)
					.map((v, k) => k)
					.filter((s) => s)
					.join(',')
				: '',
		]
			.filter((i) => i)
			.join(' ');
	},

	/**
	 * Determine whether to show an ETA instead of the stock levels
	 * NOTE: Since this function supports lazy loading, omitting one or more of the options fields may result in the wrong result
	 * @param {Object} options Options to use when calculating whether to show an ETA
	 * @param {Object} [options.item] Line item data from the items section of `/api/orders/:orderId`
	 * @param {Object} [options.product] Product information from the `/api/products/:productId` endpoint
	 * @param {Object} [options.stock] Stock level data from the `/api/products/:productId/stock` endpointi
	 * @param {Boolean} [options.etaLocked] Whether the order's ETA is frozen (order.etaLocked); when true the frozen item.eta is returned verbatim rather than derived from live product data
	 * @returns {boolean} Whether to show the ETA instead of stock levels
	 */
	hasEta(options) {
		const orderId = options?.orderId;
		const orderRef = options?.orderRef;
		const sku = options?.product?.sku;
		const fulfilment = options?.item?.fulfilment;
		if (!options.item || !options.product) {
			return false; // Not enough data - assume no ETA
		}
		// NOTE: The two frozen-value branches are deliberately checked BEFORE the fulfilment guard below.
		// An ETA already stored on the line needs no warehouse or stock lookup to be returned, and while
		// the guard came first any line whose `fulfilment` was unset (fulfilmentLocation() returns
		// undefined in several legitimate paths, including its own "Give up" fallback) reported `false`
		// here and was then blanked by the caller's `|| ''` - destroying the very value the freeze exists
		// to preserve. See also the lazy-loading NOTE above.
		if (options.etaLocked) {
			return options.item.eta || '';
		}
		if (options.item.eta) {
			return options.item.eta; // ETA has been frozen on line item, use that instead
		}
		// Deriving a NEW ETA does need the warehouse, since stock is keyed by fulfilment location
		if (!options.item.fulfilment && !options.warehouseId) {
			return false; // No fulfilment information - assume no ETA
		}
		const stockEmpty = _.isEmpty(options.stock);
		const stockAtFulfilment = options.stock?.[fulfilment]?.stock;
		const stockStatus = options.product.stockStatus;
		const productEta = options.product.eta;
		const result = (
			(stockEmpty || stockAtFulfilment <= 0) &&
			(stockStatus !== 'outofstock'
				? productEta
					? `ETA: ${productEta}`
					: ''
				: 'Out of stock') //If no stock set to 'out of stock'
		);
		return result;
	},


	/**
	 * ETA qualifiers in chronological order within a month
	 */
	etaQualifiers: ['early', 'mid', 'late'],


	/**
	 * Month names in chronological order; three-letter abbreviations are matched against these too
	 */
	etaMonths: [
		'january', 'february', 'march', 'april', 'may', 'june',
		'july', 'august', 'september', 'october', 'november', 'december',
	],


	/**
	 * Parse a free-text ETA string into a sortable rank
	 *
	 * ETA originates as a free-text Woo product attribute, so parsing is deliberately forgiving: it is
	 * case-insensitive, tolerates surrounding whitespace, strips the `ETA: ` prefix AOS itself prepends
	 * (frozen `item.eta` values carry it, `productEtaHistory` rows do not) and accepts `Oct` as well as
	 * `October`. An unqualified month sorts as though it were "mid".
	 *
	 * The source text never carries a year, so one is inferred forward - a month earlier than the current
	 * month is taken as next year. That is only ever correct for a live comparison, which is all this is
	 * used for: a locked order returns its frozen value and never ranks anything.
	 *
	 * NOTE: An unrecognised value returns `{parsed: false}` rather than being coerced to either end of the
	 * ordering, so callers can skip it instead of silently mis-sorting a bundle.
	 *
	 * @param {String} eta The ETA text to parse
	 * @param {Object} [options] Additional options
	 * @param {Date} [options.now=new Date()] Reference date used for forward year inference
	 * @returns {Object} `{parsed: Boolean, rank: Number, inStock: Boolean, outOfStock: Boolean}`
	 */
	parseEta(eta, options) {
		const settings = {
			now: new Date(),
			...options,
		};

		const text = String(eta ?? '')
			.replace(/^\s*eta\s*:\s*/i, '') // Strip the `ETA: ` prefix AOS prepends
			.trim()
			.toLowerCase();

		// Blank means in stock, which is always the soonest thing a bundle child can be
		if (!text) return {parsed: true, rank: Number.NEGATIVE_INFINITY, inStock: true, outOfStock: false};

		// Out of stock has no date and always wins - a bundle cannot ship without the item
		if (text === 'out of stock') return {parsed: true, rank: Number.POSITIVE_INFINITY, inStock: false, outOfStock: true};

		// Qualifiers may be a single word or a hyphenated range, e.g. "Mid-Late October"
		const match = /^(?:((?:early|mid|late)(?:\s*-\s*(?:early|mid|late))?)\s+)?([a-z]+)$/.exec(text);
		if (!match) return {parsed: false};

		const monthIndex = this.etaMonths.findIndex(month => month === match[2] || month.slice(0, 3) === match[2]);
		if (monthIndex === -1) return {parsed: false};

		// NOTE: A range ranks at its LATEST bound - "Mid-Late October" sorts as "Late October". Matches the
		// pessimism already applied to out-of-stock: when a child could land at either end, the bundle
		// reports the later one rather than promising the earlier
		const qualifierIndex = match[1]
			? Math.max(...match[1].split('-').map(part => this.etaQualifiers.indexOf(part.trim())))
			: 1; // Unqualified sorts as "mid"
		const year = monthIndex < settings.now.getMonth() ? settings.now.getFullYear() + 1 : settings.now.getFullYear();

		return {
			parsed: true,
			rank: ((year * 12) + monthIndex) * this.etaQualifiers.length + qualifierIndex,
			inStock: false,
			outOfStock: false,
		};
	},


	/**
	 * Resolve a bundle parent's ETA as the latest of its children
	 *
	 * A bundle carries its own Woo ETA attribute, which says nothing about what is actually holding the
	 * bundle up - the parent should report whichever child lands last. Any child that is out of stock wins
	 * outright, since the bundle cannot ship without it.
	 *
	 * NOTE: This is computed on every read and never written to the parent's `item.eta`. Storing it would
	 * freeze a value derived from other lines, which would then drift as those lines changed.
	 *
	 * Children whose ETA cannot be parsed are skipped and the remaining children compared, so one odd Woo
	 * value cannot blank the parent. Each skip is logged so the offending value can be corrected.
	 *
	 * @param {Array<String>} childEtas The resolved ETA of each child line
	 * @param {Object} [options] Additional options, forwarded to parseEta()
	 * @returns {String} The latest child ETA, or '' when every child is in stock
	 */
	bundleEta(childEtas, options) {
		let winner = '';
		let winningRank = Number.NEGATIVE_INFINITY;

		(childEtas || []).forEach(eta => {
			const parsed = this.parseEta(eta, options);
			if (!parsed.parsed) {
				console.warn('bundleEta(): Unrecognised ETA on bundle child, skipped', eta);
				return;
			}
			if (parsed.rank <= winningRank) return;

			winningRank = parsed.rank;
			winner = parsed.inStock ? '' : eta;
		});

		return winner;
	},

	/**
	 * Calculate default fulfilment center for vanities
	 * @param {String} countryCode Code of country the order is from
	 * @returns {Number} Numerical representation of warehouse in mint
	 */
	vanityWarehouse(countryCode) {
		if (!countryCode) countryCode = 'AU'; //Default to australia
		switch (countryCode) {
			case 'AU':
				return 17;
			default:
				return NaN;
		}
	},

	/**
	 * Filter a list of warehouses down to only those the user should be able to interact with
	 * @param {Object} [options] Options structure to use
	 * @param {String} [options.context='view'] In what context to filter. ENUM: 'view', 'allocate'
	 * @param {Array<Object>} [options.warehouses=[]] The list of warehouses to filter down
	 * @param {Object} [options.user={}] User to filter against
	 * @returns {Array<Object>} Warehouses post-filter
	 */
	filterWarehouses(options) {
		const settings = {
			context: 'view',
			warehouses: [],
			user: {},
			...options,
		};
		settings.contextStartCase = _.startCase(settings.context);
		return settings.warehouses.filter(
			(w) => w.permission && settings.user?.permissions[w.permission + settings.contextStartCase],
		);
	},

	// TODO: REFACTOR: Prefix with "has"

	/**
	 *
	 * Checks whether an order is removed from Woo
	 *
	 * @param {Object} order Order the order to scan
	 * @param {Object} products Products API lookup
	 * @returns {Array}
	 */
	orderHasItemsWithMissingProductsInWoo(order, products) {
		if (!_.isPlainObject(order)) throw new Error('orderHasItemsWithMissingProductsInWoo(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('orderHasItemsWithMissingProductsInWoo(): Invalid products lookup object');

		return order.items.filter((i) => i.itemType == 'product').some((i) => !products[i.product]?.sku);
	},

	/**
	 * Calculate required auto deposit
	 * @param {Object} deposit Deposit configuration
	 * @param {Number} deposit.percentage Deposit percentage
	 * @param {Array<Object>} items Order items
	 * @returns {Number}
	 */
	calculateAutoDepositForOrders(deposit, items) {
		// TODO: REFACTOR: "items" -> "orderItems" for clarity; Or pass "order" instead as first argument
		if (!items) return 0;

		let depositVal = new Big(0);
		items
			.filter((item) => !this.isItemCategory(item) && _.has(item, 'cost') && !_.isNil(item.cost)) // Exclude "itemType: 'title'" and any others without a "cost"
			.forEach(function(item) {
				// FIXME: calculate bundles; Use "itemCost"?
				// TODO: Add test coverage
				const percentage = new Big(+_.get(deposit, 'percentage', 0)).div(100);
				const cost = new Big(+_.get(item, 'cost', 0)).times(_.get(item, 'quantity', 1));
				depositVal = depositVal.plus(cost.times(percentage));
			});
		return parseFloat(depositVal.toFixed(2));
	},

	/**
	 * Retrieve products relating to an order from SQL database via endpoint
	 *
	 * @param {Object} order Order
	 * @param {Array<Object>} items Order Items
	 * @param {Object} products Products API lookup
	 * @param {Boolean} [force=false] Force cache refresh
	 * @returns {Promise<Object>}
	 */
	fetchProducts(order, items, products, force = false) {
		if (!_.isPlainObject(order)) throw new Error('fetchProducts(): Invalid order object');
		if (!_.isPlainObject(products)) throw new Error('fetchProducts(): Invalid products lookup object');

		const $$debug = $debug.extend('fetchProducts');

		if($$debug.enabled)
			$$debug(_.cloneDeep(order), _.cloneDeep(items), _.cloneDeep(products), force);
		if (!_.isArray(items)) return Promise.resolve(console.warn('fetchProducts: No items on order'));

		return Promise.allLimit(
			10, // TODO: Configurable number of concurrent requests
			_.uniqBy(items.filter((item) => item.itemType === 'product'), 'product')
				.map(item => () => this.fetchProduct(order, item, products, force)),
		);
	},

	/**
	 * Fetch product item
	 *
	 * @param {Object} order Order
	 * @param {Object} item Item
	 * @param {Object} products Products API lookup
	 * @param {Boolean} [force=false] Force cache refresh
	 * @returns {Promise<Array>}
	 */
	fetchProduct(order, item, products, force = false) {
		if (!_.isPlainObject(order)) throw new Error('fetchProduct(): Invalid order object');
		if (!_.isPlainObject(item)) throw new Error('IfetchProduct(): nvalid item object');
		if (!_.isPlainObject(products)) throw new Error('fetchProduct(): Invalid products lookup object');

		const $$debug = $debug.extend('fetchProduct');

		// if($$debug.enabled)
		// 	$$debug(_.cloneDeep(item), _.cloneDeep(products), force);
		if (!force && _.has(products, item.product)) {
			$$debug('exists', item.product);
			return Promise.resolve().then(() => products[item.product]); // Already have this product cached
		}
		if (!force && _.has(this.pendingFetchProduct, item.product)) {
			$$debug('pending', item.product, Object.keys(this.pendingFetchProduct).length);
			return Promise.resolve().then(() => this.pendingFetchProduct[item.product]);
		}

		$$debug('request', item.product, Object.keys(this.pendingFetchProduct).length);
		return (this.pendingFetchProduct[item.product] = Promise.resolve()
			// FIXME: Incompatible with backend usage; Use axios directly?
			.then(() => this.$loader.startBackground(`${this._uid}-p${item.product}`))
			.then(() => this.$http.get(`/api/products/${item.product}`, { params: { instance: order.instance } }))
			.then((res) => {
				// TESTING ONLY, REMOVEME
				if (item.product === 1099870) {
					console.error(`REMOVEME ABI-3171 product`, item.product, `is being spoofed, it has SKU = 16208`);
					throw { err: "Spoof error for QA ABI-3171 https://mfdc.slack.com/archives/D03JEUQF0NQ/p1762733372770669 https://github.com/ActuallyHappening/php-serialize/blob/main/ERRORS.md#err_bad_str_len" }
				}
				return res;
			})
			.then((res) => this.$set(products, item.product, res.data)) // Cachei
			.then(() => products[item.product]) // Return
			// FIXME: Catch errors and display toast in "orderUtils" or leave for the consumer? Doing it here means can report per item
			.catch((e) => {
				console.warn(`Failed to fetch product "${item.product}" (sku = ${item.sku})`, e);
				// see https://github.com/steelbrain/php-serialize/blob/ad2b0edc33c5b97b174f2f24a6f40ae86d27effc/src/unserialize.ts#L86
				const corruptedDataFlag = "https://github.com/ActuallyHappening/php-serialize/blob/main/ERRORS.md#err_bad_str_len";
				if (JSON.stringify(e).includes(corruptedDataFlag)) {
					// https://freedcamp.com/view/2713393/issuetracker/348661
					this.$toast.error(`CORRUPTED DATA DETECTED [ABI-3171] in "${item.product}" (sku = ${item.sku})`);
				} else {
					this.$toast.warning(`Failed to fetch product "${item.product}"`);
				}
			})
			.finally(() => delete this.pendingFetchProduct[item.product])
			.finally(() => this.$loader.stop(`${this._uid}-p${item.product}`)));
	},

	/**
	 * Fetch product stock levels by order
	 *
	 * @param {Array<Object>} items Order items
	 * @param {Object} products Products API lookup
	 * @param {Object} productStocks Product stock API lookup
	 * @param {Boolean} [force=false] Force cache refresh
	 * @returns {Promise<Array>}
	 */
	fetchProductStockItems(items, products, productStocks, force = false) {
		if (!_.isArray(items)) return Promise.resolve();

		const $$debug = $debug.extend('fetchProductStockItems');

		if($$debug.enabled)
			$$debug(_.cloneDeep(items), _.cloneDeep(products), _.cloneDeep(productStocks), force);

		return Promise.allLimit(
			10, // TODO: Configurable number of concurrent requests
			_.uniqBy(items.filter((item) => item.itemType === 'product'), 'product')
				.map(item => () => this.fetchProductStockItem(item.product, products, productStocks, force, item.sku)),
		);
	},


	createOrderStockLookup(config, http = axios) {
		const requests = new Map();
		return sku => {
			if (!requests.has(sku)) {
				requests.set(sku, Promise.resolve().then(() => http.retry({
					url: `${config.baseUrl}/api/products/${encodeURIComponent(sku)}/stock`,
					headers: {apikey: config.apiKey},
					timeout: 10 * 1000,
				})));
			}
			return requests.get(sku);
		};
	},


	// TODO: Could simply pass in "sku" rather than entire "products" list
	/**
	 * Fetch product stock level for item
	 *
	 * @param {Number} productId Item's "product" key
	 * @param {Object} products Products API lookup
	 * @param {Object} productStocks Product stock API lookup
	 * @param {Boolean} [force=false] Force cache refresh
	 * @returns {Promise<Object>}
	 */
	fetchProductStockItem(productId, products, productStocks, force = false, sku = undefined) {
		const $$debug = $debug.extend('fetchProductStockItem');

		if($$debug.enabled)
			$$debug(productId, _.cloneDeep(products), _.cloneDeep(productStocks), false);

		const resolvedSku = sku || products[productId]?.sku;
		if (!resolvedSku && !_.has(products, productId)) return Promise.resolve();

		if (!force && _.has(productStocks, productId)) {
			$$debug('exists', productId);
			return Promise.resolve().then(() => productStocks[productId]);
		}

		if (!force && _.has(this.pendingFetchProductStockLevels, productId)) {
			$$debug('pending', productId, Object.keys(this.pendingFetchProductStockLevels).length);
			return Promise.resolve().then(() => this.pendingFetchProductStockLevels[productId]);
		}

		$$debug('request', productId, Object.keys(this.pendingFetchProductStockLevels).length);
		return (this.pendingFetchProductStockLevels[productId] = Promise.resolve()
			//.then(() => this.$loader.startBackground(`${this._uid}-ps${productId}`))
			// TODO: ABI-2906: What would "$digest.cache" give us? i.e. Can we use that as the cache?
			// FIXME: Is "$config" correct in backend usage?
			.then(() => {
				if (!resolvedSku) {
					console.warn('NO SKU for product', products[productId])
					return {};
				}
				return axios.get(`${this.$config.ims.url}/api/products/${resolvedSku}/stock`, {
					headers: {
						apikey: this.$config.ims.apiKey,
					},
				})
			})
			.then((res) => {
				$$debug('response', res);
				this.$set(productStocks, productId, res.data);
			}) // Cache
			.then(() => productStocks[productId]) // Return
			// FIXME: Catch errors and display toast in "orderUtils" or leave for the consumer? Doing it here means can report per item
			.catch((e) => {
				console.warn(e);
				this.$toast.warning(`Failed to fetch product stock "${productId}"`);
			})
			.finally(() => delete this.pendingFetchProductStockLevels[productId]));
		//.finally(() => this.$loader.stop(`${this._uid}-ps${productId}`))
	},

	/**
	 * Fetch list of warehouses
	 * @returns {Promise}
	 */
	fetchWarehouses() {
		const $$debug = $debug.extend('fetchWarehouses');

		if (this.pendingFetchWarehouses) {
			$$debug('pending');
			return Promise.resolve().then(() => this.pendingFetchWarehouses);
		}

		$$debug('request');
		return (this.pendingFetchWarehouses = Promise.resolve()
			.then(() =>
				this.$digest.get(`${this.$config.ims.url}/api/warehouses`, {
					hash: 'warehouses',
					headers: {
						apikey: this.$config.ims.apiKey,
					},
				}),
			)
			.catch((e) => {
				console.warn(e);
				this.$toast.warning('Failed to fetch warehouses');
			})
			.finally(() => {
				this.pendingFetchWarehouses = undefined;
			}));
	},



	/**
	 * Logs the customer discounts that get set when a customer is set on an order
	 *
	 * @param {Object} customer Customer
	 * @param {Object} order Order
	 * @param {Number} std Standard discount
	 * @param {Number} sale Sale discount
	 * @param {Number} ex Excluded discount
	 * @returns {Promise}
	 */
	logCustomerDiscount(customer, order, std, sale, ex) {
		var activeCustomerDiscounts = [];
		var message;

		if(customer != null){
			if(customer.discounts.standard != 0){
				activeCustomerDiscounts.push(`standard discount (${customer.discounts.standard})`);
			}
			if(customer.discounts.sales != 0){
				activeCustomerDiscounts.push(`sales discount (${customer.discounts.sales})`);
			}
			if(customer.discounts.excluded != 0){
				activeCustomerDiscounts.push(`excluded discount (${customer.discounts.excluded})`);
			}
			if (activeCustomerDiscounts.length === 0) return Promise.resolve();

			message = `<a href="/customers/${order.instance}/${customer._id}">${customer.name}</a> was assigned as billing customer and the following discounts were set: ${activeCustomerDiscounts.join(", ")}`;
		}
		else{
			if(std != 0){
				activeCustomerDiscounts.push(`standard discount (${std})`);
			}
			if(sale != 0){
				activeCustomerDiscounts.push(`sales discount (${sale})`);
			}
			if(ex != 0){
				activeCustomerDiscounts.push(`excluded discount (${ex})`);
			}
			if (activeCustomerDiscounts.length === 0) return Promise.resolve();

			message = `customer was assigned and the following discounts were set: ${activeCustomerDiscounts.join(", ")}`;
		}


		return Promise.all([
			this.$http.post(`/api/logs/orders/${order._id}`,{
				body: message,
				creator: this.$session.data._id,
				type: 'orderPayment'
			})
		])
		.catch(this.$toast.catch)
		.finally(()=> (this.$refs.logs && this.$refs.logs.refresh()))
	},


	/**
	 * Removes all the item level discounts for an order
	 *
	 * @param {Object} order Order
	 */
	removeOrderDiscounts(order) {
		order.items.forEach((item) => {
			if(item.itemType == 'product'){
				if (!_.isPlainObject(item)) throw new Error('Invalid item object');
				if (!_.isPlainObject(item.discounts)) return;

				// NOTE: don't remove discounts provided by woo
				// this.$delete(item.discounts, 'sale');
				// https://freedcamp.com/view/2713393/issuetracker/280948#comment-26513105
				this.$delete(item.discounts, 'override');
				this.$delete(item.discounts, 'line');
			}
		});
	},

	/**
	 * Calculate how store credit coupons will be allocated for a payment
	 * @param {Object} order Order with totals
	 * @param {Array} coupons Available coupons with remaining_amount
	 * @param {Array} selectedCoupons Array of coupon codes to use
	 * @returns {Array} Array of allocation objects with code, appliedAmount, remainingAfter
	 */
	calculateCouponAllocations(order, coupons, selectedCoupons) {
		console.log('coupons', coupons, selectedCoupons)
		if (!_.isPlainObject(order)) throw new Error('calculateCouponAllocations(): Invalid order object');
		if (!_.isArray(coupons)) throw new Error('calculateCouponAllocations(): Invalid coupons array');
		if (!_.isArray(selectedCoupons)) throw new Error('calculateCouponAllocations(): Invalid selectedCoupons array');

		const $$debug = $debug.extend('calculateCouponAllocations');

		if($$debug.enabled)
			$$debug('order totals:', order.totals, 'coupons:', coupons, 'selectedCoupons:', selectedCoupons);

		if (!order.totals || !order.totals.grandTotalOwing) {
			$$debug('No order totals available');
			return [];
		}

		const totalOwing = order.totals.grandTotalOwing;
		const validCoupons = coupons.filter(coupon =>
			coupon.discount_type === 'store_credit' &&
			Number(coupon.remaining_amount) > 0 &&
			selectedCoupons.includes(coupon.code)
		);

		if($$debug.enabled)
			$$debug('validCoupons:', validCoupons, 'totalOwing:', totalOwing);

		const sortedCoupons = [...validCoupons].sort(
			(a, b) => new Date(a.created) - new Date(b.created)
		);

		let remainingOwing = totalOwing;
		const allocations = [];

		for (const coupon of sortedCoupons) {
			if (remainingOwing <= 0) break;

			const available = Number(coupon.remaining_amount);
			if (available <= 0) continue;

			const amountToUse = Math.min(available, remainingOwing);

			allocations.push({
				code: coupon.code,
				coupon_post_id: coupon.coupon_post_id,
				appliedAmount: amountToUse,
				remainingAfter: available - amountToUse,
			});

			remainingOwing -= amountToUse;
		}

		if($$debug.enabled)
			$$debug('allocations:', allocations, 'remainingOwing:', remainingOwing);

		return allocations;
	},

};

// Compute various meta properties
orderUtils.paymentMethodsById = orderUtils.paymentMethods.reduce((obj, method) => {
	obj[method.id] = method;
	return obj;
}, {});

let computedLists = ['orderTagOptions', 'orderEventOptions', 'orderLocationOptions'];

// FIXME: These lists are not required server-side
orderUtils.orderUtilsInit = function(model) {
	if (orderUtils.pendingInit instanceof Promise) return orderUtils.pendingInit;

	return (orderUtils.pendingInit = Promise.resolve()
		.then(() => (model ? model.find({ status: 'active' }) : axios.get('/api/lists').then((res) => res.data)))
		.then((lists) =>
			computedLists.forEach((list) => {
				let currentList = lists.find((cList) => (cList.enabled ? cList.value === list : undefined));
				if (currentList)
					orderUtils[list] = [
						...new Set([
							...orderUtils[list],
							...currentList.items
								.filter((item) => item.value && item.enabled && !item.hasKeys)
								.map((item) => item.value),
						]),
					];
			}),
		));
};

module.exports = orderUtils;
